import React, { useEffect, useMemo, useState } from "react";
import { api, mediaUrl, formatApiError } from "../lib/api";
import { safeHtml } from "../lib/sanitize";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sparkles, Plus, Trash2, Users, Calendar, Newspaper, FileText, LayoutDashboard, Building2, Layers, Trophy, Clock, ShoppingBag, Heart, BarChart3, Mail, Send, PenSquare, Upload, Image as ImageIcon, Pencil, Activity, ChevronDown, ChevronRight, UserPlus, Download, Printer, Map, Bell, Vote, HandCoins, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { formatCalendarDay } from "../lib/dateUtil";
import { fmtET, etInputToUtc, utcToEtInput } from "../lib/eventTime";
import { MILITARY_BRANCHES } from "../lib/militaryBranches";
import AdminDashboard from "./AdminDashboard";
import Reports from "./Reports";
import MemberPicker from "../components/MemberPicker";
import { useSortableTable, SortableTh } from "../components/SortableTh";
import { FullEditHoursDialog } from "./Hours";
import RichEditor from "../components/RichEditor";
import AutomatedEmailsAdmin from "../components/AutomatedEmailsAdmin";
import SiteSettingsAdmin from "../components/SiteSettingsAdmin";
import SmsSettingsAdmin from "../components/SmsSettingsAdmin";
import HoursRoleConfigAdmin from "../components/HoursRoleConfigAdmin";
import RegionsAdmin from "../components/RegionsAdmin";
import ClustersAdmin from "../components/ClustersAdmin";
import { CHAPTER_OFFICER_SLOTS } from "../lib/officerSlots";
import ProfileLayoutAdmin from "../components/ProfileLayoutAdmin";
import PageBuilder from "../components/cms/PageBuilder";
import BulkImportMembersDialog from "../components/BulkImportMembersDialog";
import ElectionsAdmin from "../components/admin/ElectionsAdmin";
import MemberBalanceEditor from "../components/MemberBalanceEditor";
import AssignmentHistoryEditor from "../components/AssignmentHistoryEditor";
import SignInActivityAdmin from "../components/SignInActivityAdmin";
import RecruitmentAdmin from "../components/RecruitmentAdmin";

export default function Admin() {
    const [tab, setTab] = useState("dashboard");
    const [perms, setPerms] = useState(null);

    useEffect(() => {
        api.get("/admin/permissions").then(({ data }) => setPerms(data)).catch(() => setPerms({ tabs: [], admin_role: "" }));
    }, []);

    // Default to first allowed tab if current tab isn't allowed
    useEffect(() => {
        if (perms && perms.tabs.length && !perms.tabs.includes(tab)) setTab(perms.tabs[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [perms]);

    if (!perms) return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 text-muted-foreground">Loading…</div>;
    const allowed = (t) => perms.tabs.includes(t);

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
            <div className="flex items-end justify-between mb-8">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">Admin console</h1>
                    <p className="text-muted-foreground mt-2">
                        {perms.admin_role && perms.admin_role !== "full" ? (
                            <>You're logged in as <strong className="capitalize">{perms.admin_role.replace(/_/g, " ")}</strong>{perms.chapter_scoped ? " · chapter-scoped" : ""}.</>
                        ) : "Members, chapters, tiers, events, hours, awards, and content."}
                    </p>
                </div>
                <div className="inline-flex items-center gap-2 bg-secondary/30 rounded-full px-4 py-1.5 text-xs font-semibold">
                    <Sparkles className="h-4 w-4" /> AI tools available
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <div className="-mx-2 px-2 sm:mx-0 sm:px-0 overflow-x-auto sm:overflow-x-visible scrollbar-hide max-w-full">
                    <TabsList className="rounded-full bg-muted p-1 inline-flex sm:flex sm:flex-wrap sm:h-auto whitespace-nowrap w-max sm:w-full">
                        {allowed("dashboard") && <TabsTrigger value="dashboard" className="rounded-full" data-testid="admin-tab-dashboard"><LayoutDashboard className="h-4 w-4 mr-1.5" />Dashboard</TabsTrigger>}
                        {allowed("members") && <TabsTrigger value="members" className="rounded-full" data-testid="admin-tab-members"><Users className="h-4 w-4 mr-1.5" />Members</TabsTrigger>}
                        {allowed("chapters") && <TabsTrigger value="chapters" className="rounded-full" data-testid="admin-tab-chapters"><Building2 className="h-4 w-4 mr-1.5" />Chapters</TabsTrigger>}
                        {allowed("regions") && <TabsTrigger value="regions" className="rounded-full" data-testid="admin-tab-regions"><Map className="h-4 w-4 mr-1.5" />Regions</TabsTrigger>}
                        {allowed("chapters") && <TabsTrigger value="clusters" className="rounded-full" data-testid="admin-tab-clusters"><Users className="h-4 w-4 mr-1.5" />Clusters</TabsTrigger>}
                        {allowed("tiers") && <TabsTrigger value="tiers" className="rounded-full" data-testid="admin-tab-tiers"><Layers className="h-4 w-4 mr-1.5" />Tiers</TabsTrigger>}
                        {allowed("events") && <TabsTrigger value="events" className="rounded-full" data-testid="admin-tab-events"><Calendar className="h-4 w-4 mr-1.5" />Events</TabsTrigger>}
                        {allowed("hours") && <TabsTrigger value="hours" className="rounded-full" data-testid="admin-tab-hours"><Clock className="h-4 w-4 mr-1.5" />Hours</TabsTrigger>}
                        {allowed("recruitment") && <TabsTrigger value="recruitment" className="rounded-full" data-testid="admin-tab-recruitment"><UserPlus className="h-4 w-4 mr-1.5" />Recruitment</TabsTrigger>}
                        {allowed("awards") && <TabsTrigger value="awards" className="rounded-full" data-testid="admin-tab-awards"><Trophy className="h-4 w-4 mr-1.5" />Awards</TabsTrigger>}
                        {allowed("gear") && <TabsTrigger value="gear" className="rounded-full" data-testid="admin-tab-gear"><ShoppingBag className="h-4 w-4 mr-1.5" />Gear</TabsTrigger>}
                        {allowed("causes") && <TabsTrigger value="causes" className="rounded-full" data-testid="admin-tab-causes"><Heart className="h-4 w-4 mr-1.5" />Causes</TabsTrigger>}
                        {allowed("reports") && <TabsTrigger value="reports" className="rounded-full" data-testid="admin-tab-reports"><BarChart3 className="h-4 w-4 mr-1.5" />Reports</TabsTrigger>}
                        {allowed("email") && <TabsTrigger value="email" className="rounded-full" data-testid="admin-tab-email"><Mail className="h-4 w-4 mr-1.5" />Email</TabsTrigger>}
                        {allowed("news") && <TabsTrigger value="news" className="rounded-full" data-testid="admin-tab-news"><Newspaper className="h-4 w-4 mr-1.5" />News</TabsTrigger>}
                        {allowed("pages") && <TabsTrigger value="pages" className="rounded-full" data-testid="admin-tab-pages"><FileText className="h-4 w-4 mr-1.5" />Pages</TabsTrigger>}
                        {allowed("elections") && <TabsTrigger value="elections" className="rounded-full" data-testid="admin-tab-elections"><Vote className="h-4 w-4 mr-1.5" />Elections</TabsTrigger>}
                        {allowed("instructors") && <TabsTrigger value="instructors" className="rounded-full" data-testid="admin-tab-instructors"><GraduationCap className="h-4 w-4 mr-1.5" />Instructors</TabsTrigger>}
                        {perms.admin_role === "full" && <TabsTrigger value="activity" className="rounded-full" data-testid="admin-tab-activity"><Activity className="h-4 w-4 mr-1.5" />Activity</TabsTrigger>}
                    </TabsList>
                </div>

                {allowed("dashboard") && <TabsContent value="dashboard" className="mt-6"><AdminDashboard scopedChapterId={perms.scoped_chapter_id} /></TabsContent>}
                {allowed("members") && <TabsContent value="members" className="mt-6"><MembersAdmin /></TabsContent>}
                {allowed("chapters") && <TabsContent value="chapters" className="mt-6"><ChaptersAdmin /></TabsContent>}
                {allowed("regions") && <TabsContent value="regions" className="mt-6"><RegionsAdmin /></TabsContent>}
                {allowed("chapters") && <TabsContent value="clusters" className="mt-6"><ClustersAdmin /></TabsContent>}
                {allowed("tiers") && <TabsContent value="tiers" className="mt-6"><TiersAdmin /></TabsContent>}
                {allowed("events") && <TabsContent value="events" className="mt-6"><EventsAdmin /></TabsContent>}
                {allowed("hours") && <TabsContent value="hours" className="mt-6"><HoursAdmin scopedChapterId={perms.scoped_chapter_id} /></TabsContent>}
                {allowed("recruitment") && <TabsContent value="recruitment" className="mt-6"><RecruitmentAdmin /></TabsContent>}
                {allowed("awards") && <TabsContent value="awards" className="mt-6"><AwardsAdmin /></TabsContent>}
                {allowed("gear") && <TabsContent value="gear" className="mt-6"><GearAdmin /></TabsContent>}
                {allowed("causes") && <TabsContent value="causes" className="mt-6"><CausesAdmin scopedChapterId={perms.scoped_chapter_id} /></TabsContent>}
                {allowed("reports") && <TabsContent value="reports" className="mt-6"><Reports scopedChapterId={perms.scoped_chapter_id} /></TabsContent>}
                {allowed("email") && <TabsContent value="email" className="mt-6"><EmailBlastAdmin /></TabsContent>}
                {allowed("news") && <TabsContent value="news" className="mt-6"><NewsAdmin /></TabsContent>}
                {allowed("pages") && <TabsContent value="pages" className="mt-6"><PagesAdmin /></TabsContent>}
                {allowed("elections") && <TabsContent value="elections" className="mt-6"><ElectionsAdmin /></TabsContent>}
                {allowed("instructors") && <TabsContent value="instructors" className="mt-6"><InstructorsAdmin /></TabsContent>}
                {perms.admin_role === "full" && <TabsContent value="activity" className="mt-6"><SignInActivityAdmin /></TabsContent>}
            </Tabs>
        </div>
    );
}

/* -------- Events -------- */
function EventsAdmin() {
    const [events, setEvents] = useState([]);
    const load = () => api.get("/events").then(({ data }) => setEvents(data));
    useEffect(() => { load(); }, []);

    async function del(id) {
        if (!confirm("Delete this event?")) return;
        await api.delete(`/events/${id}`);
        toast.success("Deleted");
        load();
    }

    return (
        <div>
            <div className="flex justify-end mb-4">
                <EventDialog onSaved={load} trigger={<Button className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="new-event-btn"><Plus className="h-4 w-4 mr-1" />New event</Button>} />
            </div>
            <div className="space-y-3">
                {events.map((e) => (
                    <div key={e.id} className="bg-card border border-border rounded-2xl p-5 flex items-center justify-between" data-testid={`admin-event-${e.id}`}>
                        <div>
                            <div className="font-heading font-semibold text-lg">{e.title}</div>
                            <div className="text-sm text-muted-foreground mt-1">{fmtET(e.start_at, "EEE, MMM d · h:mm a zzz")} · {e.rsvp_count} RSVPs</div>
                        </div>
                        <div className="flex gap-2">
                            <EventDialog event={e} onSaved={load} trigger={<Button variant="outline" className="rounded-full" data-testid={`edit-event-${e.id}`}>Edit</Button>} />
                            <Button variant="ghost" size="icon" onClick={() => del(e.id)} data-testid={`delete-event-${e.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function EventDialog({ event, onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        title: event?.title || "",
        description: event?.description || "",
        location: event?.location || "",
        start_at: event ? utcToEtInput(event.start_at) : "",
        end_at: event?.end_at ? utcToEtInput(event.end_at) : "",
        capacity: event?.capacity || 0,
        cover_image: event?.cover_image || "",
        category: event?.category || "social",
        price: event?.price || 0,
        cancelled: event?.cancelled || false,
        cancellation_note: event?.cancellation_note || "",
        rsvps_closed: event?.rsvps_closed || false,
        is_paid: event?.is_paid || false,
        payment_url: event?.payment_url || "",
        payment_amount: event?.payment_amount || 0,
        external_url: event?.external_url || "",
        external_button_label: event?.external_button_label || "",
        allows_ticket_types: event?.allows_ticket_types || false,
        enabled_ticket_types: event?.enabled_ticket_types || [],
    });
    const [aiBusy, setAiBusy] = useState(false);
    const [coverUploading, setCoverUploading] = useState(false);
    // Sub-events drafted during NEW event creation. Each: {title, category, start_at, end_at, location, allows_ticket_types}
    const [subDrafts, setSubDrafts] = useState([]);
    // For an EXISTING event we ask the backend whether it already has children
    // so we can hide the payment-mode selector on umbrella parents (members
    // can't RSVP to umbrella events directly — backend blocks it).
    const [existingChildrenCount, setExistingChildrenCount] = useState(0);
    useEffect(() => {
        if (!event?.id) return;
        api.get(`/events/${event.id}/sub-events`)
            .then(({ data }) => setExistingChildrenCount((data || []).length))
            .catch(() => {});
    }, [event?.id]);
    const isUmbrella = subDrafts.length > 0 || existingChildrenCount > 0;

    async function save() {
        const payload = {
            ...form,
            capacity: Number(form.capacity),
            price: Number(form.price),
            start_at: etInputToUtc(form.start_at),
            end_at: etInputToUtc(form.end_at),
        };
        try {
            let parentId = event?.id;
            if (event) {
                await api.put(`/events/${event.id}`, payload);
            } else {
                const { data } = await api.post("/events", payload);
                parentId = data.id;
            }
            // After NEW parent saves, sequentially create sub-events (if any drafted).
            if (!event && parentId && subDrafts.length > 0) {
                let okCount = 0;
                let failCount = 0;
                for (const s of subDrafts) {
                    if (!s.title?.trim() || !s.start_at) { failCount++; continue; }
                    try {
                        await api.post("/events", {
                            title: s.title.trim(),
                            description: "",
                            location: (s.location || "").trim(),
                            start_at: etInputToUtc(s.start_at),
                            end_at: etInputToUtc(s.end_at),
                            category: (s.category || "general").trim(),
                            capacity: 0,
                            cover_image: "",
                            parent_event_id: parentId,
                            allows_ticket_types: !!s.allows_ticket_types,
                            is_paid: !!s.is_paid,
                            payment_url: s.is_paid ? (s.payment_url || "") : "",
                            payment_amount: s.is_paid ? Number(s.payment_amount || 0) : 0,
                            external_url: s.external_url || "",
                            external_button_label: s.external_url ? (s.external_button_label || "") : "",
                        });
                        okCount++;
                    } catch { failCount++; }
                }
                if (failCount > 0) toast.error(`Parent saved. ${okCount} sub-event(s) created, ${failCount} failed.`);
                else if (okCount > 0) toast.success(`Saved with ${okCount} sub-event(s).`);
                else toast.success("Saved");
            } else {
                toast.success("Saved");
            }
            setSubDrafts([]);
            setOpen(false);
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        }
    }

    function addSubDraft() {
        setSubDrafts((prev) => [...prev, {
            title: "",
            category: "general",
            start_at: form.start_at || "",
            end_at: "",
            location: form.location || "",
            allows_ticket_types: false,
            is_paid: false,
            payment_url: "",
            payment_amount: 0,
            external_url: "",
            external_button_label: "",
        }]);
    }
    function updateSubDraft(idx, patch) {
        setSubDrafts((prev) => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
    }
    function removeSubDraft(idx) {
        setSubDrafts((prev) => prev.filter((_, i) => i !== idx));
    }

    async function aiGenerate() {
        if (!form.title) { toast.error("Add a title first"); return; }
        setAiBusy(true);
        try {
            const { data } = await api.post("/ai/event-description", { title: form.title, topic: form.description, audience: "club members", tone: "friendly" });
            setForm({ ...form, description: data.text });
            toast.success("AI draft ready ✨");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "AI failed");
        }
        setAiBusy(false);
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{event ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                    <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-1.5" data-testid="event-title-input" /></div>
                    <div className="bg-secondary/20 rounded-2xl p-4 border border-secondary/40">
                        <div className="flex items-center justify-between mb-2">
                            <Label className="inline-flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-primary" /> Description (AI-assisted)</Label>
                            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={aiGenerate} disabled={aiBusy} data-testid="ai-generate-btn">
                                {aiBusy ? "Writing…" : "Generate with AI"}
                            </Button>
                        </div>
                        <Textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" data-testid="event-description-input" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div><Label>Start <span className="text-xs font-normal text-muted-foreground">(Eastern Time)</span></Label><Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="rounded-xl mt-1.5" data-testid="event-start-input" /></div>
                        <div><Label>End (optional) <span className="text-xs font-normal text-muted-foreground">(Eastern Time)</span></Label><Input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} className="rounded-xl mt-1.5" /></div>
                        <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-xl mt-1.5" /></div>
                        <div><Label className="dark:text-white">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl mt-1.5" /></div>
                        <div><Label>Capacity (0 = unlimited)</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="rounded-xl mt-1.5" /></div>
                        <div className="sm:col-span-2">
                            <Label>Cover photo</Label>
                            <div className="mt-1.5 grid sm:grid-cols-[1fr_180px] gap-3 items-start">
                                <label className="rounded-xl border-2 border-dashed border-slate-300 px-4 py-3 text-sm font-semibold cursor-pointer hover:bg-white hover:border-primary transition-colors flex items-center gap-2 justify-center" data-testid="event-cover-upload">
                                    {coverUploading ? "Uploading…" : (<>📷 {form.cover_image ? "Replace cover photo" : "Upload cover photo"}</>)}
                                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                        const f = e.target.files?.[0]; if (!f) return;
                                        setCoverUploading(true);
                                        try {
                                            const fd = new FormData(); fd.append("file", f);
                                            const { data } = await api.post("/events/upload-cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                            setForm((p) => ({ ...p, cover_image: data.url }));
                                            toast.success("Cover uploaded");
                                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
                                        setCoverUploading(false);
                                    }} />
                                </label>
                                {form.cover_image ? (
                                    <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50">
                                        <img src={form.cover_image} alt="Cover preview" className="block w-full h-32 object-contain" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
                                    </div>
                                ) : (
                                    <div className="border border-dashed border-slate-300 rounded-xl bg-white h-32 grid place-items-center text-[10px] text-slate-400 uppercase tracking-wider">Preview</div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Ticket types selector — admin picks which subset applies for this event */}
                    <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-4">
                        <label className="flex items-center gap-3 cursor-pointer mb-2">
                            <input
                                type="checkbox"
                                checked={!!form.allows_ticket_types}
                                onChange={(e) => setForm({ ...form, allows_ticket_types: e.target.checked, enabled_ticket_types: e.target.checked && (form.enabled_ticket_types?.length === 0) ? ["general"] : form.enabled_ticket_types })}
                                className="h-4 w-4 rounded border-2 border-slate-300 accent-primary"
                                data-testid="event-allows-tickets-toggle"
                            />
                            <span className="font-semibold text-sm">Use ticket types for this event</span>
                        </label>
                        {form.allows_ticket_types && (
                            <div className="mt-2">
                                <p className="text-xs text-muted-foreground mb-2">Select which ticket types members can pick from:</p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {["vip", "all_access", "general", "guest", "speaker", "volunteer"].map((t) => (
                                        <label key={t} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 cursor-pointer hover:border-primary text-sm">
                                            <input
                                                type="checkbox"
                                                checked={(form.enabled_ticket_types || []).includes(t)}
                                                onChange={(e) => {
                                                    const cur = new Set(form.enabled_ticket_types || []);
                                                    if (e.target.checked) cur.add(t); else cur.delete(t);
                                                    setForm({ ...form, enabled_ticket_types: Array.from(cur) });
                                                }}
                                                className="h-3.5 w-3.5 accent-primary"
                                                data-testid={`event-ticket-type-${t}`}
                                            />
                                            <span className="capitalize">{t.replace("_", " ")}</span>
                                        </label>
                                    ))}
                                </div>
                                {(form.enabled_ticket_types || []).length === 0 && (
                                    <p className="text-[11px] text-amber-700 mt-2">Pick at least one type — otherwise members can't RSVP.</p>
                                )}
                            </div>
                        )}
                        {!form.allows_ticket_types && (
                            <p className="text-[11px] text-muted-foreground">Members will RSVP without choosing a ticket type. Best for casual gatherings.</p>
                        )}
                    </div>
                    {/* Payment mode — pick one: free, Zeffy, or external link.
                        Hidden on umbrella parents (events that have sub-events)
                        because members can't RSVP to umbrella events directly —
                        each sub-event configures its own payment mode. */}
                    {isUmbrella ? (
                        <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600" data-testid="event-payment-mode-umbrella-hidden">
                            <div className="font-semibold mb-1">Payment / Tickets · configured per sub-event</div>
                            <div className="text-xs leading-snug">
                                This event is an umbrella that groups sub-events. Members don&apos;t RSVP to it directly — they RSVP to each sub-event below, and each sub-event has its own Free / Zeffy / External link selector.
                            </div>
                        </div>
                    ) : (
                    <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-4 space-y-2.5" data-testid="event-payment-mode">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Payment / Tickets</div>
                        {[
                            { value: "free", title: "Free Event", desc: "Members RSVP without paying.", color: "border-slate-300" },
                            { value: "zeffy", title: "💲 Paid via Zeffy", desc: "Members pay through Zeffy and submit their receipt #. RSVP confirmed when admin approves (same flow as annual dues).", color: "border-emerald-400 bg-emerald-50" },
                            { value: "external", title: "🔗 External ticket / payment link", desc: "Members click out to your own ticket page (Eventbrite, Stripe, etc.). No internal tracking — they handle everything off-site.", color: "border-sky-400 bg-sky-50" },
                        ].map((mode) => {
                            const currentMode = form.external_url ? "external" : (form.is_paid ? "zeffy" : "free");
                            const selected = currentMode === mode.value;
                            return (
                                <label key={mode.value} className={`flex items-start gap-3 rounded-xl border-2 p-3 cursor-pointer transition-colors ${selected ? mode.color : "border-slate-200 hover:border-slate-300 bg-white"}`} data-testid={`event-payment-mode-${mode.value}`}>
                                    <input
                                        type="radio"
                                        name="payment_mode"
                                        checked={selected}
                                        onChange={() => {
                                            if (mode.value === "free") setForm({ ...form, is_paid: false, payment_url: "", payment_amount: 0, external_url: "", external_button_label: "" });
                                            else if (mode.value === "zeffy") setForm({ ...form, is_paid: true, external_url: "", external_button_label: "" });
                                            else setForm({ ...form, is_paid: false, payment_url: "", payment_amount: 0, external_url: form.external_url || "https://", external_button_label: form.external_button_label || "" });
                                        }}
                                        className="mt-1 h-4 w-4 accent-primary shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm">{mode.title}</div>
                                        <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">{mode.desc}</div>
                                    </div>
                                </label>
                            );
                        })}
                        {form.is_paid && !form.external_url && (
                            <div className="mt-3 grid sm:grid-cols-[1fr_140px] gap-3 border-t border-emerald-200 pt-3">
                                <div>
                                    <Label className="text-xs">Zeffy payment URL</Label>
                                    <Input
                                        value={form.payment_url}
                                        onChange={(e) => setForm({ ...form, payment_url: e.target.value })}
                                        placeholder="https://www.zeffy.com/en-US/ticketing/your-event"
                                        className="rounded-xl mt-1.5"
                                        data-testid="event-payment-url"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Amount (USD)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={form.payment_amount}
                                        onChange={(e) => setForm({ ...form, payment_amount: Number(e.target.value) })}
                                        className="rounded-xl mt-1.5"
                                        data-testid="event-payment-amount"
                                    />
                                </div>
                            </div>
                        )}
                        {form.external_url && (
                            <div className="mt-3 grid sm:grid-cols-[1fr_200px] gap-3 border-t border-sky-200 pt-3">
                                <div>
                                    <Label className="text-xs">Ticket / payment URL *</Label>
                                    <Input
                                        value={form.external_url}
                                        onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                                        placeholder="https://www.eventbrite.com/your-event"
                                        className="rounded-xl mt-1.5"
                                        data-testid="event-external-url"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Button label (optional)</Label>
                                    <Input
                                        value={form.external_button_label}
                                        onChange={(e) => setForm({ ...form, external_button_label: e.target.value })}
                                        placeholder="Get tickets"
                                        className="rounded-xl mt-1.5"
                                        data-testid="event-external-label"
                                    />
                                </div>
                                <p className="sm:col-span-2 text-[11px] text-sky-800 leading-snug">
                                    Members will see one prominent button that opens this URL in a new tab. No internal RSVP/payment tracking happens for external-ticket events.
                                </p>
                            </div>
                        )}
                    </div>
                    )}
                    {event && (
                        <div className={`rounded-2xl border-2 p-4 ${form.cancelled ? "border-red-300 bg-red-50" : "border-slate-200 bg-slate-50"}`}>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.cancelled}
                                    onChange={(e) => setForm({ ...form, cancelled: e.target.checked })}
                                    className="h-4 w-4 rounded border-2 border-slate-300 accent-red-600"
                                    data-testid="event-cancelled-toggle"
                                />
                                <span className="font-semibold text-sm">
                                    {form.cancelled ? "🚫 Event is cancelled — RSVPs are blocked" : "Mark this event as cancelled"}
                                </span>
                            </label>
                            {form.cancelled && (
                                <div className="mt-3">
                                    <Label className="text-xs">Reason (optional, shown on the event page)</Label>
                                    <Input
                                        value={form.cancellation_note}
                                        onChange={(e) => setForm({ ...form, cancellation_note: e.target.value })}
                                        placeholder="e.g. Severe weather"
                                        className="rounded-xl mt-1.5"
                                        data-testid="event-cancellation-note"
                                    />
                                    <p className="text-[11px] text-red-700 mt-2 leading-snug">
                                        Members will see a CANCELLED banner. The event will still appear on the calendar but the RSVP button will be disabled.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                    {event && !form.cancelled && (
                        <div className={`rounded-2xl border-2 p-4 ${form.rsvps_closed ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.rsvps_closed}
                                    onChange={(e) => setForm({ ...form, rsvps_closed: e.target.checked })}
                                    className="h-4 w-4 rounded border-2 border-slate-300 accent-amber-600"
                                    data-testid="event-rsvps-closed-toggle"
                                />
                                <span className="font-semibold text-sm">
                                    {form.rsvps_closed ? "🔒 RSVPs are closed — only admins can add members & guests" : "Close RSVPs (admins-only mode)"}
                                </span>
                            </label>
                            {form.rsvps_closed && (
                                <p className="text-[11px] text-amber-800 mt-2 leading-snug">
                                    Members can no longer RSVP, change their ticket type, edit guests, or cancel their RSVP themselves. Admins keep full access via the "RSVP for a member" dialog. Re-open RSVPs by un-checking this box.
                                </p>
                            )}
                        </div>
                    )}
                    {!event && (
                        <div className="rounded-2xl border-2 border-primary/30 bg-primary/[0.04] p-4 space-y-3" data-testid="event-subevents-section">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-0.5">Optional</div>
                                    <div className="font-semibold text-sm">Sub-events under this event</div>
                                    <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                                        Useful for anniversaries, retreats, weekends — members RSVP per sub-event after the parent is published. You can edit / add more later from the event page.
                                    </p>
                                </div>
                                <Button type="button" variant="outline" size="sm" className="rounded-full shrink-0" onClick={addSubDraft} data-testid="add-subevent-draft-btn">
                                    <Plus className="h-3.5 w-3.5 mr-1" /> Add sub-event
                                </Button>
                            </div>
                            {subDrafts.length === 0 && (
                                <p className="text-[11px] italic text-muted-foreground">No sub-events yet. Click "Add sub-event" to draft one.</p>
                            )}
                            {subDrafts.map((s, idx) => (
                                <div key={idx} className="rounded-xl border border-primary/30 bg-white p-3 space-y-2.5" data-testid={`subevent-draft-${idx}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-primary">Sub-event #{idx + 1}</div>
                                        <button type="button" onClick={() => removeSubDraft(idx)} className="text-[11px] font-bold uppercase tracking-wider text-destructive hover:underline" data-testid={`remove-subevent-draft-${idx}`}>
                                            Remove
                                        </button>
                                    </div>
                                    <div className="grid sm:grid-cols-[2fr_1fr] gap-2">
                                        <div>
                                            <Label className="text-[10px] uppercase tracking-wider">Title *</Label>
                                            <Input value={s.title} onChange={(e) => updateSubDraft(idx, { title: e.target.value })} placeholder="Welcome reception" className="rounded-xl mt-1 text-sm" data-testid={`subevent-draft-title-${idx}`} />
                                        </div>
                                        <div>
                                            <Label className="text-[10px] uppercase tracking-wider">Category</Label>
                                            <Input value={s.category} onChange={(e) => updateSubDraft(idx, { category: e.target.value })} placeholder="general" className="rounded-xl mt-1 text-sm" data-testid={`subevent-draft-category-${idx}`} />
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-2">
                                        <div>
                                            <Label className="text-[10px] uppercase tracking-wider">Starts *</Label>
                                            <Input type="datetime-local" value={s.start_at} onChange={(e) => updateSubDraft(idx, { start_at: e.target.value })} className="rounded-xl mt-1 text-sm" data-testid={`subevent-draft-start-${idx}`} />
                                        </div>
                                        <div>
                                            <Label className="text-[10px] uppercase tracking-wider">Ends (optional)</Label>
                                            <Input type="datetime-local" value={s.end_at} onChange={(e) => updateSubDraft(idx, { end_at: e.target.value })} className="rounded-xl mt-1 text-sm" data-testid={`subevent-draft-end-${idx}`} />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-[10px] uppercase tracking-wider">Location</Label>
                                        <Input value={s.location} onChange={(e) => updateSubDraft(idx, { location: e.target.value })} className="rounded-xl mt-1 text-sm" data-testid={`subevent-draft-location-${idx}`} />
                                    </div>
                                    <label className="flex items-center gap-2 text-xs">
                                        <input
                                            type="checkbox"
                                            checked={!!s.allows_ticket_types}
                                            onChange={(e) => updateSubDraft(idx, { allows_ticket_types: e.target.checked })}
                                            className="h-3.5 w-3.5 accent-primary"
                                            data-testid={`subevent-draft-tickets-${idx}`}
                                        />
                                        Allow ticket types (VIP / All Access / General Admission)
                                    </label>
                                    {/* Payment / RSVP mode for the sub-event — sub-events are the
                                        items members actually RSVP to, so paid/external lives here.
                                        Mirrors the parent's payment-mode radio but compact. */}
                                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-2.5 space-y-1.5" data-testid={`subevent-draft-payment-${idx}`}>
                                        <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500">RSVP / Payment mode</div>
                                        {[
                                            { value: "free", label: "Free RSVP", color: "border-slate-300" },
                                            { value: "zeffy", label: "Paid via Zeffy", color: "border-emerald-400 bg-emerald-50" },
                                            { value: "external", label: "External link", color: "border-sky-400 bg-sky-50" },
                                        ].map((m) => {
                                            const cur = s.external_url ? "external" : (s.is_paid ? "zeffy" : "free");
                                            const sel = cur === m.value;
                                            return (
                                                <label key={m.value} className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 cursor-pointer text-xs transition-colors ${sel ? m.color : "border-slate-200 bg-white hover:border-slate-300"}`} data-testid={`subevent-draft-mode-${m.value}-${idx}`}>
                                                    <input
                                                        type="radio"
                                                        name={`sub-mode-${idx}`}
                                                        checked={sel}
                                                        onChange={() => {
                                                            if (m.value === "free") updateSubDraft(idx, { is_paid: false, payment_url: "", payment_amount: 0, external_url: "", external_button_label: "" });
                                                            else if (m.value === "zeffy") updateSubDraft(idx, { is_paid: true, external_url: "", external_button_label: "" });
                                                            else updateSubDraft(idx, { is_paid: false, payment_url: "", payment_amount: 0, external_url: s.external_url || "https://", external_button_label: s.external_button_label || "" });
                                                        }}
                                                        className="h-3.5 w-3.5 accent-primary"
                                                    />
                                                    <span className="font-medium">{m.label}</span>
                                                </label>
                                            );
                                        })}
                                        {s.is_paid && !s.external_url && (
                                            <div className="grid sm:grid-cols-[1fr_100px] gap-2 pt-1.5 border-t border-emerald-200">
                                                <Input
                                                    value={s.payment_url || ""}
                                                    onChange={(e) => updateSubDraft(idx, { payment_url: e.target.value })}
                                                    placeholder="https://www.zeffy.com/…"
                                                    className="rounded-lg text-xs"
                                                    data-testid={`subevent-draft-payment-url-${idx}`}
                                                />
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={s.payment_amount || 0}
                                                    onChange={(e) => updateSubDraft(idx, { payment_amount: Number(e.target.value) })}
                                                    placeholder="Amount"
                                                    className="rounded-lg text-xs"
                                                    data-testid={`subevent-draft-payment-amount-${idx}`}
                                                />
                                            </div>
                                        )}
                                        {s.external_url && (
                                            <div className="grid sm:grid-cols-[1fr_140px] gap-2 pt-1.5 border-t border-sky-200">
                                                <Input
                                                    value={s.external_url || ""}
                                                    onChange={(e) => updateSubDraft(idx, { external_url: e.target.value })}
                                                    placeholder="https://eventbrite.com/…"
                                                    className="rounded-lg text-xs"
                                                    data-testid={`subevent-draft-external-url-${idx}`}
                                                />
                                                <Input
                                                    value={s.external_button_label || ""}
                                                    onChange={(e) => updateSubDraft(idx, { external_button_label: e.target.value })}
                                                    placeholder="Button label"
                                                    className="rounded-lg text-xs"
                                                    data-testid={`subevent-draft-external-label-${idx}`}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button onClick={save} className="rounded-full bg-primary hover:bg-primary/90" data-testid="event-save-btn">Save event</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* -------- News -------- */
function escapeHtml(s) {
    return String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function htmlToPlainText(html) {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
}

function NewsAdmin() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/news").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);
    async function del(id) {
        if (!confirm("Delete this article?")) return;
        await api.delete(`/news/${id}`);
        load();
    }
    return (
        <div>
            <div className="flex justify-end mb-4">
                <NewsDialog onSaved={load} trigger={<Button className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="new-news-btn"><Plus className="h-4 w-4 mr-1" />New article</Button>} />
            </div>
            <div className="space-y-3">
                {items.map((n) => (
                    <div key={n.id} className="bg-card border border-border rounded-2xl p-5 flex items-center justify-between" data-testid={`admin-news-${n.id}`}>
                        <div>
                            <div className="font-heading font-semibold text-lg">{n.title}</div>
                            <div className="text-sm text-muted-foreground mt-1 line-clamp-1">{n.summary}</div>
                        </div>
                        <div className="flex gap-2">
                            <NewsDialog article={n} onSaved={load} trigger={<Button variant="outline" className="rounded-full">Edit</Button>} />
                            <Button variant="ghost" size="icon" onClick={() => del(n.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function NewsDialog({ article, onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        title: article?.title || "",
        summary: article?.summary || "",
        body: article?.body || "",
        body_html: article?.body_html || (article?.body ? article.body.split(/\n{2,}/).map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`).join("") : ""),
        background_color: article?.background_color || "",
        cover_image: article?.cover_image || "",
        tags: (article?.tags || []).join(", "),
        images: article?.images || [],
        template: article?.template || "classic",
    });
    // Notify members via email on CREATE only (edits don't re-blast).
    const [notify, setNotify] = useState(true);
    const [emailBusy, setEmailBusy] = useState(false);
    const [imgBusy, setImgBusy] = useState(false);

    async function save() {
        // Keep `body` as a plain-text fallback for search + templates that
        // still split paragraphs. Derive it from the HTML if the admin edited
        // via the rich editor.
        const plain = htmlToPlainText(form.body_html || "") || form.body || "";
        const payload = {
            ...form,
            body: plain,
            body_html: form.body_html || "",
            background_color: form.background_color || "",
            tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
            images: (form.images || []).filter(Boolean).slice(0, 5),
        };
        try {
            if (article) {
                await api.put(`/news/${article.id}`, payload);
                toast.success("Saved");
            } else {
                await api.post(`/news?notify=${notify ? "true" : "false"}`, payload);
                toast.success(notify ? "Published — members are being notified by email." : "Published silently.");
            }
            setOpen(false);
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        }
    }

    async function aiEmail() {
        if (!form.title) { toast.error("Add a title first"); return; }
        setEmailBusy(true);
        try {
            const { data } = await api.post("/ai/draft-email", { subject: form.title, goal: form.summary || form.title, tone: "warm" });
            const html = (data.text || "").split(/\n{2,}/).map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`).join("");
            setForm({ ...form, body: data.text, body_html: html });
            toast.success("Email drafted ✨");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "AI failed");
        }
        setEmailBusy(false);
    }

    async function addImages(files) {
        if (!files?.length) return;
        const remaining = 5 - (form.images?.length || 0);
        if (remaining <= 0) { toast.error("Max 5 additional images per article"); return; }
        setImgBusy(true);
        const uploads = Array.from(files).slice(0, remaining);
        const results = [];
        for (const f of uploads) {
            const fd = new FormData();
            fd.append("file", f);
            try {
                const { data } = await api.post("/news/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                results.push(data.url);
            } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail) || `Upload failed: ${f.name}`);
            }
        }
        if (results.length) {
            setForm((p) => ({ ...p, images: [...(p.images || []), ...results].slice(0, 5) }));
            toast.success(`${results.length} image${results.length > 1 ? "s" : ""} added`);
        }
        setImgBusy(false);
    }

    function removeImage(idx) {
        setForm((p) => ({ ...p, images: (p.images || []).filter((_, i) => i !== idx) }));
    }

    function moveImage(idx, dir) {
        setForm((p) => {
            const arr = [...(p.images || [])];
            const target = idx + dir;
            if (target < 0 || target >= arr.length) return p;
            [arr[idx], arr[target]] = [arr[target], arr[idx]];
            return { ...p, images: arr };
        });
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{article ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                    <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-1.5" data-testid="news-title-input" /></div>
                    <div><Label>Summary</Label><Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    <div>
                        <Label>Layout template</Label>
                        <Select value={form.template} onValueChange={(v) => setForm({ ...form, template: v })}>
                            <SelectTrigger className="rounded-xl mt-1.5" data-testid="news-template-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="classic">Classic — single column</SelectItem>
                                <SelectItem value="two_col">Two columns</SelectItem>
                                <SelectItem value="three_col">Three columns</SelectItem>
                                <SelectItem value="image_left">Image left, text right</SelectItem>
                                <SelectItem value="image_right">Text left, image right</SelectItem>
                                <SelectItem value="gallery">Gallery — text with images interleaved</SelectItem>
                                <SelectItem value="hero">Hero cover + text below</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1.5">Body paragraphs (separated by blank lines) will be split across columns when a multi-column template is chosen.</p>
                    </div>
                    <div className="bg-secondary/20 rounded-2xl p-4 border border-secondary/40">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <Label className="inline-flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-primary" /> Body (rich text · AI email-draft available)</Label>
                            <div className="flex items-center gap-3">
                                <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                                    Background
                                    <input
                                        type="color"
                                        value={form.background_color || "#ffffff"}
                                        onChange={(e) => setForm({ ...form, background_color: e.target.value })}
                                        className="h-7 w-9 cursor-pointer rounded border border-border bg-transparent"
                                        data-testid="news-bg-color"
                                        title="Article background color"
                                    />
                                    {form.background_color && (
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, background_color: "" })}
                                            className="text-xs text-muted-foreground hover:text-primary underline"
                                            data-testid="news-bg-color-clear"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </label>
                                <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={aiEmail} disabled={emailBusy} data-testid="ai-email-btn">
                                    {emailBusy ? "Drafting…" : "Draft as email"}
                                </Button>
                            </div>
                        </div>
                        <div style={form.background_color ? { backgroundColor: form.background_color, borderRadius: 16, padding: 6 } : undefined}>
                            <RichEditor
                                value={form.body_html}
                                onChange={(html) => setForm((f) => ({ ...f, body_html: html }))}
                                placeholder="Write your article — use the toolbar to format, add images, colors, etc."
                                minHeight={280}
                            />
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Cover photo</Label>
                            <label className="mt-1.5 rounded-xl border-2 border-dashed border-slate-300 px-4 py-3 text-sm font-semibold cursor-pointer hover:bg-white hover:border-primary transition-colors flex items-center gap-2 justify-center" data-testid="news-image-upload-btn">
                                <Upload className="h-4 w-4" />{form.cover_image ? "Replace cover photo" : "Upload cover photo"}
                                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                    const f = e.target.files?.[0]; if (!f) return;
                                    const fd = new FormData(); fd.append("file", f);
                                    try {
                                        const { data } = await api.post("/news/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                        setForm((p) => ({ ...p, cover_image: data.url }));
                                        toast.success("Cover photo uploaded");
                                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
                                }} />
                            </label>
                            {form.cover_image && (
                                <img src={mediaUrl(form.cover_image)} alt="Preview" className="mt-2 w-full max-h-32 object-contain rounded-xl border border-border bg-slate-50" />
                            )}
                        </div>
                        <div><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between">
                            <Label>Additional photos ({form.images?.length || 0}/5)</Label>
                            <label
                                className={`rounded-full text-xs font-semibold px-3 py-1.5 border cursor-pointer inline-flex items-center gap-1.5 ${(form.images?.length || 0) >= 5 || imgBusy ? "border-slate-200 text-slate-400 cursor-not-allowed" : "border-primary text-primary hover:bg-primary/5"}`}
                                data-testid="news-add-images-btn"
                            >
                                <Upload className="h-3.5 w-3.5" />
                                {imgBusy ? "Uploading…" : "Add photos"}
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="hidden"
                                    disabled={(form.images?.length || 0) >= 5 || imgBusy}
                                    onChange={(e) => addImages(e.target.files)}
                                />
                            </label>
                        </div>
                        {form.images?.length > 0 && (
                            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="news-images-preview">
                                {form.images.map((src, idx) => (
                                    <div key={`${src}-${idx}`} className="relative group rounded-xl overflow-hidden border border-border bg-slate-50">
                                        <img src={mediaUrl(src)} alt={`Photo ${idx + 1}`} className="w-full h-28 object-cover" />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                            <Button type="button" size="icon" variant="secondary" className="h-7 w-7 rounded-full" onClick={() => moveImage(idx, -1)} disabled={idx === 0} data-testid={`news-image-left-${idx}`}>←</Button>
                                            <Button type="button" size="icon" variant="secondary" className="h-7 w-7 rounded-full" onClick={() => moveImage(idx, 1)} disabled={idx === form.images.length - 1} data-testid={`news-image-right-${idx}`}>→</Button>
                                            <Button type="button" size="icon" variant="destructive" className="h-7 w-7 rounded-full" onClick={() => removeImage(idx)} data-testid={`news-image-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter className="flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                    {!article && (
                        <label className="flex items-center gap-2 text-sm text-muted-foreground mr-auto cursor-pointer select-none" data-testid="news-notify-label">
                            <input
                                type="checkbox"
                                checked={notify}
                                onChange={(e) => setNotify(e.target.checked)}
                                className="h-4 w-4 rounded border-border accent-primary"
                                data-testid="news-notify-toggle"
                            />
                            <span>Notify members via email</span>
                        </label>
                    )}
                    <Button onClick={save} className="rounded-full bg-primary hover:bg-primary/90" data-testid="news-save-btn">
                        {article ? "Save" : (notify ? "Publish & notify" : "Publish silently")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* -------- Pages -------- */
function PagesAdmin() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/pages").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);
    async function del(slug) {
        if (!confirm("Delete this page?")) return;
        await api.delete(`/pages/${slug}`);
        load();
    }
    return (
        <div className="space-y-8">
            <SiteSettingsAdmin />
            <SmsSettingsAdmin />
            <HoursRoleConfigAdmin />
            <ProfileLayoutAdmin />
            <div>
                <div className="flex items-end justify-between mb-4">
                    <div>
                        <h3 className="font-heading text-xl font-bold">Custom pages</h3>
                        <p className="text-xs text-muted-foreground">Author standalone pages at <code>/page/&lt;slug&gt;</code>.</p>
                    </div>
                    <PageDialog onSaved={load} trigger={<Button className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="new-page-btn"><Plus className="h-4 w-4 mr-1" />New page</Button>} />
                </div>
                <div className="space-y-3">
                    {items.map((p) => (
                        <div key={p.id} className="bg-card border border-border rounded-2xl p-5 flex items-center justify-between" data-testid={`admin-page-${p.slug}`}>
                            <div>
                                <div className="font-heading font-semibold text-lg">{p.title}</div>
                                <div className="text-sm text-muted-foreground mt-1">/page/{p.slug}</div>
                            </div>
                            <div className="flex gap-2">
                                <PageDialog page={p} onSaved={load} trigger={<Button variant="outline" className="rounded-full">Edit</Button>} />
                                <Button variant="ghost" size="icon" onClick={() => del(p.slug)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function PageDialog({ page, onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ slug: page?.slug || "", title: page?.title || "", body: page?.body || "", blocks: page?.blocks || [] });
    const [mode, setMode] = useState((page?.blocks && page.blocks.length > 0) ? "blocks" : "blocks");
    async function save() {
        try {
            const payload = { title: form.title, body: form.body, blocks: form.blocks };
            if (page) {
                await api.put(`/pages/${page.slug}`, payload);
            } else {
                await api.post("/pages", { slug: form.slug, ...payload });
            }
            toast.success("Saved");
            setOpen(false);
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        }
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">{page ? "Edit page" : "New page"}</DialogTitle>
                    <DialogDescription className="sr-only">Create or edit a custom page. Use the visual editor to add drag-and-drop blocks, or switch to the legacy text mode.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div><Label>Slug</Label><Input disabled={!!page} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} className="rounded-xl mt-1.5" data-testid="page-slug-input" /></div>
                        <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-1.5" data-testid="page-title-input" /></div>
                    </div>
                    <div className="flex items-center gap-2 border-b pb-2">
                        <Button size="sm" variant={mode === "blocks" ? "default" : "outline"} onClick={() => setMode("blocks")} className="rounded-full" data-testid="page-mode-blocks">Visual editor</Button>
                        <Button size="sm" variant={mode === "body" ? "default" : "outline"} onClick={() => setMode("body")} className="rounded-full" data-testid="page-mode-body">Legacy text</Button>
                    </div>
                    {mode === "blocks" ? (
                        <PageBuilder blocks={form.blocks} onChange={(blocks) => setForm({ ...form, blocks })} testIdPrefix="page-dialog-builder" />
                    ) : (
                        <div><Label>Body</Label><Textarea rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    )}
                </div>
                <DialogFooter><Button onClick={save} className="rounded-full bg-primary hover:bg-primary/90" data-testid="page-save-btn">Save</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* -------- Members admin: with role toggle, chapter/tier assignment -------- */
function ApplicationsPanel({ onApproved }) {
    const [apps, setApps] = useState([]);
    const [busy, setBusy] = useState({});

    async function load() {
        try {
            const { data } = await api.get("/admin/applications", { params: { status_filter: "pending" } });
            setApps(data);
        } catch { /* ignore */ }
    }
    useEffect(() => { load(); }, []);

    async function review(id, action) {
        const note = action === "reject" ? (prompt("Optional reason for rejection (sent in email):") || "") : "";
        setBusy({ ...busy, [id]: true });
        try {
            const { data } = await api.post(`/admin/applications/${id}/review`, { action, note });
            if (action === "approve") {
                if (data?.welcome_email_sent) {
                    toast.success("Approved — Welcome email delivered to the applicant.");
                } else {
                    toast.warning(`Approved, but welcome email did NOT send: ${data?.welcome_email_detail || "unknown error"}. Check Brevo domain config.`, { duration: 8000 });
                }
            } else {
                toast.success("Application rejected");
            }
            await load();
            if (action === "approve") onApproved?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
        setBusy({ ...busy, [id]: false });
    }

    if (apps.length === 0) return null;
    return (
        <div className="bg-card rounded-2xl border-2 border-primary/20 shadow-warm p-5 mb-6" data-testid="applications-panel">
            <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Pending applications</div>
                    <h3 className="font-heading text-xl font-bold mt-1">{apps.length} awaiting review</h3>
                </div>
            </div>
            <div className="space-y-2">
                {apps.map((a) => (
                    <div key={a.id} className="border border-border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`application-${a.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="font-heading text-base font-bold truncate">{a.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{a.email} · {a.intake_line || "no line"}{a.intake_completed_at ? ` · Intake ${a.intake_completed_at}` : ""}</div>
                            <div className="text-xs text-muted-foreground mt-0.5 truncate">{[a.address, a.city, a.state, a.zip_code, a.country].filter(Boolean).join(", ")}</div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <Button size="sm" variant="outline" disabled={busy[a.id]} onClick={() => review(a.id, "reject")} className="rounded-full" data-testid={`reject-application-${a.id}`}>Reject</Button>
                            <Button size="sm" disabled={busy[a.id]} onClick={() => review(a.id, "approve")} className="rounded-full bg-primary hover:bg-primary/90" data-testid={`approve-application-${a.id}`}>Approve</Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PendingIntakeChangesPanel({ onChanged }) {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState({});

    async function load() {
        try {
            const { data } = await api.get("/admin/pending-intake-changes");
            setItems(data || []);
        } catch { setItems([]); }
    }
    useEffect(() => { load(); }, []);

    async function review(id, action) {
        const note = action === "reject" ? (prompt("Optional reason for rejection:") || "") : "";
        setBusy({ ...busy, [id]: true });
        try {
            await api.post(`/admin/members/${id}/intake-completion-review`, { action, note });
            toast.success(action === "approve" ? "Intake date approved" : "Intake date change rejected");
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
        setBusy({ ...busy, [id]: false });
    }

    if (items.length === 0) return null;
    return (
        <div className="bg-amber-50 dark:bg-amber-950/20 rounded-2xl border-2 border-amber-300 shadow-warm p-5 mb-6" data-testid="pending-intake-panel">
            <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-amber-700">Pending intake date changes</div>
                    <h3 className="font-heading text-xl font-bold mt-1">{items.length} awaiting review</h3>
                </div>
            </div>
            <div className="space-y-2">
                {items.map((m) => (
                    <div key={m.id} className="border border-amber-200 bg-white rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`intake-change-${m.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="font-heading text-base font-bold truncate">{m.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{m.email}{m.line_name && ` · "${m.line_name}"`}</div>
                            <div className="text-xs mt-1">
                                Current: <strong>{m.current_intake_completed_at || "—"}</strong>
                                <span className="mx-2 opacity-60">→</span>
                                Requested: <strong className="text-amber-700">{m.pending_intake_completed_at}</strong>
                            </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <Button size="sm" variant="outline" disabled={busy[m.id]} onClick={() => review(m.id, "reject")} className="rounded-full" data-testid={`reject-intake-${m.id}`}>Reject</Button>
                            <Button size="sm" disabled={busy[m.id]} onClick={() => review(m.id, "approve")} className="rounded-full bg-primary hover:bg-primary/90" data-testid={`approve-intake-${m.id}`}>Approve</Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


/**
 * Chapter-change approval flow. Any admin with `members` tab access sees the
 * pending list (governor-managers only see requests touching their own
 * chapter, enforced backend-side). Only FULL-access admins can approve/deny.
 * On approval the backend updates user.chapter_id and emails/pushes the
 * member + all governor-managers of both source & target chapters +
 * full-access admins.
 */
function ChapterChangeRequestsPanel({ isFullAdmin, onChanged }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/chapter-change-requests${showAll ? "?status=all" : ""}`);
            setRows(data || []);
        } catch { setRows([]); }
        setLoading(false);
    }
    useEffect(() => { load(); }, [showAll]);

    async function decide(id, approve) {
        const verb = approve ? "APPROVE" : "DENY";
        if (!window.confirm(`${verb} this chapter change request? The member and governor-managers will be notified.`)) return;
        try {
            await api.post(`/admin/chapter-change-requests/${id}/${approve ? "approve" : "deny"}`);
            toast.success(`Request ${approve ? "approved" : "denied"}`);
            load();
            onChanged && onChanged();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
        }
    }

    if (loading) return null;
    if (rows.length === 0 && !showAll) return null;

    return (
        <div className="mb-6 border rounded-2xl bg-amber-50/60 border-amber-200 p-4" data-testid="chapter-change-requests-panel">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-amber-700" /> Chapter-change requests
                    {rows.length > 0 && (
                        <span className="text-xs bg-amber-200 text-amber-900 rounded-full px-2 py-0.5" data-testid="chapter-change-count">{rows.length}</span>
                    )}
                </h3>
                <button type="button" onClick={() => setShowAll(!showAll)} className="text-xs underline text-slate-600" data-testid="chapter-change-toggle-history">
                    {showAll ? "Show pending only" : "Show history"}
                </button>
            </div>
            {rows.length === 0 ? (
                <p className="text-sm text-slate-500">No chapter change requests to review.</p>
            ) : (
                <div className="space-y-2">
                    {rows.map((r) => (
                        <div key={r.id} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 rounded-lg border border-amber-100 bg-white p-3" data-testid={`chapter-change-row-${r.id}`}>
                            <Avatar className="h-10 w-10 shrink-0">
                                <AvatarImage src={mediaUrl(r.user_avatar_url)} />
                                <AvatarFallback>{r.user_name?.[0] || "?"}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">{r.user_name}</div>
                                <div className="text-xs text-slate-600">
                                    <span className="font-medium">{r.current_chapter_name || "Unassigned"}</span>
                                    {" → "}
                                    <span className="font-medium text-primary">{r.target_chapter_name}</span>
                                </div>
                                {r.reason && <div className="text-xs text-slate-500 italic mt-1">&ldquo;{r.reason}&rdquo;</div>}
                                {r.status !== "pending" && (
                                    <div className="text-[10px] uppercase tracking-wider mt-1">
                                        <span className={r.status === "approved" ? "text-green-700 font-semibold" : "text-red-700 font-semibold"}>
                                            {r.status} by {r.decided_by_name || "admin"}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {r.status === "pending" && isFullAdmin && (
                                <div className="flex gap-2 self-stretch sm:self-auto">
                                    <Button size="sm" onClick={() => decide(r.id, true)} data-testid={`chapter-change-approve-${r.id}`}>Approve</Button>
                                    <Button size="sm" variant="outline" onClick={() => decide(r.id, false)} data-testid={`chapter-change-deny-${r.id}`}>Deny</Button>
                                </div>
                            )}
                            {r.status === "pending" && !isFullAdmin && (
                                <span className="text-xs text-slate-500 italic">Awaiting full-access admin</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


/**
 * Bulk import of historical event attendance. Feeds the `checkins` collection
 * so the Medallion "events attended" criterion and Chapter-of-the-Year
 * pipelines see historical data. Full-access admins only.
 */
function CsvCheckinImportPanel({ isFullAdmin }) {
    const [busy, setBusy] = useState(false);
    const [batch, setBatch] = useState(null);
    const [showAll, setShowAll] = useState({ matched: false, skipped: false });
    if (!isFullAdmin) return null;

    async function onFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        setBatch(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/checkins/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setBatch(data);
            const s = data.summary || {};
            toast.success(`Parsed ${s.total || 0} rows — ${s.insert || 0} ready, ${(s.skip || 0) + (s.duplicate || 0)} skipped. Review then Approve.`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
        }
        setBusy(false);
        e.target.value = "";
    }

    async function approve() {
        if (!batch?.batch_id) return;
        if (!window.confirm(`Approve and commit ${batch.summary?.insert || 0} attendance record${batch.summary?.insert === 1 ? "" : "s"}? Skipped and duplicate rows will be left as-is.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/checkins/import-batches/${batch.batch_id}/approve`);
            const inserted = data?.outcome?.inserted || 0;
            toast.success(`Approved — inserted ${inserted} attendance record${inserted === 1 ? "" : "s"}.`);
            setBatch(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Approve failed");
        }
        setBusy(false);
    }

    async function discard() {
        if (!batch?.batch_id) return;
        if (!window.confirm("Discard this pending import? The file will need to be re-uploaded to try again.")) return;
        setBusy(true);
        try {
            await api.delete(`/admin/checkins/import-batches/${batch.batch_id}`);
            toast.success("Import discarded.");
            setBatch(null);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Discard failed");
        }
        setBusy(false);
    }

    const rows = batch?.rows || [];
    const matched = rows.filter((r) => r.action === "insert");
    const skipped = rows.filter((r) => r.action !== "insert");
    const pending = batch?.status === "pending";
    const approved = batch?.status === "approved";

    return (
        <div className="mb-6 border rounded-2xl bg-white dark:bg-card p-4" data-testid="csv-checkin-import-panel">
            <div className="flex items-start gap-3">
                <Upload className="h-5 w-5 text-slate-600 mt-1 shrink-0" />
                <div className="flex-1">
                    <h3 className="font-heading text-lg font-semibold">Import historical event attendance (CSV)</h3>
                    <p className="text-xs text-slate-500 mt-1">
                        CSV must include an <code className="bg-slate-100 dark:bg-muted rounded px-1">email</code> (preferred) or <code className="bg-slate-100 dark:bg-muted rounded px-1">name</code> column plus <code className="bg-slate-100 dark:bg-muted rounded px-1">event_name</code> and <code className="bg-slate-100 dark:bg-muted rounded px-1">date</code>. Uploads are a <strong>preview</strong> &mdash; skipped members and reasons are surfaced below, and nothing is written until you click <strong>Approve</strong>. Idempotent &mdash; already-imported rows show as duplicates.
                    </p>
                    {!batch && (
                        <div className="mt-3">
                            <input type="file" accept=".csv,text/csv" onChange={onFile} disabled={busy} data-testid="csv-checkin-file-input" className="text-sm" />
                            {busy && <div className="text-xs text-slate-500 mt-2">Uploading &amp; parsing&hellip;</div>}
                        </div>
                    )}

                    {batch && (
                        <div className="mt-3 space-y-3" data-testid="csv-checkin-import-result">
                            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 dark:bg-muted p-3 text-sm">
                                <span className="font-mono text-xs text-slate-500 truncate">{batch.filename}</span>
                                <span className="text-xs text-slate-500">&middot;</span>
                                <span className="text-xs" data-testid="csv-batch-total"><strong>{batch.summary?.total || 0}</strong> rows</span>
                                <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[11px] font-semibold" data-testid="csv-batch-matched">{batch.summary?.insert || 0} ready</span>
                                {batch.summary?.duplicate > 0 && (
                                    <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px] font-semibold" data-testid="csv-batch-duplicate">{batch.summary.duplicate} duplicate</span>
                                )}
                                {batch.summary?.skip > 0 && (
                                    <span className="rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-[11px] font-semibold" data-testid="csv-batch-skipped">{batch.summary.skip} skipped</span>
                                )}
                                {approved && (
                                    <span className="ml-auto rounded-full bg-emerald-600 text-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider" data-testid="csv-batch-approved-badge">
                                        Approved &middot; {batch.outcome?.inserted || 0} inserted
                                    </span>
                                )}
                                {pending && (
                                    <div className="ml-auto flex gap-2">
                                        <Button variant="outline" size="sm" onClick={discard} disabled={busy} className="rounded-full" data-testid="csv-batch-discard-btn">Discard</Button>
                                        <Button size="sm" onClick={approve} disabled={busy || (batch.summary?.insert || 0) === 0} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="csv-batch-approve-btn">
                                            {busy ? "Approving\u2026" : `Approve ${batch.summary?.insert || 0} record${(batch.summary?.insert || 0) === 1 ? "" : "s"}`}
                                        </Button>
                                    </div>
                                )}
                                {approved && (
                                    <Button variant="outline" size="sm" onClick={() => setBatch(null)} className="rounded-full ml-2" data-testid="csv-batch-new-btn">Upload another</Button>
                                )}
                            </div>

                            {matched.length > 0 && (
                                <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/40 dark:bg-emerald-900/10 p-3" data-testid="csv-batch-matched-panel">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-xs font-semibold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">Ready to import ({matched.length})</div>
                                        {matched.length > 8 && (
                                            <button type="button" onClick={() => setShowAll((v) => ({ ...v, matched: !v.matched }))} className="text-xs text-emerald-700 dark:text-emerald-300 underline" data-testid="csv-batch-matched-toggle">
                                                {showAll.matched ? "Show fewer" : `Show all ${matched.length}`}
                                            </button>
                                        )}
                                    </div>
                                    <div className="max-h-56 overflow-y-auto">
                                        <table className="w-full text-xs">
                                            <thead className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-muted-foreground">
                                                <tr><th className="text-left py-1 pr-2">Row</th><th className="text-left py-1 pr-2">Member</th><th className="text-left py-1 pr-2">Event</th><th className="text-left py-1 pr-2">Date</th><th className="text-left py-1 pr-2">Event status</th></tr>
                                            </thead>
                                            <tbody>
                                                {(showAll.matched ? matched : matched.slice(0, 8)).map((r) => (
                                                    <tr key={r.row} className="border-t border-emerald-100 dark:border-emerald-900/40">
                                                        <td className="py-1 pr-2 text-slate-500 dark:text-muted-foreground">{r.row}</td>
                                                        <td className="py-1 pr-2"><span className="font-medium">{r.matched_user_name || r.name}</span> <span className="text-slate-400">{r.matched_user_email}</span></td>
                                                        <td className="py-1 pr-2">{r.event_name}</td>
                                                        <td className="py-1 pr-2 tabular-nums">{r.date_parsed}</td>
                                                        <td className="py-1 pr-2">{r.event_is_new ? <span className="text-amber-700 dark:text-amber-300">will create event</span> : <span className="text-emerald-700 dark:text-emerald-300">existing event</span>}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {skipped.length > 0 && (
                                <div className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50/40 dark:bg-red-900/10 p-3" data-testid="csv-batch-skipped-panel">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-xs font-semibold uppercase tracking-wider text-red-800 dark:text-red-300">Skipped ({skipped.length}) &mdash; will NOT be imported</div>
                                        {skipped.length > 8 && (
                                            <button type="button" onClick={() => setShowAll((v) => ({ ...v, skipped: !v.skipped }))} className="text-xs text-red-700 dark:text-red-300 underline" data-testid="csv-batch-skipped-toggle">
                                                {showAll.skipped ? "Show fewer" : `Show all ${skipped.length}`}
                                            </button>
                                        )}
                                    </div>
                                    <div className="max-h-56 overflow-y-auto">
                                        <table className="w-full text-xs">
                                            <thead className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-muted-foreground">
                                                <tr><th className="text-left py-1 pr-2">Row</th><th className="text-left py-1 pr-2">CSV member</th><th className="text-left py-1 pr-2">Event</th><th className="text-left py-1 pr-2">Date</th><th className="text-left py-1 pr-2">Reason</th></tr>
                                            </thead>
                                            <tbody>
                                                {(showAll.skipped ? skipped : skipped.slice(0, 8)).map((r) => (
                                                    <tr key={r.row} className="border-t border-red-100 dark:border-red-900/40">
                                                        <td className="py-1 pr-2 text-slate-500 dark:text-muted-foreground">{r.row}</td>
                                                        <td className="py-1 pr-2"><span className="font-medium">{r.name || <em className="text-slate-400">(no name)</em>}</span> <span className="text-slate-400">{r.email}</span></td>
                                                        <td className="py-1 pr-2">{r.event_name || <em className="text-slate-400">&mdash;</em>}</td>
                                                        <td className="py-1 pr-2 tabular-nums">{r.date_parsed || r.date_raw || <em className="text-slate-400">&mdash;</em>}</td>
                                                        <td className="py-1 pr-2 text-red-700 dark:text-red-300">{r.action === "duplicate" ? <span className="font-semibold">Duplicate &mdash; </span> : null}{r.skip_reason}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}



function MembersAdmin() {
    const { user: me } = useAuth();
    const isFullAdmin = (me?.admin_role || "full") === "full";
    const [members, setMembers] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [q, setQ] = useState("");

    const load = async () => {
        const [m, c, t] = await Promise.all([
            api.get("/members", { params: q ? { q } : {} }),
            api.get("/chapters"),
            api.get("/tiers"),
        ]);
        setMembers(m.data);
        setChapters(c.data);
        setTiers(t.data);
    };

    useEffect(() => { const id = setTimeout(load, 200); return () => clearTimeout(id); }, [q]);

    async function toggleRole(m) {
        const newRole = m.role === "admin" ? "member" : "admin";
        if (!confirm(`Change ${m.name} to ${newRole}?`)) return;
        await api.put(`/members/${m.id}/role`, { role: newRole });
        toast.success(`Role updated to ${newRole}`);
        load();
    }
    async function setChapter(m, chapter_id) {
        await api.put(`/members/${m.id}/chapter`, { chapter_id: chapter_id || null });
        toast.success("Chapter assigned");
        load();
    }
    async function setTier(m, tier_id) {
        await api.put(`/members/${m.id}/tier`, { tier_id: tier_id || null });
        toast.success("Tier updated");
        load();
    }
    async function extendMembership(m, days) {
        await api.put(`/members/${m.id}/tier`, { tier_id: m.tier_id, extend_days: days });
        toast.success(`Extended by ${days} days`);
        load();
    }
    async function deleteMember(m) {
        if (!confirm(`Delete ${m.name}? This removes all of their data (RSVPs, hours, awards, transactions).`)) return;
        try {
            await api.delete(`/members/${m.id}`);
            toast.success("Member removed");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }
    async function resendSetPassword(m) {
        if (!confirm(`Send a fresh set-password email to ${m.email}? This invalidates any prior link.`)) return;
        try {
            const { data } = await api.post(`/admin/members/${m.id}/resend-set-password`);
            if (data?.sent) toast.success(`Set-password email sent to ${m.email}`);
            else toast.warning("Token created but the email failed to send. Check Brevo logs.");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to resend"); }
    }
    async function bulkResendSetPassword() {
        const count = members.filter((x) => x.pending_set_password).length;
        if (!count) { toast.info("No members are pending password setup."); return; }
        if (!confirm(`Send a fresh set-password email to all ${count} pending members? Each receives a new 7-day link and any older links are invalidated.`)) return;
        try {
            const { data } = await api.post("/admin/members/bulk-resend-set-password");
            const failed = data?.failed || 0;
            const skipped = data?.skipped_no_email || 0;
            if (failed === 0 && skipped === 0) toast.success(`Sent ${data?.sent || 0} set-password emails.`);
            else toast.warning(`Sent ${data?.sent || 0}, failed ${failed}, skipped (no email) ${skipped}.`);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bulk resend failed"); }
    }

    const [pendingOnly, setPendingOnly] = useState(false);
    const [outstandingOnly, setOutstandingOnly] = useState(false);
    // Row-selection state for the bulk-action menu. Keyed by user id so we
    // can survive search/filter changes (selection persists across narrowing).
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkForm, setBulkForm] = useState({ label: "10-Year Anniversary Fee", amount: "150" });
    const [bulkBusy, setBulkBusy] = useState(false);
    // Iter 152 — bulk region reassignment.
    const [regions, setRegions] = useState([]);
    const [regionOpen, setRegionOpen] = useState(false);
    const [regionTarget, setRegionTarget] = useState("");
    const [regionBusy, setRegionBusy] = useState(false);
    useEffect(() => {
        // Load regions once so the bulk-move dialog is instant when opened.
        (async () => {
            try {
                const { data } = await api.get("/regions");
                setRegions(data?.regions || []);
            } catch { /* non-fatal — dialog will just show an empty picker */ }
        })();
    }, []);
    const pendingCount = members.filter((x) => x.pending_set_password).length;
    const outstandingCount = members.filter((x) => (x.outstanding_balance_total || 0) > 0).length;
    let visibleMembers = members;
    if (pendingOnly) visibleMembers = visibleMembers.filter((x) => x.pending_set_password);
    if (outstandingOnly) visibleMembers = visibleMembers.filter((x) => (x.outstanding_balance_total || 0) > 0);

    const toggleSelectMember = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const toggleSelectAllVisible = () => {
        setSelectedIds((prev) => {
            const visibleIds = visibleMembers.map((m) => m.id);
            const allSelected = visibleIds.every((id) => prev.has(id));
            const next = new Set(prev);
            if (allSelected) visibleIds.forEach((id) => next.delete(id));
            else visibleIds.forEach((id) => next.add(id));
            return next;
        });
    };
    const clearSelection = () => setSelectedIds(new Set());

    async function runBulkAddLine() {
        const amount = parseFloat(bulkForm.amount);
        if (!bulkForm.label.trim() || !(amount > 0)) {
            toast.error("Enter a label and a positive amount.");
            return;
        }
        if (selectedIds.size === 0) {
            toast.error("Select at least one member.");
            return;
        }
        setBulkBusy(true);
        try {
            const { data } = await api.post("/admin/members/balance/bulk-add-line", {
                user_ids: Array.from(selectedIds),
                label: bulkForm.label.trim(),
                amount,
            });
            const parts = [`Added to ${data.created_count} member(s)`];
            if (data.skipped_count) parts.push(`skipped ${data.skipped_count} (already had "${data.label}")`);
            if (data.error_count) parts.push(`${data.error_count} error(s)`);
            toast.success(parts.join(" · "));
            setBulkOpen(false);
            clearSelection();
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bulk add failed");
        }
        setBulkBusy(false);
    }

    async function runBulkMoveRegion() {
        if (selectedIds.size === 0) {
            toast.error("Select at least one member.");
            return;
        }
        if (!regionTarget) {
            toast.error("Pick a target region (or 'Clear override').");
            return;
        }
        setRegionBusy(true);
        try {
            const region_id = regionTarget === "__clear__" ? "" : regionTarget;
            const { data } = await api.post("/admin/members/bulk-region", {
                user_ids: Array.from(selectedIds),
                region_id,
            });
            const label = data?.cleared
                ? `Cleared region override on ${data.modified} member${data.modified === 1 ? "" : "s"}`
                : `Moved ${data.modified} member${data.modified === 1 ? "" : "s"} into ${data.region_name || "region"}`;
            toast.success(label);
            setRegionOpen(false);
            setRegionTarget("");
            clearSelection();
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bulk region move failed");
        }
        setRegionBusy(false);
    }

    const chapterName = (id) => chapters.find((c) => c.id === id)?.name || "—";
    const tierName = (id) => tiers.find((t) => t.id === id)?.name || "—";

    // Column-sort support for the members table. Placed after chapterName /
    // tierName so the sort getters can safely reference them.
    const { rows: sortedMembers, sort: memberSort, setSort: setMemberSort } = useSortableTable(
        visibleMembers,
        {
            name: (m) => (m.name || "").toLowerCase(),
            role: (m) => (m.role || "").toLowerCase(),
            chapter: (m) => chapterName(m.chapter_id).toLowerCase(),
            tier: (m) => tierName(m.tier_id).toLowerCase(),
            status: (m) => (m.status || "").toLowerCase(),
            expires: (m) => m.tier_expires_at || m.expires_at || "",
        },
    );

    return (
        <div>
            <ApplicationsPanel onApproved={load} />
            <PendingIntakeChangesPanel onChanged={load} />
            <ChapterChangeRequestsPanel isFullAdmin={isFullAdmin} onChanged={load} />
            <CsvCheckinImportPanel isFullAdmin={isFullAdmin} />
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <Input placeholder="Search members…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-full max-w-sm" data-testid="admin-member-search" />
                <div className="flex items-center gap-2 flex-wrap">
                    {selectedIds.size > 0 && (
                        <>
                            <span className="text-xs font-semibold text-primary px-2" data-testid="bulk-selection-count">
                                {selectedIds.size} selected
                            </span>
                            <button
                                type="button"
                                onClick={() => setBulkOpen(true)}
                                className="rounded-full px-3 py-1.5 text-xs font-semibold border border-primary bg-primary text-primary-foreground hover:bg-primary/90 transition"
                                data-testid="bulk-add-anniversary-fee"
                                title="Add the same balance line to every selected member"
                            >
                                + Add anniversary fee to {selectedIds.size}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setRegionTarget(""); setRegionOpen(true); }}
                                className="rounded-full px-3 py-1.5 text-xs font-semibold border border-primary/40 bg-white text-primary hover:bg-primary/5 transition"
                                data-testid="bulk-move-region-btn"
                                title="Reassign the region on every selected member in one click"
                            >
                                Move {selectedIds.size} to region
                            </button>
                            <button
                                type="button"
                                onClick={clearSelection}
                                className="rounded-full px-3 py-1.5 text-xs font-semibold border border-border text-muted-foreground hover:bg-muted transition"
                                data-testid="bulk-clear-selection"
                            >
                                Clear
                            </button>
                        </>
                    )}
                    {outstandingCount > 0 && (
                        <button
                            type="button"
                            onClick={() => setOutstandingOnly((v) => !v)}
                            className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition ${outstandingOnly ? "bg-primary/15 text-primary border-primary" : "bg-white text-primary border-primary/30 hover:bg-primary/5"}`}
                            data-testid="filter-outstanding-balance"
                            title="Show only members who have an unpaid balance"
                        >
                            {outstandingOnly ? `Showing ${outstandingCount} with balance` : `${outstandingCount} with open balance`}
                        </button>
                    )}
                    {pendingCount > 0 && (
                        <>
                            <button
                                type="button"
                                onClick={() => setPendingOnly((v) => !v)}
                                className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition ${pendingOnly ? "bg-amber-100 text-amber-900 border-amber-400" : "bg-white text-amber-800 border-amber-300 hover:bg-amber-50"}`}
                                data-testid="filter-pending-setpw"
                                title="Show only members who haven't completed /set-password"
                            >
                                {pendingOnly ? `Showing ${pendingCount} pending` : `${pendingCount} pending password setup`}
                            </button>
                            <button
                                type="button"
                                onClick={bulkResendSetPassword}
                                className="rounded-full px-3 py-1.5 text-xs font-semibold border border-amber-400 bg-amber-500 text-white hover:bg-amber-600 transition"
                                data-testid="bulk-resend-setpw"
                                title={`Send a fresh set-password email to all ${pendingCount} pending members`}
                            >
                                Resend to all {pendingCount}
                            </button>
                        </>
                    )}
                    <BulkImportMembersDialog chapters={chapters.filter(officialOnly)} onImported={load} />
                    <ExportMembersCsvButton />
                    <ExportMembersPdfButton />
                    <NewMemberDialog chapters={chapters} tiers={tiers} onSaved={load} />
                </div>
            </div>
            <div className="bg-card rounded-2xl border border-border overflow-x-auto shadow-warm">
                <table className="w-full text-sm min-w-[1000px]">
                    <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-3 w-10">
                                <input
                                    type="checkbox"
                                    aria-label="Select all visible"
                                    onChange={toggleSelectAllVisible}
                                    checked={visibleMembers.length > 0 && visibleMembers.every((m) => selectedIds.has(m.id))}
                                    className="h-4 w-4 rounded cursor-pointer"
                                    data-testid="select-all-visible"
                                />
                            </th>
                            <SortableTh column="name" sort={memberSort} onSort={setMemberSort}>Name</SortableTh>
                            <SortableTh column="role" sort={memberSort} onSort={setMemberSort}>Role</SortableTh>
                            <SortableTh column="chapter" sort={memberSort} onSort={setMemberSort}>Chapter</SortableTh>
                            <SortableTh column="tier" sort={memberSort} onSort={setMemberSort}>Tier</SortableTh>
                            <SortableTh column="status" sort={memberSort} onSort={setMemberSort}>Status</SortableTh>
                            <SortableTh column="expires" sort={memberSort} onSort={setMemberSort}>Expires</SortableTh>
                            <SortableTh align="right">Actions</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedMembers.map((m) => (
                            <tr key={m.id} className="border-t border-border hover:bg-muted/30" data-testid={`admin-member-${m.id}`}>
                                <td className="px-3 py-3">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.has(m.id)}
                                        onChange={() => toggleSelectMember(m.id)}
                                        className="h-4 w-4 rounded cursor-pointer"
                                        data-testid={`select-member-${m.id}`}
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <div className="font-medium">
                                        {m.name}
                                        {m.line_name && <span className="text-xs ml-2 text-primary font-bold">&quot;{m.line_name}&quot;</span>}
                                        {(m.outstanding_balance_total || 0) > 0 && (
                                            <span
                                                className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-primary/15 text-primary border border-primary/30"
                                                title={`Open balance: $${(m.outstanding_balance_total || 0).toFixed(2)}`}
                                                data-testid={`owes-badge-${m.id}`}
                                            >
                                                ${(m.outstanding_balance_total || 0).toFixed(2)} owed
                                            </span>
                                        )}
                                        {m.pending_set_password && (
                                            <span
                                                className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300"
                                                title="This member hasn't completed the /set-password step from the welcome email yet."
                                                data-testid={`pending-setpw-${m.id}`}
                                            >
                                                Pending password setup
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground">{m.email}</div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5 ${m.role === "admin" ? "bg-primary/15 text-primary" : "bg-muted"}`}>
                                        {m.role}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    <Select value={m.chapter_id || ""} onValueChange={(v) => setChapter(m, v)}>
                                        <SelectTrigger className="h-8 rounded-full text-xs w-40" data-testid={`member-${m.id}-chapter`}>
                                            <SelectValue placeholder={chapterName(m.chapter_id)} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {chapters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </td>
                                <td className="px-4 py-3">
                                    <Select value={m.tier_id || ""} onValueChange={(v) => setTier(m, v)} disabled={!isFullAdmin}>
                                        <SelectTrigger className="h-8 rounded-full text-xs w-32" data-testid={`member-${m.id}-tier`}>
                                            <SelectValue placeholder={tierName(m.tier_id)} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {tiers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </td>
                                <td className="px-4 py-3">
                                    <StatusPill status={m.status} />
                                </td>
                                <td className="px-4 py-3 text-muted-foreground text-xs">
                                    {m.is_lifetime_member ? (
                                        <span className="text-primary font-semibold">Lifetime</span>
                                    ) : (
                                        <>
                                            {m.membership_expires_at ? formatCalendarDay(m.membership_expires_at, "MMM d, yyyy") : "—"}
                                            {m.within_grace && <span className="ml-2 text-[10px] bg-destructive/15 text-destructive rounded-full px-2 py-0.5">grace</span>}
                                        </>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <MemberCardDialog member={m} chapters={chapters} tiers={tiers} />
                                    {!m.is_lifetime_member && (
                                        <Button size="sm" variant="outline" className="rounded-full h-7 text-xs mx-1" onClick={() => extendMembership(m, 365)} data-testid={`extend-${m.id}`}>+1yr</Button>
                                    )}
                                    {m.pending_set_password && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-full h-7 text-xs mx-1 border-amber-400 text-amber-800 hover:bg-amber-50"
                                            onClick={() => resendSetPassword(m)}
                                            data-testid={`resend-setpw-${m.id}`}
                                        >
                                            Resend link
                                        </Button>
                                    )}
                                    <EditMemberDialog member={m} chapters={chapters} tiers={tiers} isFullAdmin={isFullAdmin} onSaved={load} />
                                    <Button size="sm" variant="outline" className="rounded-full h-7 text-xs ml-1" onClick={() => toggleRole(m)} data-testid={`toggle-role-${m.id}`}>
                                        {m.role === "admin" ? "Demote" : "Promote"}
                                    </Button>
                                    <Button size="sm" variant="ghost" className="rounded-full h-7 ml-1 text-destructive hover:bg-destructive/10" onClick={() => deleteMember(m)} data-testid={`delete-member-${m.id}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogContent className="rounded-2xl max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Add balance line to {selectedIds.size} member{selectedIds.size === 1 ? "" : "s"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 text-xs text-foreground/80">
                            The same label + amount will be applied to each selected member. Members who already have an unpaid line with this exact label are skipped (re-runs are safe). You can still edit amounts per-member afterward.
                        </div>
                        <div>
                            <Label className="text-xs">Line label</Label>
                            <Input
                                value={bulkForm.label}
                                onChange={(e) => setBulkForm({ ...bulkForm, label: e.target.value })}
                                placeholder="10-Year Anniversary Fee"
                                className="rounded-xl mt-1"
                                data-testid="bulk-line-label"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Amount (USD)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={bulkForm.amount}
                                onChange={(e) => setBulkForm({ ...bulkForm, amount: e.target.value })}
                                placeholder="150.00"
                                className="rounded-xl mt-1"
                                data-testid="bulk-line-amount"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setBulkOpen(false)} className="rounded-full" data-testid="bulk-cancel">Cancel</Button>
                            <Button onClick={runBulkAddLine} disabled={bulkBusy} className="rounded-full bg-primary hover:bg-primary/90" data-testid="bulk-confirm">
                                {bulkBusy ? "Adding…" : `Add to ${selectedIds.size} member${selectedIds.size === 1 ? "" : "s"}`}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog open={regionOpen} onOpenChange={setRegionOpen}>
                <DialogContent className="rounded-2xl max-w-md" data-testid="bulk-region-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Move {selectedIds.size} member{selectedIds.size === 1 ? "" : "s"} to a region</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 text-xs text-foreground/80">
                            Overrides the region on every selected member — they will be counted under the chosen region regardless of the state on their profile. Choose <span className="font-semibold">Clear override</span> to revert everyone back to state-based bucketing.
                        </div>
                        <div>
                            <Label className="text-xs">Target region</Label>
                            <Select value={regionTarget} onValueChange={setRegionTarget}>
                                <SelectTrigger className="rounded-xl mt-1" data-testid="bulk-region-target"><SelectValue placeholder="Pick a region…" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__clear__">— Clear override (use state) —</SelectItem>
                                    {regions.map((r) => (
                                        <SelectItem key={r.id} value={r.id}>{r.emoji || "📍"} {r.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setRegionOpen(false)} className="rounded-full" data-testid="bulk-region-cancel">Cancel</Button>
                            <Button onClick={runBulkMoveRegion} disabled={regionBusy || !regionTarget} className="rounded-full bg-primary hover:bg-primary/90" data-testid="bulk-region-confirm">
                                {regionBusy ? "Applying…" : `Apply to ${selectedIds.size} member${selectedIds.size === 1 ? "" : "s"}`}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

        </div>
    );
}


function ExportMembersCsvButton() {
    const [busy, setBusy] = useState(false);
    async function run() {
        setBusy(true);
        try {
            const res = await api.get("/admin/members/export.csv", { responseType: "blob" });
            const disp = res.headers?.["content-disposition"] || "";
            const m = /filename="?([^"]+)"?/.exec(disp);
            const filename = m ? m[1] : `aop-members-${new Date().toISOString().slice(0, 10)}.csv`;
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url; a.download = filename; document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
            toast.success("Members CSV downloaded");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
        }
        setBusy(false);
    }
    return (
        <Button
            onClick={run}
            disabled={busy}
            variant="outline"
            className="rounded-full"
            data-testid="export-members-csv-btn"
            title="Download full member directory CSV — includes birth date, address, awards, RSVPs, events attended, hours & donations totals."
        >
            <Download className="h-4 w-4 mr-1" />{busy ? "Preparing…" : "Export CSV"}
        </Button>
    );
}

/**
 * Iter 129 — companion to ExportMembersCsvButton. Downloads a portrait PDF
 * where each member is rendered as a full detail card with every field from
 * the CSV export — solves "PDF cuts off names" and "PDF doesn't match CSV".
 *
 * Iter 131 — split button: default click downloads the portrait card layout;
 * the chevron opens a menu to pick a landscape "roster" (one-row-per-member)
 * layout that's easier to print as a flat sheet.
 */
function ExportMembersPdfButton() {
    const [busy, setBusy] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    async function run(layout) {
        setBusy(true);
        setMenuOpen(false);
        try {
            const params = layout && layout !== "cards" ? { layout } : {};
            const res = await api.get("/admin/members/export.pdf", { params, responseType: "blob" });
            const disp = res.headers?.["content-disposition"] || "";
            const m = /filename="?([^"]+)"?/.exec(disp);
            const filename = m ? m[1] : `aop-members-${new Date().toISOString().slice(0, 10)}.pdf`;
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = filename; document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
            toast.success(layout === "rows" ? "Roster PDF downloaded" : "Members PDF downloaded");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
        }
        setBusy(false);
    }

    return (
        <div className="relative inline-flex" data-testid="export-members-pdf-group">
            <Button
                onClick={() => run("cards")}
                disabled={busy}
                variant="outline"
                className="rounded-full rounded-r-none border-r-0"
                data-testid="export-members-pdf-btn"
                title="Download the portrait, one-page-per-member card layout with every field."
            >
                <Printer className="h-4 w-4 mr-1" />{busy ? "Preparing…" : "Export PDF"}
            </Button>
            <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="border border-input rounded-full rounded-l-none px-2 hover:bg-accent focus:outline-none disabled:opacity-50"
                data-testid="export-members-pdf-menu-btn"
                disabled={busy}
                title="Pick a layout"
            >
                <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {menuOpen && (
                <div
                    className="absolute right-0 top-full mt-1 z-30 bg-popover border border-input rounded-xl shadow-lg overflow-hidden text-sm min-w-[240px]"
                    data-testid="export-members-pdf-menu"
                    onMouseLeave={() => setMenuOpen(false)}
                >
                    <button
                        type="button"
                        onClick={() => run("cards")}
                        className="w-full text-left px-3 py-2 hover:bg-accent flex items-start gap-2"
                        data-testid="export-members-pdf-layout-cards"
                    >
                        <FileText className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                            <div className="font-medium">Detail cards (portrait)</div>
                            <div className="text-xs text-muted-foreground">One full-page card per member — every field grouped by section.</div>
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={() => run("rows")}
                        className="w-full text-left px-3 py-2 hover:bg-accent flex items-start gap-2 border-t border-input"
                        data-testid="export-members-pdf-layout-rows"
                    >
                        <LayoutDashboard className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                            <div className="font-medium">Roster (landscape rows)</div>
                            <div className="text-xs text-muted-foreground">One line per member — print-friendly flat sheet.</div>
                        </span>
                    </button>
                </div>
            )}
        </div>
    );
}


function NewMemberDialog({ chapters, tiers, onSaved }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        email: "", password: "", title: "", first_name: "", middle_name: "", last_name: "",
        line_name: "", username: "", phone: "", city: "", address: "", birthdate: "",
        branch_of_service: "", role: "member",
        chapter_id: "", tier_id: "", member_status: "active",
    });
    const [busy, setBusy] = useState(false);
    async function save() {
        if (!form.email || !form.password) { toast.error("Email and password required"); return; }
        setBusy(true);
        try {
            const payload = { ...form };
            if (!payload.chapter_id) delete payload.chapter_id;
            if (!payload.tier_id) delete payload.tier_id;
            if (!payload.member_status) delete payload.member_status;
            await api.post("/admin/members", payload);
            toast.success("Member created");
            setOpen(false);
            setForm({ email: "", password: "", title: "", first_name: "", middle_name: "", last_name: "", line_name: "", username: "", phone: "", city: "", address: "", birthdate: "", branch_of_service: "", role: "member", chapter_id: "", tier_id: "", member_status: "active" });
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Create failed"); }
        setBusy(false);
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="new-member-btn">
                    <Plus className="h-4 w-4 mr-1" /> New member
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">Add a new member</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1.5" data-testid="nm-email" /></div>
                        <div><Label>Temporary password *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl mt-1.5" data-testid="nm-password" /></div>
                    </div>
                    <div className="grid grid-cols-[110px_1fr_100px_1fr] gap-3">
                        <div>
                            <Label>Title</Label>
                            <Select value={form.title} onValueChange={(v) => setForm({ ...form, title: v === "__none__" ? "" : v })}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="nm-title"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">— None —</SelectItem>
                                    {["Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Prof.", "Rev.", "Hon.", "Mx."].map((t) => (
                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-xl mt-1.5" data-testid="nm-first" /></div>
                        <div><Label>Middle</Label><Input value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} className="rounded-xl mt-1.5" /></div>
                        <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-xl mt-1.5" data-testid="nm-last" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Line name</Label><Input value={form.line_name} onChange={(e) => setForm({ ...form, line_name: e.target.value })} className="rounded-xl mt-1.5" placeholder="e.g. Patriot" data-testid="nm-line" /></div>
                        <div><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl mt-1.5" /></div>
                        <div><Label>Birthdate</Label><Input type="date" value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} className="rounded-xl mt-1.5" data-testid="nm-birthdate" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-xl mt-1.5" data-testid="nm-address" /></div>
                        <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Branch of service</Label>
                            <Select
                                value={form.branch_of_service || "__none__"}
                                onValueChange={(v) => setForm({ ...form, branch_of_service: v === "__none__" ? "" : v })}
                            >
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="nm-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">— Not specified —</SelectItem>
                                    {MILITARY_BRANCHES.map((b) => (
                                        <SelectItem key={b} value={b}>{b}</SelectItem>
                                    ))}
                                    {form.branch_of_service && !MILITARY_BRANCHES.includes(form.branch_of_service) && (
                                        <SelectItem value={form.branch_of_service}>{form.branch_of_service} (legacy)</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Member status</Label>
                            <Select value={form.member_status} onValueChange={(v) => setForm({ ...form, member_status: v })}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="nm-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="inactive">Inactive</SelectItem>
                                    <SelectItem value="grace">Grace period</SelectItem>
                                    <SelectItem value="expired">Expired</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label>Chapter</Label>
                            <Select value={form.chapter_id} onValueChange={(v) => setForm({ ...form, chapter_id: v })}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="nm-chapter"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>{chapters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Tier</Label>
                            <Select value={form.tier_id} onValueChange={(v) => setForm({ ...form, tier_id: v })}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="nm-tier"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>{tiers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Role</Label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger className="rounded-xl mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="member">Member</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
                <DialogFooter><Button onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90" data-testid="nm-save-btn">{busy ? "Creating…" : "Create member"}</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* -------- Admin tab permissions editor (full-Admin only) -------- */
const ALL_ADMIN_TAB_KEYS = [
    "dashboard", "members", "chapters", "tiers", "events", "hours", "awards",
    "gear", "causes", "reports", "email", "news", "pages", "documents", "recruitment",
];
const ADMIN_ROLE_DEFAULT_TABS = {
    full: new Set(ALL_ADMIN_TAB_KEYS),
    membership_manager: new Set(["dashboard", "members", "chapters", "tiers", "events", "awards", "reports", "email", "recruitment"]),
    operations_manager: new Set(["dashboard", "members", "chapters", "events", "hours", "causes", "reports", "news", "documents", "recruitment"]),
    governor_manager: new Set(["dashboard", "hours", "causes", "reports", "recruitment"]),
};
function AdminTabPermissionsEditor({ value = [], onChange, disabled = false, adminRole = "full" }) {
    const hasCustom = Array.isArray(value) && value.length > 0;
    const roleDefault = ADMIN_ROLE_DEFAULT_TABS[adminRole] || ADMIN_ROLE_DEFAULT_TABS.full;
    const effective = hasCustom ? new Set(value) : roleDefault;
    function toggle(tab, checked) {
        if (disabled) return;
        // First flip from role-default into a concrete custom set, then mutate.
        const base = hasCustom ? new Set(value) : new Set(roleDefault);
        if (checked) base.add(tab); else base.delete(tab);
        // Always require dashboard so the admin can actually see /admin.
        base.add("dashboard");
        onChange(Array.from(base).sort());
    }
    function reset() { if (!disabled) onChange([]); }
    return (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="em-tab-perms">
            <div className="flex items-center justify-between gap-2 mb-2">
                <div>
                    <div className="text-sm font-semibold">Custom tab permissions</div>
                    <div className="text-xs text-muted-foreground">
                        {hasCustom
                            ? "Custom — overrides the role default above."
                            : "Following the role default. Check any box to start customizing."}
                    </div>
                </div>
                {hasCustom && !disabled && (
                    <button
                        type="button"
                        onClick={reset}
                        className="text-xs text-primary hover:underline shrink-0"
                        data-testid="em-tab-perms-reset"
                    >
                        Reset to role default
                    </button>
                )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {ALL_ADMIN_TAB_KEYS.map((tab) => {
                    const checked = effective.has(tab);
                    const isDashboard = tab === "dashboard";
                    return (
                        <label
                            key={tab}
                            className={`flex items-center gap-2 text-sm rounded-lg px-2 py-1.5 ${disabled ? "opacity-60 cursor-not-allowed" : "hover:bg-white cursor-pointer"}`}
                            data-testid={`em-tab-perm-${tab}`}
                        >
                            <input
                                type="checkbox"
                                className="h-4 w-4 rounded"
                                checked={checked}
                                disabled={disabled || isDashboard}
                                onChange={(e) => toggle(tab, e.target.checked)}
                            />
                            <span className="capitalize">{tab}</span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}

function EditMemberDialog({ member, chapters, tiers, isFullAdmin = true, onSaved }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({
                email: member.email,
                title: member.title || "",
                first_name: member.first_name || "",
                middle_name: member.middle_name || "",
                last_name: member.last_name || "",
                line_name: member.line_name || "",
                intake_line: member.intake_line || "",
                intake_completed_at: member.intake_completed_at || "",
                username: member.username || "",
                phone: member.phone || "",
                city: member.city || "",
                address: member.address || "",
                state: member.state || "",
                zip_code: member.zip_code || "",
                country: member.country || "",
                birthdate: member.birthdate ? member.birthdate.slice(0, 10) : "",
                branch_of_service: member.branch_of_service || "",
                bio: member.bio || "",
                avatar_url: member.avatar_url || "",
                chapter_id: member.chapter_id || "",
                tier_id: member.tier_id || "",
                role: member.role,
                admin_role: member.admin_role || "full",
                allowed_tabs: Array.isArray(member.allowed_tabs) ? [...member.allowed_tabs] : [],
                join_date: member.join_date ? member.join_date.slice(0, 10) : "",
                membership_expires_at: member.membership_expires_at ? member.membership_expires_at.slice(0, 10) : "",
                member_status: member.status_override || member.status || "active",
                new_password: "",
                assignment_history: member.assignment_history || [],
            });
        }
    }, [open, member]);

    async function save() {
        setBusy(true);
        try {
            const payload = Object.fromEntries(Object.entries(form).filter(([_, v]) => v !== "" && v !== null && v !== undefined));
            if (!payload.new_password) delete payload.new_password;
            // Always send assignment_history even when empty so admins can clear all rows.
            payload.assignment_history = form.assignment_history || [];
            // Always send allowed_tabs (even when empty) so admins can clear the custom override
            // and revert the user back to their admin_role default permissions.
            if (form.role === "admin") {
                payload.allowed_tabs = form.allowed_tabs || [];
            } else {
                payload.allowed_tabs = [];
            }
            // Convert join_date YYYY-MM-DD to ISO timestamp so backend recomputes membership_expires_at
            if (payload.join_date && payload.join_date.length === 10) {
                payload.join_date = new Date(`${payload.join_date}T00:00:00Z`).toISOString();
            }
            // Convert membership_expires_at YYYY-MM-DD to ISO so backend stores it
            // verbatim (overriding the auto-calculation from join_date)
            if (payload.membership_expires_at && payload.membership_expires_at.length === 10) {
                payload.membership_expires_at = new Date(`${payload.membership_expires_at}T00:00:00Z`).toISOString();
            }
            await api.put(`/members/${member.id}`, payload);
            toast.success("Saved");
            setOpen(false);
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        setBusy(false);
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" data-testid={`edit-member-${member.id}`}>Edit</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl flex flex-wrap items-center gap-2">
                        Edit {member.name}
                        {member.pending_set_password && (
                            // Show the pending-password badge inside the dialog header so
                            // admins know they're editing a member who hasn't completed
                            // onboarding yet — easy to miss when the table row is no longer
                            // visible. Editing a member does NOT clear this flag; only the
                            // member completing /set-password (or an admin setting a new
                            // password in this dialog) clears it.
                            <span
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300"
                                title="This member hasn't completed the /set-password step from the welcome email yet."
                                data-testid={`edit-pending-setpw-${member.id}`}
                            >
                                Pending password setup
                            </span>
                        )}
                    </DialogTitle>
                    {member.pending_set_password && (
                        <p className="text-xs text-amber-700 mt-1" data-testid={`edit-pending-setpw-help-${member.id}`}>
                            They haven&apos;t signed in yet. Saving edits below keeps this flag in place — set a new password in the optional password field if you want to clear it manually.
                        </p>
                    )}
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1.5" data-testid="em-email" /></div>
                        <div><Label>Username</Label><Input value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    </div>
                    <div className="grid grid-cols-[110px_1fr_100px_1fr] gap-3">
                        <div>
                            <Label>Title</Label>
                            <Select value={form.title || "__none__"} onValueChange={(v) => setForm({ ...form, title: v === "__none__" ? "" : v })}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="em-title"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">— None —</SelectItem>
                                    {["Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Prof.", "Rev.", "Hon.", "Mx."].map((t) => (
                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div><Label>First name</Label><Input value={form.first_name || ""} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-xl mt-1.5" data-testid="em-first" /></div>
                        <div><Label>Middle</Label><Input value={form.middle_name || ""} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} className="rounded-xl mt-1.5" /></div>
                        <div><Label>Last name</Label><Input value={form.last_name || ""} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Line name</Label><Input value={form.line_name || ""} onChange={(e) => setForm({ ...form, line_name: e.target.value })} className="rounded-xl mt-1.5" data-testid="em-line" /></div>
                        <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Intake line</Label><Input value={form.intake_line || ""} onChange={(e) => setForm({ ...form, intake_line: e.target.value })} className="rounded-xl mt-1.5" placeholder="Spring '24 — A1" data-testid="em-intake-line" /></div>
                        <div><Label>Intake completion date <span className="text-xs text-muted-foreground font-normal">(Month/Year)</span></Label><Input type="month" value={form.intake_completed_at || ""} onChange={(e) => setForm({ ...form, intake_completed_at: e.target.value })} className="rounded-xl mt-1.5" data-testid="em-intake-completed-at" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Address</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-xl mt-1.5" data-testid="em-address" /></div>
                        <div><Label>City</Label><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div><Label>State</Label><Input value={form.state || ""} onChange={(e) => setForm({ ...form, state: e.target.value })} className="rounded-xl mt-1.5" data-testid="em-state" placeholder="TX" /></div>
                        <div><Label>Zip code</Label><Input value={form.zip_code || ""} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="rounded-xl mt-1.5" data-testid="em-zip" placeholder="77001" /></div>
                        <div><Label>Country</Label><Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-xl mt-1.5" data-testid="em-country" placeholder="USA" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Date joined <span className="text-xs text-muted-foreground font-normal">(recomputes renewal date)</span></Label>
                            <Input type="date" value={form.join_date || ""} onChange={(e) => setForm({ ...form, join_date: e.target.value })} className="rounded-xl mt-1.5" data-testid="em-join-date" />
                        </div>
                        <div>
                            <Label>Membership expires <span className="text-xs text-muted-foreground font-normal">{isFullAdmin ? "(editable — overrides auto from join date)" : "(auto from join date)"}</span></Label>
                            {isFullAdmin ? (
                                <Input
                                    type="date"
                                    value={form.membership_expires_at || ""}
                                    onChange={(e) => setForm({ ...form, membership_expires_at: e.target.value })}
                                    className="rounded-xl mt-1.5"
                                    data-testid="em-membership-expires"
                                />
                            ) : (
                                <div className="rounded-xl mt-1.5 px-3 py-2 bg-muted/40 text-sm text-muted-foreground" data-testid="em-expires-preview">
                                    {(() => {
                                        const selectedTier = tiers.find((t) => t.id === form.tier_id);
                                        if (selectedTier?.is_lifetime) return <span className="text-primary font-semibold">Lifetime · no expiration</span>;
                                        if (form.join_date) return format(new Date(new Date(`${form.join_date}T00:00:00Z`).getTime() + 365 * 86400000), "MMM d, yyyy");
                                        return member.membership_expires_at ? formatCalendarDay(member.membership_expires_at, "MMM d, yyyy") : "—";
                                    })()}
                                </div>
                            )}
                            {isFullAdmin && form.membership_expires_at && (() => {
                                const selectedTier = tiers.find((t) => t.id === form.tier_id);
                                return selectedTier?.is_lifetime
                                    ? <div className="text-xs text-primary mt-1 font-semibold">Lifetime tier — expiration ignored.</div>
                                    : null;
                            })()}
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div><Label>Birthdate</Label><Input type="date" value={form.birthdate || ""} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} className="rounded-xl mt-1.5" data-testid="em-birthdate" /></div>
                        <div>
                            <Label>Branch of service</Label>
                            <Select
                                value={form.branch_of_service || "__none__"}
                                onValueChange={(v) => setForm({ ...form, branch_of_service: v === "__none__" ? "" : v })}
                            >
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="em-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">— Not specified —</SelectItem>
                                    {MILITARY_BRANCHES.map((b) => (
                                        <SelectItem key={b} value={b}>{b}</SelectItem>
                                    ))}
                                    {form.branch_of_service && !MILITARY_BRANCHES.includes(form.branch_of_service) && (
                                        <SelectItem value={form.branch_of_service}>{form.branch_of_service} (legacy)</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Member status</Label>
                            <Select value={form.member_status || "active"} onValueChange={(v) => setForm({ ...form, member_status: v })}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="em-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="inactive">Inactive</SelectItem>
                                    <SelectItem value="grace">Grace period</SelectItem>
                                    <SelectItem value="expired">Expired</SelectItem>
                                    <SelectItem value="deceased">Deceased (Omega Chapter)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {/* Iter 38: admin replaces Avatar URL with upload. The endpoint
                            /members/me/avatar only works for self — for arbitrary users we
                            use the generic /email/upload-image route which returns a URL,
                            then we save it to form.avatar_url and the regular PUT /members
                            persists it. */}
                        <div>
                            <Label>Profile photo</Label>
                            <div className="flex items-center gap-3 mt-1.5">
                                {form.avatar_url ? (
                                    <img src={form.avatar_url} alt="" className="h-14 w-14 rounded-full object-contain border border-border" />
                                ) : (
                                    <div className="h-14 w-14 rounded-full border-2 border-dashed border-border grid place-items-center text-[10px] text-muted-foreground">
                                        No photo
                                    </div>
                                )}
                                <label className="rounded-full border px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50 inline-flex items-center gap-1.5" data-testid="em-avatar-upload">
                                    {form.avatar_url ? "Replace" : "Upload"}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={async (e) => {
                                            const f = e.target.files?.[0];
                                            if (!f) return;
                                            const fd = new FormData();
                                            fd.append("file", f);
                                            try {
                                                const { data } = await api.post("/email/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                                setForm({ ...form, avatar_url: data.url });
                                                toast.success("Photo uploaded — Save to persist");
                                            } catch (err) {
                                                toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
                                            }
                                            e.target.value = "";
                                        }}
                                    />
                                </label>
                                {form.avatar_url && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        type="button"
                                        onClick={() => setForm({ ...form, avatar_url: "" })}
                                        className="text-xs text-destructive"
                                        data-testid="em-avatar-remove"
                                    >
                                        Remove
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div></div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label>Chapter</Label>
                            <Select value={form.chapter_id || ""} onValueChange={(v) => setForm({ ...form, chapter_id: v })}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="em-chapter"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>{chapters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Tier {!isFullAdmin && <span className="text-xs font-normal text-muted-foreground">(only full Admins can change)</span>}</Label>
                            <Select value={form.tier_id || ""} onValueChange={(v) => setForm({ ...form, tier_id: v })} disabled={!isFullAdmin}>
                                <SelectTrigger className="rounded-xl mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>{tiers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Role</Label>
                            <Select value={form.role || "member"} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="em-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="member">Member</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    {form.role === "admin" && (
                        <div>
                            <Label>Admin permissions</Label>
                            <Select value={form.admin_role || "full"} onValueChange={(v) => setForm({ ...form, admin_role: v })} disabled={!isFullAdmin}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="em-admin-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="full">Admin (full access)</SelectItem>
                                    <SelectItem value="membership_manager">Membership Manager</SelectItem>
                                    <SelectItem value="operations_manager">Operations Manager</SelectItem>
                                    <SelectItem value="governor_manager">Governor Manager (chapter-scoped)</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="text-xs text-muted-foreground mt-1.5">
                                Membership Manager: dashboard, members, chapters, tiers, events, awards, reports, email.<br />
                                Operations Manager: dashboard, members, chapters, events, hours, causes, reports, news.<br />
                                Governor Manager: only their own chapter — dashboard, hours, causes, reports.
                            </div>
                            <AdminTabPermissionsEditor
                                value={form.allowed_tabs || []}
                                onChange={(next) => setForm({ ...form, allowed_tabs: next })}
                                disabled={!isFullAdmin}
                                adminRole={form.admin_role || "full"}
                            />
                        </div>
                    )}
                    <div><Label>Bio</Label><Textarea rows={3} value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                className="mt-1 h-4 w-4 rounded"
                                checked={!!form.trust_zeffy}
                                onChange={(e) => setForm({ ...form, trust_zeffy: e.target.checked })}
                                data-testid="em-trust-zeffy"
                            />
                            <div className="flex-1">
                                <div className="text-sm font-semibold">Auto-approve Zeffy dues payments</div>
                                <div className="text-xs text-muted-foreground">When ON, dues submissions with a valid-looking Zeffy receipt # are immediately approved and membership is extended 365 days — no admin review needed.</div>
                            </div>
                        </label>
                    </div>
                    <div><Label>Reset password (optional)</Label><Input type="password" value={form.new_password || ""} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="rounded-xl mt-1.5" placeholder="Leave blank to keep current" data-testid="em-new-password" /></div>
                    <MemberBalanceEditor memberId={member.id} memberName={member.name} />
                    <AssignmentHistoryEditor
                        value={form.assignment_history}
                        onChange={(v) => setForm({ ...form, assignment_history: v })}
                        chapters={chapters}
                    />
                </div>
                <DialogFooter><Button onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90" data-testid="em-save-btn">{busy ? "Saving…" : "Save changes"}</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* -------- Chapters -------- */
function ChaptersAdmin() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/chapters").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);
    async function del(id) {
        if (!confirm("Delete chapter? Members will be unassigned.")) return;
        await api.delete(`/chapters/${id}`);
        toast.success("Deleted");
        load();
    }
    return (
        <div>
            <div className="flex justify-end mb-4">
                <ChapterDialog onSaved={load} trigger={<Button className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="new-chapter-btn"><Plus className="h-4 w-4 mr-1" />New chapter</Button>} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                {items.map((c) => (
                    <div key={c.id} className="bg-card border border-border rounded-2xl p-5" data-testid={`admin-chapter-${c.id}`}>
                        <div className="flex items-start gap-3">
                            {c.logo_url && <img src={mediaUrl(c.logo_url)} alt={c.name} className="w-12 h-12 rounded-xl object-contain border border-border shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <div className="font-heading font-semibold text-lg">{c.name}</div>
                                <div className="text-sm text-muted-foreground">
                                    {c.region && <>{c.region} region</>}
                                    {c.region && c.state ? " · " : ""}
                                    {c.state}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">Founded {c.founded_year || "—"} · {c.member_count} members</div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                                <ChapterDialog chapter={c} onSaved={load} trigger={<Button variant="outline" size="sm" className="rounded-full">Edit</Button>} />
                                <Button variant="ghost" size="icon" onClick={() => del(c.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                            </div>
                        </div>
                        {c.description && <p className="text-sm mt-3 text-muted-foreground">{c.description}</p>}
                    </div>
                ))}
            </div>
        </div>
    );
}

function ChapterDialog({ chapter, onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: chapter?.name || "",
        region: chapter?.region || "",
        state: chapter?.state || "",
        founded_year: chapter?.founded_year || "",
        description: chapter?.description || "",
        logo_url: chapter?.logo_url || "",
        // Iter 171 — optional Governor. Empty string clears; matches Lt. Governor UX.
        governor_user_id: chapter?.governor_user_id || "",
        // Iter 154 — optional Lt. Governor. Empty string = "no LG assigned"
        // and clears the field on save.
        lieutenant_governor_user_id: chapter?.lieutenant_governor_user_id || "",
        // Iter 180 — additional officer spotlights (each independent; a
        // member can hold multiple titles across the officer line-up).
        president_user_id: chapter?.president_user_id || "",
        vice_president_user_id: chapter?.vice_president_user_id || "",
        secretary_user_id: chapter?.secretary_user_id || "",
        treasurer_user_id: chapter?.treasurer_user_id || "",
        director_of_membership_user_id: chapter?.director_of_membership_user_id || "",
        director_of_operations_user_id: chapter?.director_of_operations_user_id || "",
        director_of_marketing_user_id: chapter?.director_of_marketing_user_id || "",
        instructor_user_id: chapter?.instructor_user_id || "",
        // Iter 182 — chapter-level Liaison officer spotlight.
        liaison_user_id: chapter?.liaison_user_id || "",
    });
    const [members, setMembers] = useState([]);
    useEffect(() => {
        // Only fetch the member list when the dialog is open so a huge org
        // roster isn't loaded for every card render.
        if (!open) return;
        api.get("/members").then((r) => setMembers(r.data || [])).catch(() => setMembers([]));
    }, [open]);
    const OFFICER_SLOTS = CHAPTER_OFFICER_SLOTS.map((s) => [s.key, s.title]);
    async function save() {
        if (!form.name) { toast.error("Pick a chapter from the list"); return; }
        try {
            const payload = {
                ...form,
                founded_year: form.founded_year ? Number(form.founded_year) : null,
            };
            // Normalize officer picker values: "__clear__" → "" (unassign).
            for (const [key] of OFFICER_SLOTS) {
                if (payload[key] === "__clear__") payload[key] = "";
            }
            if (chapter) await api.put(`/chapters/${chapter.id}`, payload);
            else await api.post("/chapters", payload);
            toast.success("Saved");
            setOpen(false);
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    }
    async function uploadLogo(file) {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10 MB"); return; }
        const fd = new FormData(); fd.append("file", file);
        try {
            const { data } = await api.post("/chapters/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((p) => ({ ...p, logo_url: data.url }));
            toast.success("Logo uploaded");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{chapter ? "Edit chapter" : "New chapter"}</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                    <div>
                        <Label>Chapter name *</Label>
                        <Input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="e.g. Texas, Florida, Tri-South, DMV, Carolinas, Pacific Northwest"
                            className="rounded-xl mt-1.5"
                            data-testid="chapter-name-input"
                        />
                        <div className="text-xs text-muted-foreground mt-1.5">Type any name. Existing official chapters: Texas, Florida, Tri-South, DMV.</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Region</Label><Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="e.g. South, Mid-Atlantic" className="rounded-xl mt-1.5" data-testid="chapter-region-input" /></div>
                        <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="e.g. TX, FL, Multi" className="rounded-xl mt-1.5" data-testid="chapter-state-input" /></div>
                    </div>
                    <div><Label>Founded year</Label><Input type="number" value={form.founded_year} onChange={(e) => setForm({ ...form, founded_year: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3">
                        <div>
                            <div className="font-heading text-sm font-semibold">Officer spotlights</div>
                            <div className="text-xs text-muted-foreground">
                                Each slot is independent — one member can hold several titles. Leave blank for any role that isn't assigned.
                            </div>
                        </div>
                        {OFFICER_SLOTS.map(([key, label]) => (
                            <div key={key}>
                                <Label>{label}</Label>
                                <Select
                                    value={form[key] || "__clear__"}
                                    onValueChange={(v) => setForm({ ...form, [key]: v === "__clear__" ? "" : v })}
                                >
                                    <SelectTrigger className="rounded-xl mt-1.5" data-testid={`chapter-officer-select-${key}`}>
                                        <SelectValue placeholder={`Pick a ${label}\u2026`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__clear__">&mdash; No {label} &mdash;</SelectItem>
                                        {members.map((m) => (
                                            <SelectItem key={m.id} value={m.id}>{m.name}{m.email ? ` · ${m.email}` : ""}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ))}
                    </div>
                    <div>
                        <Label>Chapter logo</Label>
                        <div className="flex items-center gap-2 mt-1.5">
                            <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="rounded-xl flex-1" placeholder="https://… or upload" data-testid="chapter-logo-url" />
                            <label className="cursor-pointer">
                                <Button asChild variant="outline" size="sm" className="rounded-full" type="button">
                                    <span data-testid="chapter-logo-upload-btn"><Upload className="h-3.5 w-3.5 mr-1" />Upload</span>
                                </Button>
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                            </label>
                        </div>
                        {form.logo_url && (
                            <img src={mediaUrl(form.logo_url)} alt="Logo preview" className="mt-2 w-24 h-24 rounded-2xl object-contain border border-border" />
                        )}
                    </div>
                </div>
                <DialogFooter><Button onClick={save} className="rounded-full bg-primary hover:bg-primary/90" data-testid="chapter-save-btn">Save</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* -------- Tiers -------- */
function TiersAdmin() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/tiers").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);
    async function del(id) { if (!confirm("Delete this tier?")) return; await api.delete(`/tiers/${id}`); load(); }
    return (
        <div>
            <div className="flex justify-end mb-4">
                <TierDialog onSaved={load} trigger={<Button className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="new-tier-btn"><Plus className="h-4 w-4 mr-1" />New tier</Button>} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((t) => (
                    <div key={t.id} className="bg-card border border-border rounded-2xl p-5" data-testid={`admin-tier-${t.id}`}>
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-12 rounded-full" style={{ backgroundColor: t.color }} />
                                <div>
                                    <div className="font-heading font-semibold">{t.name}</div>
                                    <div className="text-xs text-muted-foreground">${t.annual_dues}/year · {t.member_count} members</div>
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <TierDialog tier={t} onSaved={load} trigger={<Button variant="outline" size="sm" className="rounded-full">Edit</Button>} />
                                <Button variant="ghost" size="icon" onClick={() => del(t.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                            </div>
                        </div>
                        {t.description && <p className="text-sm mt-3 text-muted-foreground leading-relaxed">{t.description}</p>}
                    </div>
                ))}
            </div>
        </div>
    );
}

function TierDialog({ tier, onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: tier?.name || "",
        order: tier?.order || 0,
        color: tier?.color || "#E86A58",
        annual_dues: tier?.annual_dues ?? 60,
        description: tier?.description || "",
    });
    async function save() {
        try {
            const payload = { ...form, order: Number(form.order), annual_dues: Number(form.annual_dues) };
            if (tier) await api.put(`/tiers/${tier.id}`, payload);
            else await api.post("/tiers", payload);
            toast.success("Saved");
            setOpen(false);
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{tier ? "Edit tier" : "New tier"}</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                    <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5" data-testid="tier-name-input" /></div>
                    <div className="grid grid-cols-3 gap-3">
                        <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="rounded-xl mt-1.5" /></div>
                        <div><Label>Dues ($)</Label><Input type="number" value={form.annual_dues} onChange={(e) => setForm({ ...form, annual_dues: e.target.value })} className="rounded-xl mt-1.5" /></div>
                        <div><Label>Color</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="rounded-xl mt-1.5 h-10" /></div>
                    </div>
                    <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1.5" /></div>
                </div>
                <DialogFooter><Button onClick={save} className="rounded-full bg-primary hover:bg-primary/90" data-testid="tier-save-btn">Save</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* -------- Hours queue (admin review) -------- */
function HoursAdmin() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [search, setSearch] = useState("");
    const load = () => api.get(`/hours${filter !== "all" ? `?status_filter=${filter}` : ""}`).then(({ data }) => setItems(data));
    useEffect(() => { load(); }, [filter]);
    async function review(id, status) { await api.put(`/hours/${id}/review`, { status, note: "" }); toast.success(status); load(); }
    async function del(h) {
        // Hard delete from the queue — only used when the entry is wrong (typo,
        // duplicate, etc.) and shouldn't show up in member history either.
        if (!confirm(`Permanently delete ${h.user_name}'s ${h.hours}h entry on ${h.date ? formatCalendarDay(h.date, "MMM d, yyyy") : "unknown date"}?\n\nThis cannot be undone.`)) return;
        try {
            await api.delete(`/hours/${h.id}`);
            toast.success("Hours entry deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not delete");
        }
    }
    const filtered = search.trim()
        ? items.filter((h) => (h.user_name || "").toLowerCase().includes(search.toLowerCase().trim()))
        : items;
    return (
        <div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
                {["pending", "approved", "rejected", "all"].map((s) => (
                    <button key={s} onClick={() => setFilter(s)} className={`rounded-full px-4 py-1.5 text-sm font-medium capitalize ${filter === s ? "bg-primary text-primary-foreground shadow-warm" : "bg-muted hover:bg-muted/70"}`} data-testid={`admin-hours-filter-${s}`}>{s}</button>
                ))}
                <div className="ml-auto flex items-center gap-2 min-w-[260px]">
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by member name…"
                        className="rounded-full h-9 text-sm"
                        data-testid="admin-hours-search"
                    />
                    {search && (
                        <button type="button" onClick={() => setSearch("")} className="text-xs text-muted-foreground hover:text-foreground whitespace-nowrap" data-testid="admin-hours-search-clear">Clear</button>
                    )}
                </div>
            </div>
            {search.trim() && (
                <div className="text-xs text-muted-foreground mb-2">
                    {filtered.length} of {items.length} entries match "{search.trim()}"
                </div>
            )}
            {filtered.length === 0 ? <div className="text-muted-foreground">{search.trim() ? "No matches." : "Empty."}</div> : (
                <div className="space-y-3">
                    {filtered.map((h) => (
                        <div key={h.id} className="bg-card border border-border rounded-2xl p-5 flex items-start gap-4" data-testid={`admin-hours-${h.id}`}>
                            <div className="w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center font-heading font-bold text-lg shrink-0">{h.hours}<span className="text-[10px] font-normal -mt-0.5">hrs</span></div>
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                    <div className="font-heading font-bold text-base">{h.user_name}</div>
                                    <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-accent/40">{h.event_type === "aop_related" ? "AOP-related" : h.event_type === "trendsetters_spirits" ? "Trendsetters Spirits" : "Other"}</span>
                                    <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${h.status === "approved" ? "bg-green-100 text-green-700" : h.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{h.status}</span>
                                </div>
                                <div className="text-xs text-muted-foreground">{h.date && formatCalendarDay(h.date, "EEE, MMM d, yyyy")}</div>
                                {h.agency_name && (
                                    <div className="mt-2 text-sm"><span className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Agency:</span> <span className="font-medium">{h.agency_name}</span></div>
                                )}
                                {(h.description || h.activity) && (
                                    <div className="mt-1.5 text-sm bg-muted/40 rounded-xl p-2.5">
                                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1">What did they do</div>
                                        <div className="leading-relaxed">{h.description || h.activity}</div>
                                    </div>
                                )}
                                {(h.host_name || h.host_email || h.host_phone) && (
                                    <div className="mt-1.5 text-xs grid sm:grid-cols-3 gap-1.5 bg-card border border-border rounded-xl p-2.5" data-testid={`admin-hours-host-${h.id}`}>
                                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold col-span-full mb-0.5">Verification contact</div>
                                        {h.host_name && <div><span className="font-bold">Host:</span> {h.host_name}</div>}
                                        {h.host_email && <div className="break-all"><span className="font-bold">Email:</span> <a className="text-primary hover:underline" href={`mailto:${h.host_email}`}>{h.host_email}</a></div>}
                                        {h.host_phone && <div><span className="font-bold">Phone:</span> <a className="text-primary hover:underline" href={`tel:${h.host_phone}`}>{h.host_phone}</a></div>}
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 shrink-0 items-end">
                                {h.status === "pending" && (
                                    <>
                                        <Button size="sm" onClick={() => review(h.id, "approved")} className="rounded-full bg-primary hover:bg-primary/90" data-testid={`admin-approve-${h.id}`}>Approve</Button>
                                        <Button size="sm" variant="outline" onClick={() => review(h.id, "rejected")} className="rounded-full" data-testid={`admin-reject-${h.id}`}>Reject</Button>
                                    </>
                                )}
                                <FullEditHoursDialog
                                    h={h}
                                    onSaved={load}
                                    trigger={
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            className="rounded-full"
                                            data-testid={`admin-hours-edit-${h.id}`}
                                            title="Edit every field on this entry"
                                        >
                                            <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                                        </Button>
                                    }
                                />
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="rounded-full text-destructive hover:bg-destructive/10"
                                    onClick={() => del(h)}
                                    data-testid={`admin-hours-delete-${h.id}`}
                                    title="Permanently delete this entry"
                                >
                                    <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* -------- Awards admin -------- */
function AwardsAdmin() {
    const [awards, setAwards] = useState([]);
    const [members, setMembers] = useState([]);
    const [savingOrder, setSavingOrder] = useState(false);
    const { user: me } = useAuth();
    const isFullAdmin = (me?.admin_role || "full") === "full";

    const load = async () => {
        const [a, m] = await Promise.all([api.get("/awards"), api.get("/members")]);
        // Iter 152: Medallions have their own admin surface (Medallion
        // Eligibility panel + Manual Grant on Awards.jsx). Filter them
        // out of the awards catalog so admins never re-order or re-edit
        // medallions from the general awards list.
        setAwards((a.data || []).filter((x) => !x.medallion_tier));
        setMembers(m.data);
    };
    useEffect(() => { load(); }, []);

    async function del(id) { if (!confirm("Delete this award?")) return; await api.delete(`/awards/${id}`); load(); }

    async function persistOrder(next) {
        setAwards(next);
        setSavingOrder(true);
        try {
            await api.put("/awards/reorder", { ordered_ids: next.map((a) => a.id) });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Reorder failed");
            load();
        }
        setSavingOrder(false);
    }

    function move(idx, dir) {
        const next = [...awards];
        const t = idx + dir;
        if (t < 0 || t >= next.length) return;
        [next[idx], next[t]] = [next[t], next[idx]];
        persistOrder(next);
    }

    async function resetToDefault() {
        if (!confirm("Reset the awards catalog to the default AOP order? Any custom ordering will be lost.")) return;
        // Send an empty list so the backend falls back to defaults; simpler: clear
        // sort_order on every award via reorder with an alphabetically-sorted
        // reset would be wrong. Instead persist the canonical order by name.
        const canonical = [
            "Life Membership Ribbon",
            "Founder's Lifetime Achievement Award",
            "Pauline Tate Dedication Ribbon",
            "Member's Ribbon",
            "Recruitment Ribbon",
            "Dr. Ken Thompson Distinguished Community Service Ribbon",
            "Master Instructor Ribbon",
            "Instructor Ribbon",
            "National Leadership Ribbon",
            "State Leadership Ribbon",
            "Community Service Ribbon",
            "Fundraiser Ribbon",
            "Joint Planning Ribbon",
            "Organization Planning Ribbon",
            "Service Ribbon",
            "Alpha Omega Phi Ribbon",
            "Federation Ribbon",
        ];
        const byName = new Map(awards.map((a) => [a.name.toLowerCase().replace(/’/g, "'").trim(), a]));
        const ordered = [];
        for (const n of canonical) {
            const key = n.toLowerCase().replace(/’/g, "'").trim();
            if (byName.has(key)) { ordered.push(byName.get(key)); byName.delete(key); }
        }
        // Append any custom awards (not in the canonical list) at the end.
        for (const a of byName.values()) ordered.push(a);
        await persistOrder(ordered);
        toast.success("Awards catalog reset to default AOP order");
    }

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div className="text-xs text-muted-foreground">
                    {isFullAdmin ? "Use ↑ / ↓ to change the order shown to members on the /awards catalog." : "Contact a Full Access admin to change the catalog order."}
                </div>
                <div className="flex items-center gap-2">
                    {isFullAdmin && (
                        <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={resetToDefault} data-testid="awards-reset-order">
                            Reset to AOP default order
                        </Button>
                    )}
                    <AwardDialog onSaved={load} trigger={<Button className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="new-award-btn"><Plus className="h-4 w-4 mr-1" />New award</Button>} />
                </div>
            </div>
            {savingOrder && <div className="text-xs text-muted-foreground mb-2">Saving order…</div>}
            <div className="grid md:grid-cols-2 gap-4">
                {awards.map((a, idx) => (
                    <div key={a.id} className="bg-card border border-border rounded-2xl p-5" data-testid={`admin-award-${a.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="font-heading font-semibold text-lg flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground font-normal shrink-0">#{idx + 1}</span>
                                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: a.color }} />
                                    <span className="truncate">{a.name}</span>
                                </div>
                                <div className="text-sm text-muted-foreground mt-1">{a.description}</div>
                                <div className="text-xs text-muted-foreground mt-2">Granted {a.granted_count}× · icon: {a.icon}</div>
                            </div>
                            <div className="flex flex-col gap-1 items-end shrink-0">
                                {isFullAdmin && (
                                    <div className="flex gap-1">
                                        <Button type="button" variant="ghost" size="icon" onClick={() => move(idx, -1)} disabled={idx === 0 || savingOrder} data-testid={`award-up-${a.id}`}>↑</Button>
                                        <Button type="button" variant="ghost" size="icon" onClick={() => move(idx, 1)} disabled={idx === awards.length - 1 || savingOrder} data-testid={`award-down-${a.id}`}>↓</Button>
                                    </div>
                                )}
                                <div className="flex gap-1">
                                    <GrantAwardDialog award={a} members={members} onSaved={load} />
                                    <AwardDialog award={a} onSaved={load} trigger={<Button variant="outline" size="sm" className="rounded-full">Edit</Button>} />
                                    <Button variant="ghost" size="icon" onClick={() => del(a.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {isFullAdmin && <AwardEligibilityPanel awards={awards} onGranted={load} />}
            {isFullAdmin && <MedallionEligibilityPanel awards={awards} onGranted={load} />}
            <AutoGrantsPanel />
        </div>
    );
}


/**
 * Admin-only Medallion eligibility panel. Surfaces members who satisfy every
 * criterion for a Bronze/Silver/Gold Medallion (based on years of consecutive
 * service, cumulative CS hours, events attended, and fundraised dollars). This
 * lives in the Admin Awards page — regular members never see suggestions.
 *
 * Renders one collapsible per tier. Each row shows the member + coloured pips
 * for each criterion (green ✓ / red ✗) so admins can eyeball at a glance
 * exactly which criteria a candidate satisfies. Clicking "Grant" hits the
 * same `/awards/{id}/grant` endpoint used by manual grants — so grants roll
 * into the standard Awards flow (profile display, PDFs, etc.).
 */
function MedallionEligibilityPanel({ awards, onGranted }) {
    const [eligibility, setEligibility] = useState({ bronze: [], silver: [], gold: [] });
    const [loading, setLoading] = useState(true);
    const [openTier, setOpenTier] = useState("bronze");
    const [granting, setGranting] = useState(false);

    const awardByTier = useMemo(() => {
        const m = {};
        for (const a of awards || []) if (a.medallion_tier) m[a.medallion_tier] = a;
        return m;
    }, [awards]);

    async function refresh() {
        setLoading(true);
        try {
            const { data } = await api.get("/awards/medallion-eligibility");
            setEligibility(data || { bronze: [], silver: [], gold: [] });
        } catch { /* ignore */ }
        setLoading(false);
    }
    useEffect(() => { refresh(); }, []);

    async function grantOne(uid, tier) {
        const a = awardByTier[tier];
        if (!a) return;
        if (!window.confirm(`Grant the ${a.name} to this member?`)) return;
        setGranting(true);
        try {
            await api.post(`/awards/${a.id}/grant`, { user_id: uid, note: `Auto-suggested (${tier} Medallion eligibility met).` });
            toast.success(`${a.name} granted`);
            refresh();
            onGranted && onGranted();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Grant failed");
        }
        setGranting(false);
    }

    const tiers = [
        { key: "bronze", label: "Bronze", color: "#CD7F32", criteriaCount: 4 },
        { key: "silver", label: "Silver", color: "#C0C0C0", criteriaCount: 5 },
        { key: "gold", label: "Gold", color: "#FFD700", criteriaCount: 5 },
    ];

    return (
        <div className="mt-10 border-t pt-8" data-testid="medallion-eligibility-panel">
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <h3 className="font-heading text-2xl font-bold tracking-tight">Medallion Club eligibility</h3>
                <Button type="button" variant="outline" className="rounded-full" onClick={refresh} data-testid="medallion-eligibility-refresh">
                    Refresh
                </Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
                Members who currently meet every criterion for each Medallion tier. Suggestions are
                admin-only &mdash; regular members do not see this list. Grants roll into the standard
                Awards flow (visible on profiles and PDFs). Admins can also grant medallions manually
                from Awards &rarr; Medallion Club regardless of eligibility.
            </p>

            {loading ? (
                <div className="text-sm text-muted-foreground">Computing eligibility…</div>
            ) : (
                <div className="space-y-4">
                    {tiers.map((t) => {
                        const rows = eligibility[t.key] || [];
                        const eligibleRows = rows.filter((r) => r.eligible && !r.already_granted);
                        const closeRows = rows.filter((r) => !r.eligible && r.criteria_met_count >= 3);
                        const open = openTier === t.key;
                        return (
                            <div key={t.key} className="border rounded-2xl bg-card overflow-hidden" data-testid={`medallion-eligibility-${t.key}`}>
                                <button
                                    type="button"
                                    onClick={() => setOpenTier(open ? "" : t.key)}
                                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/50 transition"
                                    data-testid={`medallion-eligibility-toggle-${t.key}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: t.color }} />
                                        <span className="font-semibold text-lg">{t.label} Medallion</span>
                                        <span className="text-xs bg-green-100 text-green-800 rounded-full px-2.5 py-0.5 font-semibold" data-testid={`medallion-eligibility-count-${t.key}`}>
                                            {eligibleRows.length} eligible
                                        </span>
                                        {closeRows.length > 0 && (
                                            <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-2.5 py-0.5">
                                                {closeRows.length} close (meets 3+)
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-slate-400 text-xl">{open ? "−" : "+"}</span>
                                </button>
                                {open && (
                                    <div className="border-t px-5 py-4 space-y-4">
                                        {eligibleRows.length === 0 ? (
                                            <p className="text-sm text-muted-foreground">No members currently meet every criterion for the {t.label} Medallion.</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {eligibleRows.map((c) => (
                                                    <MedallionCandidateRow key={c.user_id} candidate={c} tier={t} onGrant={() => grantOne(c.user_id, t.key)} granting={granting} />
                                                ))}
                                            </div>
                                        )}
                                        {closeRows.length > 0 && (
                                            <div>
                                                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mt-3 mb-2">Close to eligible ({closeRows.length}) — meets 3+ criteria</div>
                                                <div className="space-y-2 max-h-96 overflow-y-auto">
                                                    {closeRows.slice(0, 30).map((c) => (
                                                        <MedallionCandidateRow key={c.user_id} candidate={c} tier={t} readonly />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function MedallionCandidateRow({ candidate, tier, onGrant, granting, readonly }) {
    const c = candidate;
    const met = c.criteria_met || {};
    const pips = [
        { key: "years", label: `${c.years_of_service}y`, ok: met.years, title: "Consecutive years of service" },
        { key: "cs_hours", label: `${c.cs_hours}h`, ok: met.cs_hours, title: "Cumulative CS hours" },
        { key: "events", label: `${c.events_attended}ev`, ok: met.events, title: "National/State events attended" },
        { key: "fundraised", label: `$${(c.fundraised || 0).toLocaleString()}`, ok: met.fundraised, title: "Fundraised" },
    ];
    if (c.requires_prior_tier) {
        pips.push({ key: "prior", label: c.requires_prior_tier[0].toUpperCase() + c.requires_prior_tier.slice(1), ok: met.prior_tier, title: `Requires ${c.requires_prior_tier} medallion` });
    }
    return (
        <div className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white p-2" data-testid={`medallion-candidate-${tier.key}-${c.user_id}`}>
            <Avatar className="h-9 w-9 shrink-0"><AvatarImage src={mediaUrl(c.avatar_url)} /><AvatarFallback>{c.name?.[0] || "?"}</AvatarFallback></Avatar>
            <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{c.name}</div>
                {c.chapter_name && <div className="text-xs text-slate-500 truncate">{c.chapter_name}</div>}
            </div>
            <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                {pips.map((p) => (
                    <span key={p.key} title={p.title} className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${p.ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>{p.label}</span>
                ))}
            </div>
            {!readonly && (
                <Button size="sm" onClick={onGrant} disabled={granting} data-testid={`medallion-grant-${tier.key}-${c.user_id}`}>Grant</Button>
            )}
        </div>
    );
}

function AutoGrantsPanel() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [days, setDays] = useState(30);
    const [kindFilter, setKindFilter] = useState("all");

    async function load(nextDays = days) {
        try {
            const { data } = await api.get(`/admin/auto-grants?days=${nextDays}`);
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load auto-grants");
        }
    }
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    async function runNow() {
        setBusy(true);
        try {
            const { data: sweep } = await api.post("/admin/auto-grants/run-now");
            toast.success(
                `Sweep complete · Service Ribbon: ${sweep.service_ribbon_granted} · CS Ribbon: ${sweep.community_service_ribbon_granted}`
            );
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Sweep failed");
        }
        setBusy(false);
    }

    const items = data?.items || [];
    const filtered = kindFilter === "all"
        ? items
        : items.filter((g) => (g.auto_grant_kind || "").toLowerCase() === kindFilter);

    const byKind = items.reduce((acc, g) => {
        const k = g.auto_grant_kind || "other";
        acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});

    const KINDS = [
        { key: "all",                        label: "All",                          count: items.length },
        { key: "alpha_omega_phi_ribbon",     label: "AOP Ribbon (on join)",         count: byKind.alpha_omega_phi_ribbon || 0 },
        { key: "service_ribbon",             label: "Service Ribbon (anniversary)", count: byKind.service_ribbon || 0 },
        { key: "community_service_ribbon",   label: "Community Service (100h)",     count: byKind.community_service_ribbon || 0 },
    ];

    return (
        <div className="mt-10 bg-card border border-border rounded-2xl p-5" data-testid="auto-grants-panel">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                    <div className="font-heading text-xl font-bold">Recent automatic grants</div>
                    <div className="text-xs text-muted-foreground mt-1 max-w-xl">
                        Awards granted automatically by the system — Alpha Omega Phi Ribbon on join, Service Ribbon on the actual anniversary, and Community Service Ribbon on Dec 31 for members with 100+ hours.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={days}
                        onChange={(e) => { const d = Number(e.target.value); setDays(d); load(d); }}
                        className="h-9 rounded-full border border-border bg-background px-3 text-sm"
                        data-testid="auto-grants-days-filter"
                    >
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                        <option value={365}>Last year</option>
                    </select>
                    <Button onClick={runNow} disabled={busy} variant="outline" size="sm" className="rounded-full" data-testid="auto-grants-run-now-btn">
                        {busy ? "Running…" : "Run sweep now"}
                    </Button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
                {KINDS.map((k) => (
                    <button
                        key={k.key}
                        type="button"
                        onClick={() => setKindFilter(k.key)}
                        className={`text-xs rounded-full px-3 py-1.5 font-semibold transition-colors border ${kindFilter === k.key ? "bg-primary text-white border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/40"}`}
                        data-testid={`auto-grants-kind-${k.key}`}
                    >
                        {k.label} <span className={`ml-1.5 text-[10px] ${kindFilter === k.key ? "text-white/80" : "text-muted-foreground"}`}>{k.count}</span>
                    </button>
                ))}
            </div>

            {!data ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">
                    No automatic grants in the selected window. Automatic Alpha Omega Phi Ribbons will fire the moment new members join, and Service Ribbons will fire on each anniversary.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                                <th className="py-2 pr-3">Granted at</th>
                                <th className="py-2 pr-3">Member</th>
                                <th className="py-2 pr-3">Award</th>
                                <th className="py-2 pr-3">Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((g) => (
                                <tr key={g.id} className="border-b border-border/60" data-testid={`auto-grant-row-${g.id}`}>
                                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums">
                                        {g.granted_at ? formatCalendarDay(g.granted_at, "MMM d, yyyy") : "—"}
                                    </td>
                                    <td className="py-2 pr-3">{g.user_name || g.user_id}</td>
                                    <td className="py-2 pr-3">
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: g.award_color || "#F9D466" }} />
                                            {g.award_name || "—"}
                                            {g.ordinal > 1 && <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-1.5 py-0.5 bg-primary/10 text-primary">{g.ordinal}×</span>}
                                        </span>
                                    </td>
                                    <td className="py-2 pr-3 text-muted-foreground">{g.reason || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
function AwardEligibilityPanel({ awards, onGranted }) {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    async function refresh(y = year) {
        setLoading(true); setError("");
        try {
            const { data } = await api.get("/awards/eligibility", { params: { year: y } });
            setData(data);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Failed to compute eligibility");
        }
        setLoading(false);
    }
    useEffect(() => { refresh(year); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year]);

    async function grant(awardName, userId, reason) {
        // Match by exact name first (case-insensitive), then by prefix — this
        // prevents "Community Service Ribbon" from grabbing "Dr. Ken Thompson
        // Distinguished Community Service Ribbon" and prevents "Member's
        // Ribbon" from grabbing "Life Membership Ribbon".
        const wanted = awardName.toLowerCase().trim();
        let target = awards.find((a) => (a.name || "").toLowerCase().trim() === wanted);
        if (!target) {
            target = awards.find((a) => (a.name || "").toLowerCase().trim().startsWith(wanted));
        }
        if (!target) { toast.error(`No award named "${awardName}" — create one first.`); return; }
        try {
            await api.post(`/awards/${target.id}/grant`, { user_id: userId, reason });
            toast.success(`Granted "${target.name}"`);
            onGranted?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Grant failed"); }
    }

    const yearOptions = [];
    for (let y = now.getFullYear() + 1; y >= 2017; y--) yearOptions.push(y);

    return (
        <div className="mt-10 border-t pt-8" data-testid="awards-eligibility-panel">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                <div>
                    <h3 className="font-heading text-2xl font-bold tracking-tight">Awards eligibility</h3>
                    <p className="text-sm text-muted-foreground">Members who meet the criteria for each programme award in this calendar year (Jan 1 – Dec 31).</p>
                </div>
                <div className="flex items-center gap-3">
                    <Label className="text-xs uppercase tracking-wider">Year</Label>
                    <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                        <SelectTrigger className="rounded-full w-28" data-testid="eligibility-year-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" className="rounded-full" onClick={() => refresh(year)} data-testid="eligibility-refresh">
                        {loading ? "Loading…" : "Refresh"}
                    </Button>
                </div>
            </div>
            {error && <div className="text-sm text-destructive mb-3">{error}</div>}
            {!data && !loading && <div className="text-sm text-muted-foreground">No data.</div>}
            {data && (
                <div className="grid xl:grid-cols-2 gap-4">
                    <EligibilityCard
                        title="Service Ribbon"
                        subtitle="1st complete year, then every 5th year of continuous active membership. Eligible only once the anniversary date has been reached."
                        rows={data.service_ribbon}
                        empty="No members hit a service milestone this year."
                        columns={[
                            { key: "name", label: "Member" },
                            { key: "milestone", label: "Milestone" },
                            { key: "join_date", label: "Date joined", format: (v) => v ? formatCalendarDay(v, "MMM d, yyyy") : "—" },
                            { key: "anniversary_date", label: "Anniversary", format: (v) => v ? formatCalendarDay(v, "MMM d, yyyy") : "—" },
                            { key: "chapter_name", label: "Chapter" },
                        ]}
                        onGrant={(r) => grant("Service Ribbon", r.user_id, `${r.milestone} of active membership — anniversary ${r.anniversary_date}`)}
                        awardName="Service Ribbon"
                        testId="eligibility-service"
                    />
                    <EligibilityCard
                        title="Fundraiser Ribbon"
                        subtitle="Top donor of the calendar year."
                        rows={data.fundraiser_ribbon}
                        empty="No donations recorded this year."
                        columns={[
                            { key: "rank", label: "#" },
                            { key: "name", label: "Member" },
                            { key: "amount", label: "Raised", format: (v) => `$${Number(v).toLocaleString()}` },
                            { key: "chapter_name", label: "Chapter" },
                        ]}
                        onGrant={(r) => grant("Fundraiser Ribbon", r.user_id, `Top fundraiser $${Number(r.amount).toLocaleString()} (${data.year})`)}
                        awardName="Fundraiser Ribbon"
                        testId="eligibility-fundraiser"
                    />
                    <EligibilityCard
                        title="Community Service Ribbon"
                        subtitle="Members with 100+ approved volunteer hours."
                        rows={data.community_service_ribbon}
                        empty="No member hit 100 hours yet."
                        columns={[
                            { key: "name", label: "Member" },
                            { key: "hours", label: "Hours" },
                            { key: "chapter_name", label: "Chapter" },
                        ]}
                        onGrant={(r) => grant("Community Service Ribbon", r.user_id, `${r.hours}h volunteered (${data.year})`)}
                        awardName="Community Service Ribbon"
                        testId="eligibility-community"
                    />
                    <EligibilityCard
                        title="Dr. Ken Thompson Distinguished CS Award"
                        subtitle="Top per chapter — weighted 90% AOP + 5% Trendsetters + 5% Other."
                        rows={data.ken_thompson}
                        empty="No qualifying hours logged this year."
                        columns={[
                            { key: "chapter_name", label: "Chapter" },
                            { key: "name", label: "Member" },
                            { key: "weighted_score", label: "Score" },
                            { key: "aop_hours", label: "AOP" },
                        ]}
                        onGrant={(r) => grant("Dr. Ken Thompson Distinguished Community Service Ribbon", r.user_id, `Top chapter member (score ${r.weighted_score}) (${data.year})`)}
                        awardName="Dr. Ken Thompson Distinguished Community Service Ribbon"
                        testId="eligibility-ken-thompson"
                    />
                    <EligibilityCard
                        title="Recruitment Ribbon"
                        subtitle="Top recruiters of the calendar year."
                        rows={data.recruitment_ribbon}
                        empty="No recruits recorded this year."
                        columns={[
                            { key: "rank", label: "#" },
                            { key: "name", label: "Member" },
                            { key: "recruits", label: "Recruits" },
                            { key: "chapter_name", label: "Chapter" },
                        ]}
                        onGrant={(r) => grant("Recruitment Ribbon", r.user_id, `${r.recruits} recruits (${data.year})`)}
                        awardName="Recruitment Ribbon"
                        testId="eligibility-recruitment"
                    />
                    <EligibilityCard
                        title="Member's Ribbon"
                        subtitle="Top 5 in each of: most AOP-related hours, most overall hours, most recruits, most amount donated, most check-ins. All 5 categories shown per member; top-5 placements are marked."
                        rows={data.members_ribbon}
                        empty="No qualifying activity this year."
                        columns={[
                            { key: "name", label: "Member" },
                            {
                                key: "aop_hours",
                                label: "Categories",
                                format: (_v, r) => {
                                    const topIn = new Set(r.top_in || []);
                                    const rows = [
                                        { label: "AOP hours",   value: r.aop_hours ?? 0,       top: topIn.has("AOP hours") },
                                        { label: "Total hours", value: r.total_hours ?? 0,     top: topIn.has("Total hours") },
                                        { label: "Recruits",    value: r.recruits ?? 0,        top: topIn.has("Recruits") },
                                        { label: "Donations",   value: `$${(r.donated_amount ?? 0).toFixed(2)}`, top: topIn.has("Donations") },
                                        { label: "Check-ins",   value: r.checkins ?? 0,        top: topIn.has("Check-ins") },
                                    ];
                                    return (
                                        <div className="flex flex-col gap-1 min-w-[180px]" data-testid="members-ribbon-categories">
                                            {rows.map((row) => (
                                                <div
                                                    key={row.label}
                                                    className={`flex items-center justify-between gap-3 text-xs rounded-md px-2 py-1 ${row.top ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground"}`}
                                                    data-testid={`members-ribbon-cat-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                >
                                                    <span className="flex items-center gap-1.5">
                                                        {row.top && <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />}
                                                        {row.label}
                                                    </span>
                                                    <span className="tabular-nums">{row.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                },
                            },
                            { key: "chapter_name", label: "Chapter" },
                        ]}
                        onGrant={(r) => grant("Member's Ribbon", r.user_id, `Top-5 recognition: ${(r.top_in || []).join(", ") || "activity across categories"} (${data.year})`)}
                        awardName="Member's Ribbon"
                        testId="eligibility-members"
                    />
                    <div className="xl:col-span-2 bg-card border border-border rounded-2xl p-5" data-testid="eligibility-chapter">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="font-heading font-semibold text-lg">Chapter of the Year</div>
                                <div className="text-xs text-muted-foreground">Score = (recruits + hours + donation $ + checkins) ÷ (members before recruits).</div>
                            </div>
                        </div>
                        {data.chapter_of_the_year?.length ? (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                                            <th className="py-2">Chapter</th>
                                            <th>Score</th>
                                            <th>Recruits</th>
                                            <th>Hours</th>
                                            <th>Donations</th>
                                            <th>Check-ins</th>
                                            <th>Base members</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.chapter_of_the_year.map((c, i) => (
                                            <tr key={c.chapter_id} className={`border-b border-border/60 ${i === 0 ? "bg-primary/5 font-semibold" : ""}`}>
                                                <td className="py-2">{c.chapter_name}</td>
                                                <td>{c.score}</td>
                                                <td>{c.recruits}</td>
                                                <td>{c.hours}</td>
                                                <td>${Number(c.donation_amount || 0).toLocaleString()}</td>
                                                <td>{c.checkins}</td>
                                                <td>{c.base_members}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : <div className="text-sm text-muted-foreground">No chapter activity yet this year.</div>}
                    </div>
                </div>
            )}
        </div>
    );
}

function EligibilityCard({ title, subtitle, rows, empty, columns, onGrant, awardName, testId }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-5" data-testid={testId}>
            <div className="mb-3">
                <div className="font-heading font-semibold text-lg">{title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
            </div>
            {!rows?.length ? (
                <div className="text-sm text-muted-foreground italic">{empty}</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                                {columns.map((c) => <th key={c.key} className="py-2 pr-2">{c.label}</th>)}
                                <th className="py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={`${r.user_id || r.chapter_id}-${i}`} className="border-b border-border/60">
                                    {columns.map((c) => (
                                        <td key={c.key} className="py-2 pr-2 align-top">
                                            {c.format ? c.format(r[c.key], r) : (r[c.key] ?? "—")}
                                        </td>
                                    ))}
                                    <td className="py-2 text-right">
                                        {r.user_id && (
                                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => onGrant(r)} data-testid={`${testId}-grant-${r.user_id}`}>
                                                Grant
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {rows.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-2">
                            Grants apply the &quot;{awardName}&quot; award — make sure it exists above.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}



function AwardDialog({ award, onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: award?.name || "",
        description: award?.description || "",
        icon: award?.icon || "trophy",
        color: award?.color || "#F9D466",
    });
    async function save() {
        try {
            if (award) await api.put(`/awards/${award.id}`, form);
            else await api.post("/awards", form);
            toast.success("Saved");
            setOpen(false);
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{award ? "Edit award" : "New award"}</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                    <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5" data-testid="award-name-input" /></div>
                    <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Icon</Label>
                            <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="award-icon-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="trophy">Trophy</SelectItem>
                                    <SelectItem value="medal">Medal</SelectItem>
                                    <SelectItem value="star">Star</SelectItem>
                                    <SelectItem value="heart">Heart</SelectItem>
                                    <SelectItem value="graduation-cap">Graduation</SelectItem>
                                    <SelectItem value="sparkles">Sparkles</SelectItem>
                                    <SelectItem value="award">Award</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div><Label>Color</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="rounded-xl mt-1.5 h-10" /></div>
                    </div>
                </div>
                <DialogFooter><Button onClick={save} className="rounded-full bg-primary hover:bg-primary/90" data-testid="award-save-btn">Save</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function GrantAwardDialog({ award, members, onSaved }) {
    const [open, setOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [memberQuery, setMemberQuery] = useState("");
    const [reason, setReason] = useState("");
    const [grantedAt, setGrantedAt] = useState("");
    const [busy, setBusy] = useState(false);

    function toggle(id) {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }

    async function grant() {
        if (selectedIds.length === 0) { toast.error("Pick at least one member"); return; }
        setBusy(true);
        try {
            // Same gotcha as Hours: an HTML date input gives "YYYY-MM-DD" with no time,
            // and `new Date(s)` parses it as UTC midnight — which displays as the prior
            // day in any western-hemisphere timezone. Append "T00:00:00" so it's parsed
            // as local midnight; matches the edit dialog's behavior.
            const granted_at = grantedAt ? new Date(`${grantedAt}T00:00:00`).toISOString() : undefined;
            if (selectedIds.length === 1) {
                const payload = { user_id: selectedIds[0], reason };
                if (granted_at) payload.granted_at = granted_at;
                await api.post(`/awards/${award.id}/grant`, payload);
                toast.success(`${award.name} granted`);
            } else {
                const payload = { user_ids: selectedIds, reason };
                if (granted_at) payload.granted_at = granted_at;
                const { data } = await api.post(`/awards/${award.id}/grant-bulk`, payload);
                toast.success(`${award.name} granted to ${data.created} member${data.created === 1 ? "" : "s"}`);
            }
            setOpen(false);
            setSelectedIds([]); setReason(""); setGrantedAt(""); setMemberQuery("");
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
        setBusy(false);
    }

    const filtered = memberQuery.trim()
        ? members.filter((m) => {
            const q = memberQuery.trim().toLowerCase();
            return (m.name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q);
        })
        : members;

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" className="rounded-full bg-primary/10 text-primary hover:bg-primary/20" data-testid={`grant-btn-${award.id}`}>Grant</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">Grant "{award.name}"</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                    <div>
                        <Label>Members * <span className="text-xs font-normal text-muted-foreground">({selectedIds.length} selected)</span></Label>
                        <Input
                            placeholder="Search members…"
                            value={memberQuery}
                            onChange={(e) => setMemberQuery(e.target.value)}
                            className="rounded-xl mt-1.5"
                            data-testid="grant-member-search"
                        />
                        {selectedIds.length > 0 && (
                            <div className="mt-2 flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">{selectedIds.length} member{selectedIds.length === 1 ? "" : "s"} selected</span>
                                <button type="button" onClick={() => setSelectedIds([])} className="text-primary font-semibold hover:underline" data-testid="grant-clear-selected">Clear</button>
                            </div>
                        )}
                        <div className="mt-1.5 max-h-48 overflow-y-auto rounded-xl border border-border bg-card divide-y divide-border" data-testid="grant-member-list">
                            {filtered.length === 0 ? (
                                <div className="px-3 py-4 text-xs text-muted-foreground italic">No members match.</div>
                            ) : filtered.map((m) => {
                                const checked = selectedIds.includes(m.id);
                                return (
                                    <label key={m.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-muted/40 ${checked ? "bg-primary/5" : ""}`} data-testid={`grant-member-row-${m.id}`}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggle(m.id)}
                                            className="h-4 w-4 accent-primary shrink-0"
                                            data-testid={`grant-member-checkbox-${m.id}`}
                                        />
                                        <span className="font-medium truncate">{m.name}</span>
                                        {m.email && <span className="text-xs text-muted-foreground truncate">· {m.email}</span>}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <Label>Date granted <span className="text-xs text-muted-foreground font-normal">(defaults to today)</span></Label>
                        <Input type="date" value={grantedAt} onChange={(e) => setGrantedAt(e.target.value)} className="rounded-xl mt-1.5" data-testid="grant-date-input" />
                    </div>
                    <div><Label>Reason (optional)</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-xl mt-1.5" data-testid="grant-reason-input" /></div>
                </div>
                <DialogFooter>
                    <Button onClick={grant} disabled={busy || selectedIds.length === 0} className="rounded-full bg-primary hover:bg-primary/90" data-testid="grant-confirm-btn">
                        {busy ? "Granting…" : (selectedIds.length > 1 ? `Grant to ${selectedIds.length} members` : "Grant award")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* -------- Member Card (clickable view) -------- */
function StatusPill({ status }) {
    const map = {
        active: "bg-green-500/15 text-green-700",
        inactive: "bg-slate-500/15 text-slate-600",
        grace: "bg-amber-500/20 text-amber-700",
        expired: "bg-destructive/15 text-destructive",
        deceased: "bg-black/10 text-black",
    };
    return (
        <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${map[status] || "bg-muted"}`}>
            {status === "deceased" ? "Omega ✦" : status}
        </span>
    );
}

function MemberCardDialog({ member, chapters, tiers, trigger }) {
    const { user: currentUser } = useAuth();
    const isAdmin = currentUser?.role === "admin";
    const [open, setOpen] = useState(false);
    const [details, setDetails] = useState(member);
    const [grants, setGrants] = useState([]);
    const [otyWins, setOtyWins] = useState([]);
    const [editingGrant, setEditingGrant] = useState(null);
    const loadGrants = () => api.get(`/members/${member.id}/awards`).then(({ data }) => setGrants(data || [])).catch(() => setGrants([]));
    const loadOtyWins = () => api.get(`/of-the-year`, { params: { user_id: member.id } })
        .then(({ data }) => setOtyWins(data || []))
        .catch(() => setOtyWins([]));
    useEffect(() => {
        if (open) {
            api.get(`/members/${member.id}`).then(({ data }) => setDetails(data)).catch(() => {});
            loadGrants();
            loadOtyWins();
        }
    }, [open, member.id]);
    const chapter = chapters?.find((c) => c.id === details.chapter_id);
    const tier = tiers?.find((t) => t.id === details.tier_id);

    async function removeGrant(g) {
        if (!confirm(`Revoke "${g.award_name || 'this award'}" from ${details.name}? This cannot be undone.`)) return;
        try {
            await api.delete(`/awards/grants/${g.id}`);
            toast.success("Award revoked");
            loadGrants();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not revoke"); }
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" data-testid={`view-member-${member.id}`}>View</Button>}
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">Member card</DialogTitle></DialogHeader>
                <div className="flex items-center gap-4 mt-1">
                    {details.avatar_url ? (
                        <img src={details.avatar_url} alt={details.name} className="w-20 h-20 rounded-full object-contain border-2 border-white shadow-warm" data-testid={`member-card-${member.id}-avatar`} />
                    ) : (
                        <div className="w-20 h-20 rounded-full bg-primary/15 text-primary grid place-items-center font-heading font-black text-2xl">
                            {(details.name || details.email)[0]?.toUpperCase()}
                        </div>
                    )}
                    <div className="flex-1">
                        <div className="font-heading text-xl font-bold">{details.name}</div>
                        {details.line_name && <div className="text-xs font-bold uppercase tracking-widest text-primary">"{details.line_name}"</div>}
                        <div className="mt-1 flex flex-wrap gap-2 items-center">
                            <StatusPill status={details.status} />
                            {details.role === "admin" && <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-primary/15 text-primary">Admin</span>}
                            {tier && <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-accent/40">{tier.name}</span>}
                        </div>
                    </div>
                </div>
                <div className="mt-4 space-y-2 text-sm" data-testid={`member-card-${member.id}-details`}>
                    <DetailRow label="Email" value={details.email} />
                    {details.username && <DetailRow label="Username" value={`@${details.username}`} />}
                    <DetailRow label="Phone" value={details.phone} />
                    <DetailRow label="Address" value={[details.address, details.city, details.state, details.zip_code].filter(Boolean).join(", ")} />
                    <DetailRow label="Country" value={details.country} />
                    <DetailRow label="Birthdate" value={details.birthdate ? formatCalendarDay(details.birthdate.length === 10 ? `${details.birthdate}T00:00:00` : details.birthdate, "MMM d, yyyy") : ""} />
                    <DetailRow label="Branch of service" value={details.branch_of_service} />
                    <DetailRow label="Intake line" value={details.intake_line} />
                    <DetailRow label="Intake completed" value={details.intake_completed_at} />
                    <DetailRow label="Chapter" value={chapter?.name} />
                    <DetailRow label="Member type" value={tier?.name} />
                    <DetailRow label="Date joined" value={details.join_date ? formatCalendarDay(details.join_date, "MMM d, yyyy") : (details.created_at ? format(parseISO(details.created_at), "MMM d, yyyy") : "")} />
                    {details.is_lifetime_member ? (
                        <div className="flex justify-between gap-3 py-1.5 border-b last:border-0 border-border/40">
                            <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Membership</span>
                            <span className="text-sm font-bold text-primary">Lifetime · no renewal</span>
                        </div>
                    ) : (
                        <DetailRow label="Membership expires" value={details.membership_expires_at ? formatCalendarDay(details.membership_expires_at, "MMM d, yyyy") : ""} />
                    )}
                    {details.bio && (
                        <div className="pt-2 border-t">
                            <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">Bio</div>
                            <p className="text-sm leading-relaxed">{details.bio}</p>
                        </div>
                    )}
                    <div className="pt-3 border-t">
                        <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                            Ribbons & Achievements <span className="text-foreground/80">({grants.length})</span>
                        </div>
                        {grants.length === 0 ? (
                            <div className="text-xs text-muted-foreground italic">No ribbons earned yet.</div>
                        ) : (
                            <div className="space-y-2" data-testid={`member-card-${member.id}-ribbons`}>
                                {grants.map((g) => (
                                    <div key={g.id} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2" data-testid={`ribbon-${g.id}`}>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: g.award_color || "#F9D466" }}></span>
                                            <div className="min-w-0">
                                                <div className="font-semibold text-sm truncate flex items-center gap-1.5 flex-wrap">
                                                    <span className="truncate">{g.award_name || g.name || "Ribbon"}</span>
                                                    {g.ordinal > 1 && <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-1.5 py-0.5 bg-primary/10 text-primary">{g.ordinal}×</span>}
                                                    {g.auto_granted && (
                                                        <span
                                                            title="Automatically granted by the system"
                                                            className="text-[9px] uppercase tracking-wider font-bold rounded-full px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200"
                                                            data-testid={`ribbon-auto-badge-${g.id}`}
                                                        >
                                                            AUTO
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-muted-foreground truncate">
                                                    {g.granted_at ? formatCalendarDay(g.granted_at, "MMM d, yyyy") : ""}{g.reason ? ` · ${g.reason}` : ""}
                                                </div>
                                            </div>
                                        </div>
                                        {isAdmin && (
                                            <div className="flex items-center gap-1 shrink-0">
                                                <button onClick={() => setEditingGrant(g)} className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-1 border border-primary text-primary hover:bg-primary hover:text-white transition-colors" data-testid={`ribbon-edit-${g.id}`}>
                                                    Edit
                                                </button>
                                                <button onClick={() => removeGrant(g)} className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-1 border border-destructive text-destructive hover:bg-destructive hover:text-white transition-colors" data-testid={`ribbon-remove-${g.id}`}>
                                                    Remove
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Of The Year Honors — every win the member has earned */}
                    <div className="pt-3 border-t">
                        <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                            Of The Year Honors <span className="text-foreground/80">({otyWins.length})</span>
                        </div>
                        {otyWins.length === 0 ? (
                            <div className="text-xs text-muted-foreground italic" data-testid={`member-card-${member.id}-oty-empty`}>No &ldquo;Of The Year&rdquo; honors yet.</div>
                        ) : (
                            <div className="space-y-1.5" data-testid={`member-card-${member.id}-oty`}>
                                {otyWins.map((o) => (
                                    <div key={o.id} className="flex items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-1.5" data-testid={`oty-win-${o.id}`}>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-semibold truncate">
                                                <span className="text-primary font-heading mr-1.5">{o.year}</span>
                                                {o.category_label || o.category}
                                            </div>
                                            {(o.chapter_name || o.note) && (
                                                <div className="text-[11px] text-muted-foreground truncate">
                                                    {[o.chapter_name, o.note].filter(Boolean).join(" · ")}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <GrantEditDialog grant={editingGrant} onClose={() => setEditingGrant(null)} onSaved={() => { setEditingGrant(null); loadGrants(); }} />
                    <div className="pt-2 text-[10px] text-muted-foreground italic">
                        Secure data (passwords, payment methods, transactions) is intentionally hidden.
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function DetailRow({ label, value }) {
    if (!value) return null;
    return (
        <div className="flex items-start gap-3">
            <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground w-32 shrink-0 pt-0.5">{label}</div>
            <div className="text-sm flex-1 break-words">{value}</div>
        </div>
    );
}

function GrantEditDialog({ grant, onClose, onSaved }) {
    const [reason, setReason] = useState("");
    const [grantedAt, setGrantedAt] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (grant) {
            setReason(grant.reason || "");
            setGrantedAt(grant.granted_at ? grant.granted_at.slice(0, 10) : "");
        }
    }, [grant]);

    if (!grant) return null;
    async function save() {
        setBusy(true);
        try {
            await api.put(`/awards/grants/${grant.id}`, { reason, granted_at: grantedAt });
            toast.success("Award updated");
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update"); }
        setBusy(false);
    }
    return (
        <Dialog open={!!grant} onOpenChange={(v) => { if (!v) onClose?.(); }}>
            <DialogContent className="max-w-md" data-testid="grant-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Edit award grant</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    <div className="rounded-xl bg-muted/40 p-3 text-sm" data-testid="grant-edit-award-name">
                        <span className="font-semibold">{grant.award_name}</span>
                        {grant.ordinal > 1 && <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-1.5 py-0.5 bg-primary/10 text-primary ml-2">{grant.ordinal}×</span>}
                    </div>
                    <div>
                        <Label>Date granted</Label>
                        <Input type="date" value={grantedAt} onChange={(e) => setGrantedAt(e.target.value)} className="rounded-xl mt-1.5" data-testid="grant-edit-date" />
                    </div>
                    <div>
                        <Label>Reason</Label>
                        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-xl mt-1.5" data-testid="grant-edit-reason" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
                    <Button onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90" data-testid="grant-edit-save">
                        {busy ? "Saving…" : "Save changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { MemberCardDialog, StatusPill };

/* -------- Gear Admin -------- */
function GearAdmin() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/gear").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);
    async function del(id) {
        if (!confirm("Remove this gear item?")) return;
        await api.delete(`/gear/${id}`);
        toast.success("Removed");
        load();
    }
    return (
        <div>
            <div className="flex justify-end mb-4">
                <GearDialog onSaved={load} trigger={<Button className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="new-gear-btn"><Plus className="h-4 w-4 mr-1" />New item</Button>} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((g) => (
                    <div key={g.id} className="bg-card border border-border rounded-2xl overflow-hidden" data-testid={`admin-gear-${g.id}`}>
                        <div className="aspect-video bg-muted overflow-hidden">
                            {g.cover_image && <img src={g.cover_image} alt={g.name} className="w-full h-full object-contain" />}
                        </div>
                        <div className="p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="font-heading font-semibold leading-tight">{g.name}</div>
                                <div className="font-bold shrink-0">${g.price?.toFixed(0)}</div>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">{g.category} · {g.sku || "—"} · {g.in_stock ? "In stock" : "Sold out"}</div>
                            <div className="mt-3 flex gap-1">
                                <GearDialog item={g} onSaved={load} trigger={<Button variant="outline" size="sm" className="rounded-full text-xs">Edit</Button>} />
                                <Button variant="ghost" size="icon" onClick={() => del(g.id)} data-testid={`delete-gear-${g.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="col-span-full text-muted-foreground text-center py-8">No gear yet.</div>}
            </div>
        </div>
    );
}

function GearDialog({ item, onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyGear());
    useEffect(() => {
        if (open) {
            setForm(item ? {
                ...item,
                price: item.price ?? 0,
                sizes: (item.sizes || []).join(", "),
                colors: (item.colors || []).join(", "),
            } : emptyGear());
        }
    }, [open, item]);

    async function save() {
        try {
            const payload = {
                ...form,
                price: Number(form.price) || 0,
                sizes: typeof form.sizes === "string" ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean) : (form.sizes || []),
                colors: typeof form.colors === "string" ? form.colors.split(",").map((s) => s.trim()).filter(Boolean) : (form.colors || []),
            };
            if (item) await api.put(`/gear/${item.id}`, payload);
            else await api.post("/gear", payload);
            toast.success("Saved");
            setOpen(false);
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{item ? "Edit gear" : "New gear item"}</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                    <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5" data-testid="gear-name-input" /></div>
                    {/* Iter 38: External-link mode + Rich HTML title also available in the
                        Admin → Gear tab editor (mirrors the public Gear.jsx editor). */}
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="gear-admin-external-block">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={!!form.is_external_link}
                                onChange={(e) => setForm({ ...form, is_external_link: e.target.checked })}
                                data-testid="gear-admin-is-external"
                            />
                            <span className="text-sm font-semibold">External link mode</span>
                        </label>
                        {form.is_external_link && (
                            <Input
                                value={form.external_url || ""}
                                onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                                placeholder="https://example.com/our-store"
                                className="rounded-xl mt-2"
                                data-testid="gear-admin-external-url"
                            />
                        )}
                    </div>
                    <div>
                        <Label>HTML / Rich title <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                        <Textarea
                            rows={2}
                            value={form.name_html || ""}
                            onChange={(e) => setForm({ ...form, name_html: e.target.value })}
                            className="rounded-xl mt-1.5 font-mono text-xs"
                            placeholder='<strong style="color:#C8102E">Visit our Store</strong>'
                            data-testid="gear-admin-name-html"
                        />
                    </div>
                    <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    <div className="grid grid-cols-3 gap-3">
                        <div><Label>Price ($)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-xl mt-1.5" /></div>
                        <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="rounded-xl mt-1.5" /></div>
                        <div>
                            <Label>Category</Label>
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger className="rounded-xl mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="apparel">Apparel</SelectItem>
                                    <SelectItem value="accessory">Accessory</SelectItem>
                                    <SelectItem value="memorabilia">Memorabilia</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    {/* Iter 38: cover image now upload-only. */}
                    <div>
                        <Label>Cover image</Label>
                        <div className="flex items-center gap-3 mt-1.5">
                            {form.cover_image ? (
                                <img src={form.cover_image} alt="" className="h-16 w-24 object-contain rounded-lg border border-border" />
                            ) : (
                                <div className="h-16 w-24 rounded-lg border-2 border-dashed border-border grid place-items-center text-[10px] text-muted-foreground">
                                    No image
                                </div>
                            )}
                            <label className="rounded-full border px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50 inline-flex items-center gap-1.5" data-testid="gear-admin-cover-upload">
                                {form.cover_image ? "Replace" : "Upload"}
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={async (e) => {
                                        const f = e.target.files?.[0];
                                        if (!f) return;
                                        const fd = new FormData();
                                        fd.append("file", f);
                                        try {
                                            const { data } = await api.post("/email/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                            setForm({ ...form, cover_image: data.url });
                                            toast.success("Image uploaded");
                                        } catch (err) {
                                            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
                                        }
                                        e.target.value = "";
                                    }}
                                />
                            </label>
                            {form.cover_image && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    type="button"
                                    onClick={() => setForm({ ...form, cover_image: "" })}
                                    className="text-xs text-destructive"
                                    data-testid="gear-admin-cover-remove"
                                >
                                    Remove
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Sizes (comma)</Label><Input value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} className="rounded-xl mt-1.5" placeholder="S, M, L, XL" /></div>
                        <div><Label>Colors (comma)</Label><Input value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} className="rounded-xl mt-1.5" placeholder="Navy, Red, White" /></div>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} /> In stock
                    </label>
                </div>
                <DialogFooter><Button onClick={save} className="rounded-full bg-primary hover:bg-primary/90" data-testid="gear-save-btn">Save</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
function emptyGear() {
    return { name: "", name_html: "", description: "", price: 0, sku: "", category: "apparel", cover_image: "", sizes: "", colors: "", in_stock: true, is_external_link: false, external_url: "" };
}

/* -------- Causes Admin -------- */
/* ---------- Instructors & Intake Lines admin tab ---------- */
// A "period" bound is a partial date: "" (unknown), "YYYY", "YYYY-MM",
// or "YYYY-MM-DD". This helper renders any of those variants back into
// a human-friendly string for the table.
function formatPartialDate(s) {
    if (!s) return "—";
    const parts = String(s).split("-");
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) {
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const mIdx = parseInt(parts[1], 10) - 1;
        return `${monthNames[mIdx] ?? parts[1]} ${parts[0]}`;
    }
    try {
        return format(parseISO(s), "MMM d, yyyy");
    } catch {
        return s;
    }
}

// Split "YYYY[-MM[-DD]]" into individual fields for the edit form.
function splitPartialDate(s) {
    const parts = String(s || "").split("-");
    return { year: parts[0] || "", month: parts[1] || "", day: parts[2] || "" };
}

// Merge {year, month, day} back into a canonical partial-date string
// dropping trailing empty components. Returns "" when year is blank.
function joinPartialDate({ year, month, day }) {
    const y = (year || "").trim();
    if (!y) return "";
    const m = (month || "").trim();
    if (!m) return y;
    const mm = m.padStart(2, "0");
    const d = (day || "").trim();
    if (!d) return `${y}-${mm}`;
    return `${y}-${mm}-${d.padStart(2, "0")}`;
}

function PartialDateInput({ value, onChange, testidPrefix }) {
    const { year, month, day } = useMemo(() => splitPartialDate(value), [value]);
    function update(next) {
        onChange(joinPartialDate(next));
    }
    return (
        <div className="grid grid-cols-3 gap-2">
            <Input
                placeholder="Year"
                value={year}
                onChange={(e) => update({ year: e.target.value.replace(/\D/g, "").slice(0, 4), month, day })}
                className="rounded-xl"
                data-testid={`${testidPrefix}-year`}
                inputMode="numeric"
            />
            <Input
                placeholder="Month (opt)"
                value={month}
                onChange={(e) => update({ year, month: e.target.value.replace(/\D/g, "").slice(0, 2), day })}
                className="rounded-xl"
                data-testid={`${testidPrefix}-month`}
                inputMode="numeric"
                disabled={!year}
            />
            <Input
                placeholder="Day (opt)"
                value={day}
                onChange={(e) => update({ year, month, day: e.target.value.replace(/\D/g, "").slice(0, 2) })}
                className="rounded-xl"
                data-testid={`${testidPrefix}-day`}
                inputMode="numeric"
                disabled={!year || !month}
            />
        </div>
    );
}

function emptyInstructorForm() {
    return { user_id: null, line_name: "", from_date: "", end_date: "", notes: "" };
}

function InstructorDialog({ instructor, members = [], onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyInstructorForm());
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (!open) return;
        if (instructor) {
            setForm({
                user_id: instructor.user_id || null,
                line_name: instructor.line_name || "",
                from_date: instructor.from_date || "",
                end_date: instructor.end_date || "",
                notes: instructor.notes || "",
            });
        } else {
            setForm(emptyInstructorForm());
        }
    }, [open, instructor]);

    const memberOptions = useMemo(
        () => members.map((m) => ({ id: m.id, name: m.name, email: m.email })),
        [members],
    );

    async function save() {
        if (!form.user_id) { toast.error("Pick a member first."); return; }
        if (!form.line_name.trim()) { toast.error("Line name is required."); return; }
        setBusy(true);
        try {
            const payload = {
                user_id: form.user_id,
                line_name: form.line_name.trim(),
                from_date: form.from_date || null,
                end_date: form.end_date || null,
                notes: form.notes.trim(),
            };
            if (instructor) {
                await api.put(`/admin/instructors/${instructor.id}`, payload);
            } else {
                await api.post("/admin/instructors", payload);
            }
            toast.success("Saved");
            setOpen(false);
            onSaved && onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="instructor-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">{instructor ? "Edit instructor" : "Add instructor"}</DialogTitle>
                    <DialogDescription>
                        Track who led each intake line. Fill in what you know — the month and day are optional if only the year is on record.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-3">
                    <div>
                        <Label>Instructor (member)</Label>
                        <div className="mt-1.5">
                            <MemberPicker
                                options={memberOptions}
                                value={form.user_id}
                                onChange={(m) => setForm({ ...form, user_id: m?.id || null })}
                                placeholder="Search by name or email…"
                                testid="instructor-member"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Line name</Label>
                        <Input
                            value={form.line_name}
                            onChange={(e) => setForm({ ...form, line_name: e.target.value })}
                            placeholder="e.g. Alpha Line, Fall 2020"
                            className="rounded-xl mt-1.5"
                            data-testid="instructor-line-name"
                        />
                    </div>
                    <div>
                        <Label>From (year required, month/day optional)</Label>
                        <div className="mt-1.5">
                            <PartialDateInput
                                value={form.from_date}
                                onChange={(v) => setForm({ ...form, from_date: v })}
                                testidPrefix="instructor-from"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>To (year required, month/day optional)</Label>
                        <div className="mt-1.5">
                            <PartialDateInput
                                value={form.end_date}
                                onChange={(v) => setForm({ ...form, end_date: v })}
                                testidPrefix="instructor-end"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Notes (optional)</Label>
                        <Textarea
                            rows={2}
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Any context worth preserving."
                            className="rounded-xl mt-1.5"
                            data-testid="instructor-notes"
                        />
                    </div>
                </div>
                <DialogFooter className="mt-4">
                    <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                    <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={save} disabled={busy} data-testid="instructor-save-btn">
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function InstructorsAdmin() {
    const [rows, setRows] = useState([]);
    const [members, setMembers] = useState([]);
    const [query, setQuery] = useState("");
    const load = () => api.get("/admin/instructors").then(({ data }) => setRows(data || [])).catch(() => setRows([]));
    useEffect(() => {
        load();
        api.get("/members").then(({ data }) => setMembers(data || [])).catch(() => setMembers([]));
    }, []);

    async function remove(id) {
        if (!confirm("Delete this instructor record?")) return;
        try {
            await api.delete(`/admin/instructors/${id}`);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
    }

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            (r.user_name || "").toLowerCase().includes(q) ||
            (r.line_name || "").toLowerCase().includes(q) ||
            (r.user_email || "").toLowerCase().includes(q),
        );
    }, [rows, query]);

    const { rows: sortedRows, sort, setSort } = useSortableTable(
        filtered,
        {
            user_name: (r) => (r.user_name || "").toLowerCase(),
            line_name: (r) => (r.line_name || "").toLowerCase(),
            from_date: (r) => r.from_date || "",
            end_date: (r) => r.end_date || "",
            notes: (r) => (r.notes || "").toLowerCase(),
        },
    );

    return (
        <div>
            <div className="flex justify-between items-center gap-3 flex-wrap mb-4">
                <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by instructor or line name…"
                    className="rounded-full max-w-sm"
                    data-testid="instructors-search"
                />
                <InstructorDialog
                    members={members}
                    onSaved={load}
                    trigger={
                        <Button className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="new-instructor-btn">
                            <Plus className="h-4 w-4 mr-1" />Add instructor
                        </Button>
                    }
                />
            </div>

            {filtered.length === 0 ? (
                <div className="text-muted-foreground text-center py-16 border border-dashed border-border rounded-2xl" data-testid="instructors-empty">
                    {rows.length === 0 ? "No instructors on record yet. Add the first one to start the archive." : "No matches for that search."}
                </div>
            ) : (
                <div className="overflow-x-auto bg-card border border-border rounded-2xl">
                    <table className="w-full text-sm" data-testid="instructors-table">
                        <thead>
                            <tr className="text-muted-foreground border-b border-border">
                                <SortableTh column="user_name" sort={sort} onSort={setSort}>Instructor</SortableTh>
                                <SortableTh column="line_name" sort={sort} onSort={setSort}>Line</SortableTh>
                                <SortableTh column="from_date" sort={sort} onSort={setSort}>From</SortableTh>
                                <SortableTh column="end_date" sort={sort} onSort={setSort}>To</SortableTh>
                                <SortableTh column="notes" sort={sort} onSort={setSort}>Notes</SortableTh>
                                <SortableTh align="right">Actions</SortableTh>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((r) => (
                                <tr key={r.id} className="border-b border-border/60 hover:bg-muted/30" data-testid={`instructor-row-${r.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold">{r.user_name || "—"}</div>
                                        {r.user_email && <div className="text-xs text-muted-foreground">{r.user_email}</div>}
                                    </td>
                                    <td className="px-4 py-3 font-medium">{r.line_name}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">{formatPartialDate(r.from_date)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">{formatPartialDate(r.end_date)}</td>
                                    <td className="px-4 py-3 text-muted-foreground max-w-xs truncate" title={r.notes || ""}>{r.notes || "—"}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex gap-1">
                                            <InstructorDialog
                                                instructor={r}
                                                members={members}
                                                onSaved={load}
                                                trigger={
                                                    <Button variant="outline" size="sm" className="rounded-full" data-testid={`edit-instructor-${r.id}`}>
                                                        <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                                                    </Button>
                                                }
                                            />
                                            <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`delete-instructor-${r.id}`}>
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


function AwardDonationDialog({ causes = [], onAwarded }) {
    const [open, setOpen] = useState(false);
    const [members, setMembers] = useState([]);
    const [memberId, setMemberId] = useState(null);
    const [amount, setAmount] = useState("");
    const [causeId, setCauseId] = useState("__unallocated__");
    const [causeLabel, setCauseLabel] = useState("");
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [note, setNote] = useState("");
    const [anonymous, setAnonymous] = useState(false);
    const [method, setMethod] = useState("manual");
    const [busy, setBusy] = useState(false);

    // Preload the membership list the moment the dialog opens so the
    // MemberPicker's search feels instant.
    useEffect(() => {
        if (!open) return;
        api.get("/members").then(({ data }) => setMembers(data || [])).catch(() => setMembers([]));
    }, [open]);

    function reset() {
        setMemberId(null);
        setAmount("");
        setCauseId("__unallocated__");
        setCauseLabel("");
        setDate(new Date().toISOString().slice(0, 10));
        setNote("");
        setAnonymous(false);
        setMethod("manual");
    }

    const memberOptions = useMemo(
        () => members.map((m) => ({ id: m.id, name: m.name, email: m.email })),
        [members],
    );

    async function award() {
        if (!memberId) { toast.error("Pick a member first."); return; }
        const amt = Number(amount);
        if (!Number.isFinite(amt) || amt <= 0) { toast.error("Amount must be a positive number."); return; }
        setBusy(true);
        try {
            const payload = {
                user_id: memberId,
                amount: amt,
                cause_id: causeId === "__unallocated__" ? null : causeId,
                cause_label: causeId === "__unallocated__" ? causeLabel.trim() : "",
                date: date || null,
                note: note.trim(),
                anonymous,
                method: method.trim() || "manual",
            };
            await api.post("/donations/admin/manual", payload);
            toast.success(`$${amt.toFixed(2)} donation awarded`);
            reset();
            setOpen(false);
            onAwarded && onAwarded();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to award donation");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="award-donation-btn">
                    <HandCoins className="h-4 w-4 mr-1" />Award donation
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="award-donation-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Award a donation</DialogTitle>
                    <DialogDescription>
                        Record a donation directly on a member's behalf (cash, check, in-kind, etc.).
                        The donation appears on their profile, the cause total, and the leaderboards.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-3">
                    <div>
                        <Label>Member</Label>
                        <div className="mt-1.5">
                            <MemberPicker
                                options={memberOptions}
                                value={memberId}
                                onChange={(m) => setMemberId(m?.id || null)}
                                placeholder="Search by name or email…"
                                testid="award-donation-member"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Amount ($)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="100.00"
                                className="rounded-xl mt-1.5"
                                data-testid="award-donation-amount"
                            />
                        </div>
                        <div>
                            <Label>Date</Label>
                            <Input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="rounded-xl mt-1.5"
                                data-testid="award-donation-date"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Cause</Label>
                        <Select value={causeId} onValueChange={setCauseId}>
                            <SelectTrigger className="rounded-xl mt-1.5" data-testid="award-donation-cause"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__unallocated__">Unallocated (general fund)</SelectItem>
                                {causes.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {causeId === "__unallocated__" && (
                        <div>
                            <Label>Fund label (optional)</Label>
                            <Input
                                value={causeLabel}
                                onChange={(e) => setCauseLabel(e.target.value)}
                                placeholder="e.g. General fund, In memoriam, Scholarship pool…"
                                className="rounded-xl mt-1.5"
                                data-testid="award-donation-cause-label"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Preserved on the receipt and admin report even though no cause is linked.</p>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Payment method</Label>
                            <Input
                                value={method}
                                onChange={(e) => setMethod(e.target.value)}
                                placeholder="manual, cash, check, in-kind…"
                                className="rounded-xl mt-1.5"
                                data-testid="award-donation-method"
                            />
                        </div>
                        <div className="flex items-end pb-1.5">
                            <label className="inline-flex items-center gap-2 text-sm select-none cursor-pointer" data-testid="award-donation-anon-wrap">
                                <input
                                    type="checkbox"
                                    checked={anonymous}
                                    onChange={(e) => setAnonymous(e.target.checked)}
                                    className="h-4 w-4"
                                    data-testid="award-donation-anon"
                                />
                                Record as anonymous
                            </label>
                        </div>
                    </div>
                    <div>
                        <Label>Note (optional)</Label>
                        <Textarea
                            rows={2}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Check #, in-kind description, or any context for the receipt."
                            className="rounded-xl mt-1.5"
                            data-testid="award-donation-note"
                        />
                    </div>
                </div>
                <DialogFooter className="mt-4">
                    <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                    <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={award} disabled={busy || !memberId || !amount} data-testid="award-donation-submit">
                        {busy ? "Saving…" : "Award donation"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CausesAdmin() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/causes").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);
    async function del(id) {
        if (!confirm("Delete this cause?")) return;
        await api.delete(`/causes/${id}`);
        toast.success("Deleted");
        load();
    }
    return (
        <div>
            <div className="flex justify-end gap-2 mb-4 flex-wrap">
                <AwardDonationDialog causes={items} onAwarded={load} />
                <DonationsCsvImportDialog onImported={load} />
                <CauseDialog onSaved={load} trigger={<Button className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="new-cause-btn"><Plus className="h-4 w-4 mr-1" />New cause</Button>} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                {items.map((c) => {
                    const pct = c.goal_amount > 0 ? Math.min(100, Math.round((c.raised_amount / c.goal_amount) * 100)) : 0;
                    return (
                        <div key={c.id} className="bg-card border border-border rounded-2xl overflow-hidden" data-testid={`admin-cause-${c.id}`}>
                            {c.cover_image && <div className="aspect-[3/1] bg-muted overflow-hidden"><img src={c.cover_image} alt={c.title} className="w-full h-full object-contain" /></div>}
                            <div className="p-5">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="font-heading font-semibold text-lg">{c.title}</div>
                                        <div className="text-xs text-muted-foreground mt-1">${c.raised_amount.toFixed(0)} / ${c.goal_amount.toFixed(0)} ({pct}%) · {c.donor_count} donors · {c.is_active ? "active" : "paused"}</div>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <CauseDialog cause={c} onSaved={load} trigger={<Button variant="outline" size="sm" className="rounded-full">Edit</Button>} />
                                        <Button variant="ghost" size="icon" onClick={() => del(c.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                    </div>
                                </div>
                                <div className="mt-3 w-full bg-muted rounded-full h-2 overflow-hidden">
                                    <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                                </div>
                            </div>
                        </div>
                    );
                })}
                {items.length === 0 && <div className="col-span-full text-muted-foreground text-center py-8">No causes yet.</div>}
            </div>
        </div>
    );
}

function CauseDialog({ cause, onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyCause());
    useEffect(() => {
        if (open) setForm(cause ? { ...cause, goal_amount: cause.goal_amount ?? 0 } : emptyCause());
    }, [open, cause]);

    async function save() {
        try {
            const payload = { ...form, goal_amount: Number(form.goal_amount) || 0 };
            if (cause) await api.put(`/causes/${cause.id}`, payload);
            else await api.post("/causes", payload);
            toast.success("Saved");
            setOpen(false);
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{cause ? "Edit cause" : "New cause"}</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                    <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-1.5" data-testid="cause-title-input" /></div>
                    <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Goal ($)</Label><Input type="number" step="100" value={form.goal_amount} onChange={(e) => setForm({ ...form, goal_amount: e.target.value })} className="rounded-xl mt-1.5" /></div>
                        <div>
                            <Label>Category</Label>
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger className="rounded-xl mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="general">General</SelectItem>
                                    <SelectItem value="veteran">Veteran</SelectItem>
                                    <SelectItem value="education">Education</SelectItem>
                                    <SelectItem value="anniversary">Anniversary</SelectItem>
                                    <SelectItem value="emergency">Emergency</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label>Cover photo</Label>
                        <label className="mt-1.5 rounded-xl border-2 border-dashed border-slate-300 px-4 py-3 text-sm font-semibold cursor-pointer hover:bg-white hover:border-primary transition-colors flex items-center gap-2 justify-center" data-testid="cause-image-upload-btn">
                            <Upload className="h-4 w-4" />{form.cover_image ? "Replace cover photo" : "Upload cover photo"}
                            <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                const f = e.target.files?.[0]; if (!f) return;
                                if (f.size > 10 * 1024 * 1024) { toast.error("Image must be under 10 MB"); return; }
                                const fd = new FormData(); fd.append("file", f);
                                try {
                                    const { data } = await api.post("/causes/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                    setForm((p) => ({ ...p, cover_image: data.url }));
                                    toast.success("Cover photo uploaded");
                                } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
                            }} />
                        </label>
                        {form.cover_image && (
                            <img src={mediaUrl(form.cover_image)} alt="Cause preview" className="mt-2 w-full max-h-32 object-contain rounded-xl border border-border bg-slate-50" />
                        )}
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
                    </label>

                    {/* Payment routing — PayPal in-app checkout has been retired.
                        Every cause now collects via Zeffy. Existing PayPal-configured
                        causes get migrated to Zeffy the next time their admin saves. */}
                    <div className="border-t pt-3">
                        <Label>Zeffy donation URL</Label>
                        <Input
                            value={form.zeffy_url || ""}
                            onChange={(e) => setForm({ ...form, zeffy_url: e.target.value, payment_processor: "zeffy" })}
                            placeholder="https://www.zeffy.com/en-US/donation-form/your-form"
                            className="rounded-xl mt-1.5"
                            data-testid="cause-zeffy-url-input"
                        />
                        <div className="text-[11px] text-slate-500 mt-1">
                            Members will be sent to this URL to complete their gift. Without it, members will see a
                            "contact the chapter to donate" message instead of a payment button. Track receipts manually
                            via the bulk CSV importer.
                        </div>
                    </div>
                </div>
                <DialogFooter><Button onClick={save} className="rounded-full bg-primary hover:bg-primary/90" data-testid="cause-save-btn">Save</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
function emptyCause() {
    return { title: "", description: "", goal_amount: 0, cover_image: "", category: "general", is_active: true, payment_processor: "zeffy", zeffy_url: "" };
}

/* -------- Email Blast Admin (Brevo) -------- */
function EmailBlastAdmin() {
    const [view, setView] = useState("compose");
    return (
        <Tabs value={view} onValueChange={setView}>
            <div className="-mx-2 px-2 sm:mx-0 sm:px-0 overflow-x-auto sm:overflow-x-visible scrollbar-hide max-w-full">
                <TabsList className="rounded-full bg-muted p-1 inline-flex sm:flex sm:flex-wrap sm:h-auto whitespace-nowrap w-max sm:w-full">
                    <TabsTrigger value="compose" className="rounded-full" data-testid="email-tab-compose"><Send className="h-4 w-4 mr-1.5" />Compose</TabsTrigger>
                    <TabsTrigger value="templates" className="rounded-full" data-testid="email-tab-templates"><FileText className="h-4 w-4 mr-1.5" />Templates</TabsTrigger>
                    <TabsTrigger value="signatures" className="rounded-full" data-testid="email-tab-signatures"><PenSquare className="h-4 w-4 mr-1.5" />Signatures</TabsTrigger>
                    <TabsTrigger value="history" className="rounded-full" data-testid="email-tab-history"><Clock className="h-4 w-4 mr-1.5" />History</TabsTrigger>
                    <TabsTrigger value="test-send" className="rounded-full" data-testid="email-tab-test-send"><Send className="h-4 w-4 mr-1.5" />Test send</TabsTrigger>
                    <TabsTrigger value="automated" className="rounded-full" data-testid="email-tab-automated"><Clock className="h-4 w-4 mr-1.5" />Automated</TabsTrigger>
                    <TabsTrigger value="deliverability" className="rounded-full" data-testid="email-tab-deliverability"><Mail className="h-4 w-4 mr-1.5" />Deliverability</TabsTrigger>
                    <TabsTrigger value="push" className="rounded-full" data-testid="email-tab-push"><Bell className="h-4 w-4 mr-1.5" />Push</TabsTrigger>
                </TabsList>
            </div>
            <TabsContent value="compose" className="mt-6"><ComposeBlast /></TabsContent>
            <TabsContent value="templates" className="mt-6"><EmailTemplates /></TabsContent>
            <TabsContent value="signatures" className="mt-6"><EmailSignatures /></TabsContent>
            <TabsContent value="history" className="mt-6"><BlastHistory /></TabsContent>
            <TabsContent value="test-send" className="mt-6"><EmailTestSend /></TabsContent>
            <TabsContent value="automated" className="mt-6"><AutomatedEmailsAdmin /></TabsContent>
            <TabsContent value="deliverability" className="mt-6"><EmailDeliverability /></TabsContent>
            <TabsContent value="push" className="mt-6"><PushAdmin /></TabsContent>
        </Tabs>
    );
}

function PushAdmin() {
    /* Iter 140 — OneSignal admin composer.
     * Reuses the "email" admin permission so anyone who can send blasts
     * can also broadcast push notifications. Not a channel-replacement for
     * email — sits alongside it.
     * Iter 142 — audience "Individual member(s)" now takes email addresses
     * (comma / newline separated) which the backend resolves to user_ids. */
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [url, setUrl] = useState("");
    const [iconUrl, setIconUrl] = useState("");
    const [segment, setSegment] = useState("active");
    const [chapterId, setChapterId] = useState("");
    const [tierId, setTierId] = useState("");
    const [customEmails, setCustomEmails] = useState("");
    const [testOnly, setTestOnly] = useState(false);
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState([]);
    const [config, setConfig] = useState({ enabled: false });
    const [chapters, setChapters] = useState([]);
    const [tiers, setTiers] = useState([]);

    async function loadAll() {
        try {
            const [c, h, ch, ti] = await Promise.all([
                api.get("/push/config"),
                api.get("/admin/push/history"),
                api.get("/chapters"),
                api.get("/tiers"),
            ]);
            setConfig(c.data);
            setHistory(h.data || []);
            setChapters(ch.data || []);
            setTiers(ti.data || []);
        } catch { /* ignore */ }
    }

    useEffect(() => { loadAll(); }, []);

    async function send() {
        if (!title.trim() || !body.trim()) {
            toast.error("Title and body are required");
            return;
        }
        setBusy(true);
        try {
            const parsedEmails = customEmails
                .split(/[\s,;\n]+/)
                .map((s) => s.trim())
                .filter((s) => s.includes("@"));
            if (segment === "custom" && parsedEmails.length === 0) {
                toast.error("Enter at least one member email address.");
                setBusy(false);
                return;
            }
            const payload = {
                title: title.trim(),
                body: body.trim(),
                url: url.trim() || null,
                icon_url: iconUrl.trim() || null,
                segment,
                chapter_id: segment === "chapter" ? chapterId : null,
                tier_id: segment === "tier" ? tierId : null,
                custom_emails: segment === "custom" ? parsedEmails : [],
                test_only: testOnly,
            };
            const { data } = await api.post("/admin/push/send", payload);
            const unmatched = data.unmatched_emails || [];
            if (unmatched.length > 0) {
                toast.warning(`Push sent, but ${unmatched.length} email${unmatched.length === 1 ? "" : "s"} didn't match a member: ${unmatched.join(", ")}`);
            } else {
                toast.success(
                    testOnly
                        ? "Test push sent to you — check your device."
                        : `Push sent to ${data.recipients ?? "your subscribers"}.`
                );
            }
            await loadAll();
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.error === "no_matching_recipients") {
                toast.error(`None of those emails match a member: ${(detail.unmatched_emails || []).join(", ")}`);
            } else {
                toast.error(formatApiError(detail) || "Push failed to send");
            }
        }
        setBusy(false);
    }

    if (!config.enabled) {
        return (
            <div className="rounded-2xl border border-dashed p-8 bg-muted/30" data-testid="push-admin-not-configured">
                <div className="flex items-start gap-3">
                    <Bell className="h-6 w-6 text-muted-foreground mt-0.5" />
                    <div>
                        <div className="font-semibold text-lg">Push notifications not configured</div>
                        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                            Set <code className="bg-muted px-1 rounded">ONESIGNAL_APP_ID</code> and <code className="bg-muted px-1 rounded">ONESIGNAL_REST_API_KEY</code> in your backend <code>.env</code> and redeploy to enable push notifications.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="push-admin">
            <div className="rounded-2xl border p-6 bg-card">
                <div className="flex items-center gap-2 mb-4">
                    <Bell className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">Send a push notification</h3>
                </div>
                <div className="grid gap-4">
                    <div>
                        <Label>Title *</Label>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. New event this weekend!" maxLength={80} data-testid="push-title" />
                        <div className="text-xs text-muted-foreground mt-1">{title.length}/80 · keep it snappy — this is what appears on the lock screen.</div>
                    </div>
                    <div>
                        <Label>Body *</Label>
                        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={180} placeholder="A short blurb that expands the title" data-testid="push-body" />
                        <div className="text-xs text-muted-foreground mt-1">{body.length}/180</div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Deep link URL</Label>
                            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://aop-app.org/news/…" data-testid="push-url" />
                            <div className="text-xs text-muted-foreground mt-1">Where the notification takes the user when tapped.</div>
                        </div>
                        <div>
                            <Label>Icon / thumbnail URL</Label>
                            <Input value={iconUrl} onChange={(e) => setIconUrl(e.target.value)} placeholder="https://…jpg (square, 192px+)" data-testid="push-icon" />
                        </div>
                    </div>
                    <div>
                        <Label>Audience</Label>
                        <Select value={segment} onValueChange={setSegment}>
                            <SelectTrigger data-testid="push-segment"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Everyone who's opted in (broadcast)</SelectItem>
                                <SelectItem value="active">All active members</SelectItem>
                                <SelectItem value="admins">Admins only</SelectItem>
                                <SelectItem value="chapter">By chapter</SelectItem>
                                <SelectItem value="tier">By tier</SelectItem>
                                <SelectItem value="custom">Specific member(s) by email</SelectItem>
                            </SelectContent>
                        </Select>
                        {segment === "chapter" && (
                            <Select value={chapterId} onValueChange={setChapterId}>
                                <SelectTrigger className="mt-2" data-testid="push-chapter"><SelectValue placeholder="Pick a chapter" /></SelectTrigger>
                                <SelectContent>
                                    {chapters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        )}
                        {segment === "tier" && (
                            <Select value={tierId} onValueChange={setTierId}>
                                <SelectTrigger className="mt-2" data-testid="push-tier"><SelectValue placeholder="Pick a tier" /></SelectTrigger>
                                <SelectContent>
                                    {tiers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        )}
                        {segment === "custom" && (
                            <div className="mt-2">
                                <Textarea
                                    value={customEmails}
                                    onChange={(e) => setCustomEmails(e.target.value)}
                                    placeholder="member1@example.com, member2@example.com&#10;or one per line"
                                    rows={3}
                                    data-testid="push-emails"
                                />
                                <div className="text-xs text-muted-foreground mt-1">
                                    Enter one or more member email addresses (comma, space, semicolon, or newline separated). We'll resolve each to the right member and warn you if any don't match.
                                </div>
                            </div>
                        )}
                    </div>
                    <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer" data-testid="push-test-only-label">
                        <input type="checkbox" checked={testOnly} onChange={(e) => setTestOnly(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="push-test-only" />
                        Test send (only me — great for previewing before broadcasting)
                    </label>
                    <div className="flex justify-end">
                        <Button onClick={send} disabled={busy} className="rounded-full" data-testid="push-send-btn">
                            {busy ? "Sending…" : testOnly ? "Send test to me" : "Send push"}
                        </Button>
                    </div>
                </div>
            </div>

            <div className="rounded-2xl border p-6 bg-card">
                <h3 className="text-lg font-semibold mb-4">Recent pushes</h3>
                {history.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No pushes sent yet.</div>
                ) : (
                    <div className="space-y-3">
                        {history.slice(0, 20).map((h) => (
                            <div key={h.id} className="border-b last:border-b-0 pb-3 last:pb-0" data-testid={`push-history-${h.id}`}>
                                <div className="flex items-baseline justify-between gap-3">
                                    <div className="font-semibold">{h.title}</div>
                                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                                        {h.onesignal_recipients ?? 0} devices · {new Date(h.sent_at).toLocaleString()}
                                    </div>
                                </div>
                                <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{h.body}</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    {h.kind || h.sent_by_name || "manual"} · segment: {h.segment}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function EmailDeliverability() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [dnsCheck, setDnsCheck] = useState(null);
    const [dnsBusy, setDnsBusy] = useState(false);

    useEffect(() => {
        api.get("/email/deliverability")
            .then(({ data }) => setData(data))
            .catch((err) => setError(formatApiError(err.response?.data?.detail) || "Could not load deliverability info"));
    }, []);

    async function runDnsCheck() {
        setDnsBusy(true);
        setDnsCheck(null);
        try {
            const { data: res } = await api.get("/email/deliverability/check-dns");
            setDnsCheck(res);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "DNS check failed");
        }
        setDnsBusy(false);
    }

    if (error) return <div className="bg-card border border-border rounded-2xl p-6 text-red-700" data-testid="deliverability-error">{error}</div>;
    if (!data) return <div className="text-muted-foreground">Loading…</div>;

    const sandboxWarn = data.is_provider_sandbox ?? data.is_resend_sandbox;
    const notConfigured = !(data.provider_configured ?? data.resend_configured);
    const providerLabel = data.provider_label || "Brevo";

    return (
        <div className="space-y-5 max-w-3xl" data-testid="deliverability-panel">
            <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="font-heading text-xl font-bold mb-1">Email deliverability</h3>
                <p className="text-sm text-muted-foreground">
                    Stop emails from landing in members' junk folders. This app sends through <strong>{providerLabel}</strong>. Verify your DNS records match what {providerLabel} expects — that's what stops Gmail / Yahoo / Outlook from routing you to spam.
                </p>
            </div>

            {(sandboxWarn || notConfigured) && (
                <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-4 text-sm" data-testid="deliverability-warning">
                    <div className="font-bold text-red-800 mb-1">⚠ Action required</div>
                    {notConfigured && <div>{providerLabel} API key is not configured on the server. Emails won't send at all.</div>}
                    {sandboxWarn && <div>You're sending from <code>{data.from}</code> — that's the {providerLabel} sandbox address and <strong>guarantees</strong> spam folder placement. Switch the FROM environment variable to your verified domain (<code>info@aop-app.org</code> or similar).</div>}
                </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3" data-testid="deliverability-stats">
                <div className="rounded-2xl border border-border bg-card p-4">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Provider</div>
                    <div className="font-mono text-sm mt-1">{providerLabel}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mt-3">Sender</div>
                    <div className="font-mono text-sm mt-1 break-all">{data.from}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mt-3">Reply-To</div>
                    <div className="font-mono text-sm mt-1 break-all">{data.reply_to}</div>
                </div>
                <div className="rounded-2xl border border-border bg-card p-4">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Sending domain</div>
                    <div className="font-mono text-sm mt-1">{data.sending_domain || "—"}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mt-3">Opted out</div>
                    <div className="font-heading text-2xl font-black mt-1" data-testid="deliverability-optout-count">{data.opt_out_count} <span className="text-sm font-normal text-muted-foreground">/ {data.total_with_email} members</span></div>
                </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
                    <h4 className="font-heading text-lg font-bold">DNS records to verify at your registrar</h4>
                    <Button
                        onClick={runDnsCheck}
                        disabled={dnsBusy}
                        variant="outline"
                        className="rounded-full"
                        data-testid="deliverability-dns-check-btn"
                    >
                        {dnsBusy ? "Checking…" : "Run live DNS check"}
                    </Button>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                    Log in to your domain registrar (the place you bought <code>{data.sending_domain || "your domain"}</code>)
                    and confirm these records exist. Values are for <strong>{providerLabel}</strong>. Remove any conflicting records from previous email providers to avoid authentication failures.
                </p>
                <div className="space-y-3">
                    {data.dns_checklist.map((row, i) => (
                        <div key={i} className="rounded-xl border border-border bg-muted/30 p-3" data-testid={`dns-row-${i}`}>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="inline-block text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-primary/10 text-primary">{row.record}</span>
                                <span className="text-xs text-muted-foreground">Host: <code className="bg-card px-1 py-0.5 rounded">{row.host}</code></span>
                            </div>
                            <div className="text-xs leading-relaxed font-mono break-all bg-card border border-border rounded p-2">{row.value}</div>
                            {row.note && <div className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{row.note}</div>}
                        </div>
                    ))}
                </div>

                {dnsCheck && (
                    <div className="mt-5 rounded-xl border-2 border-primary/40 bg-primary/5 p-4" data-testid="deliverability-dns-result">
                        <div className="flex items-center gap-2 mb-3">
                            <span className={`inline-block text-xs font-bold rounded-full px-3 py-1 ${dnsCheck.all_pass ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                                {dnsCheck.all_pass ? "✓ All records pass" : "⚠ Some records need fixing"}
                            </span>
                            <span className="text-xs text-muted-foreground">Live lookup for <code>{dnsCheck.domain}</code></span>
                        </div>
                        {dnsCheck.note && <div className="text-xs text-muted-foreground mb-3">{dnsCheck.note}</div>}
                        <div className="space-y-2">
                            {(dnsCheck.records || []).map((rec, i) => (
                                <div key={i} className="text-xs bg-card border border-border rounded-lg p-3" data-testid={`dns-check-${i}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`inline-block w-2 h-2 rounded-full ${rec.ok === true ? "bg-emerald-500" : rec.ok === false ? "bg-red-500" : "bg-slate-400"}`}></span>
                                        <span className="font-bold">{rec.name}</span>
                                        {rec.ok === true && <span className="text-emerald-700">pass</span>}
                                        {rec.ok === false && <span className="text-red-700">fail</span>}
                                    </div>
                                    {rec.value && <div className="font-mono text-[11px] break-all mt-1 bg-muted/40 rounded p-1.5">{rec.value}</div>}
                                    {rec.expected && <div className="text-[11px] text-muted-foreground mt-1">Expected: {rec.expected}</div>}
                                    {rec.details && (
                                        <div className="mt-2 space-y-1">
                                            {rec.details.map((d, j) => (
                                                <div key={j} className="text-[11px]">
                                                    <span className={d.ok ? "text-emerald-700" : "text-red-700"}>{d.ok ? "✓" : "✗"}</span> <code>{d.host}</code> <span className="text-muted-foreground">({d.type})</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-4 text-xs text-muted-foreground leading-relaxed">
                    After updating DNS, wait 15-30 minutes for propagation, then re-run the check above. You can also send yourself a test from the <strong>Test send</strong> tab to&nbsp;
                    <a href="https://www.mail-tester.com/" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">mail-tester.com</a>
                    {" "}and aim for 9/10 or higher.
                </div>
            </div>

            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-xs text-emerald-900 leading-relaxed" data-testid="deliverability-app-side">
                <div className="font-bold text-emerald-800 mb-1.5 text-sm">What this app already does to stay out of junk:</div>
                <ul className="list-disc pl-5 space-y-1">
                    <li>Multipart messages (HTML + plain-text fallback)</li>
                    <li><code>List-Unsubscribe</code> + <code>List-Unsubscribe-Post: List-Unsubscribe=One-Click</code> headers on bulk mail</li>
                    <li>Visible unsubscribe link + your organization's mailing address in every blast footer (CAN-SPAM compliance)</li>
                    <li><code>Reply-To</code> set to your real inbox (replies don't bounce)</li>
                    <li><code>Precedence: bulk</code> on marketing mail so inbox providers route it correctly</li>
                    <li>Opted-out members are automatically excluded from new blasts and dues reminders</li>
                </ul>
            </div>
        </div>
    );
}

function EmailTestSend() {
    const [toEmail, setToEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [templateId, setTemplateId] = useState("");
    const [templates, setTemplates] = useState([]);
    const [busy, setBusy] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    useEffect(() => {
        api.get("/email/templates").then(({ data }) => setTemplates(data || [])).catch(() => setTemplates([]));
    }, []);

    const selectedTemplate = templates.find((t) => t.id === templateId);

    async function send() {
        if (!toEmail.trim() || !toEmail.includes("@")) {
            toast.error("Enter a valid email address.");
            return;
        }
        setBusy(true);
        setLastResult(null);
        try {
            const payload = { to_email: toEmail.trim() };
            if (subject.trim()) payload.subject = subject.trim();
            if (templateId) payload.template_id = templateId;
            const { data } = await api.post("/email/test-send", payload);
            setLastResult(data);
            if (data.ok) {
                toast.success(`Test email accepted by Brevo (to ${data.to}).`);
            } else {
                toast.error(`Brevo rejected the test: ${data.detail || "unknown error"}`, { duration: 10000 });
            }
        } catch (e) {
            const detail = formatApiError(e.response?.data?.detail) || "Failed to send test email";
            setLastResult({ ok: false, detail });
            toast.error(detail);
        }
        setBusy(false);
    }

    return (
        <div className="max-w-xl space-y-5" data-testid="email-test-send-panel">
            <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="font-heading text-xl font-bold mb-1">Send a test email</h3>
                <p className="text-sm text-muted-foreground">Validate that Brevo is configured and your verified sending domain is reaching inboxes — without having to approve a real applicant or queue a blast.</p>
            </div>
            <div>
                <Label>Recipient email *</Label>
                <Input
                    type="email"
                    value={toEmail}
                    onChange={(e) => setToEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="rounded-xl mt-1.5"
                    data-testid="test-email-to-input"
                />
                <p className="text-xs text-muted-foreground mt-1.5">Tip: Send to a separate inbox (Gmail, Yahoo, Outlook) to confirm external deliverability.</p>
            </div>
            <div>
                <Label>Use a saved template <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                <Select value={templateId || "__none__"} onValueChange={(v) => setTemplateId(v === "__none__" ? "" : v)}>
                    <SelectTrigger className="rounded-xl mt-1.5" data-testid="test-email-template-select">
                        <SelectValue placeholder="No template — send the deliverability check" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__none__">No template — send the deliverability check</SelectItem>
                        {templates.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {selectedTemplate && (
                    <p className="text-xs text-muted-foreground mt-1.5">
                        Template subject: <em>{selectedTemplate.subject || "(none)"}</em>. Variables like <code>{"{{first_name}}"}</code> will render with <strong>your</strong> profile values so the preview is realistic.
                    </p>
                )}
            </div>
            <div>
                <Label>Subject override <span className="text-xs text-muted-foreground font-normal">(optional — leave blank to use the template's subject)</span></Label>
                <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder={selectedTemplate?.subject || "Alpha Omega Phi — Test email"}
                    className="rounded-xl mt-1.5"
                    data-testid="test-email-subject-input"
                />
            </div>
            <Button onClick={send} disabled={busy || !toEmail.trim()} className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="test-email-send-btn">
                <Send className="h-4 w-4 mr-1.5" />{busy ? "Sending…" : "Send test email"}
            </Button>
            {lastResult && (
                <div
                    className={`rounded-2xl border p-4 text-sm ${lastResult.ok ? "border-green-300 bg-green-50 text-green-900" : "border-red-300 bg-red-50 text-red-900"}`}
                    data-testid="test-email-result"
                >
                    <div className="font-semibold mb-1">{lastResult.ok ? "Brevo accepted the test." : "Brevo rejected the test."}</div>
                    <div className="text-xs leading-relaxed">{lastResult.detail}</div>
                    {lastResult.template_name && <div className="text-xs mt-2 opacity-80">Template: <code>{lastResult.template_name}</code></div>}
                    {lastResult.subject && <div className="text-xs opacity-80">Subject: <code>{lastResult.subject}</code></div>}
                    {lastResult.from && <div className="text-xs opacity-80">From: <code>{lastResult.from}</code></div>}
                    {lastResult.to && <div className="text-xs opacity-80">To: <code>{lastResult.to}</code></div>}
                    {lastResult.message_id && <div className="text-xs opacity-80">Message id: <code>{lastResult.message_id}</code></div>}
                </div>
            )}
        </div>
    );
}

function ComposeBlast() {
    const { user: currentAdmin } = useAuth();
    const isMembershipManager = (currentAdmin?.admin_role || "").toLowerCase() === "membership_manager";
    const [subject, setSubject] = useState("");
    const [body_html, setBody] = useState("<p>Hello {{first_name}},</p><p>Write your message here. Use the toolbar — bold, lists, images, links — no code needed.</p><p>— Alpha Omega Phi</p>");
    const [background_color, setBackgroundColor] = useState("");
    const [segment, setSegment] = useState("active");
    const [tier_id, setTierId] = useState("");
    const [chapter_id, setChapterId] = useState("");
    const [individualIds, setIndividualIds] = useState([]);
    const [externalEmails, setExternalEmails] = useState("");
    const [ccEmails, setCcEmails] = useState("");
    const [memberSearch, setMemberSearch] = useState("");
    const [tiers, setTiers] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [members, setMembers] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [signatures, setSignatures] = useState([]);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const [draftStatus, setDraftStatus] = useState(""); // "saving" | "saved <time>" | ""
    // Iter 143 — file attachments (PDF/docs/etc.) added to the outgoing blast.
    const [attachments, setAttachments] = useState([]); // [{id, filename, size, content_type}]
    const [attachBusy, setAttachBusy] = useState(false);
    const attachInputRef = React.useRef(null);
    const dirtyRef = React.useRef(false);
    const autosaveTimer = React.useRef(null);

    useEffect(() => {
        api.get("/tiers").then(({ data }) => setTiers(data)).catch(() => {});
        api.get("/chapters").then(({ data }) => setChapters(data)).catch(() => {});
        api.get("/members").then(({ data }) => setMembers(data)).catch(() => {});
        api.get("/email/templates").then(({ data }) => setTemplates(data)).catch(() => {});
        api.get("/email/signatures").then(({ data }) => setSignatures(data)).catch(() => {});
        loadDrafts();
    }, []);

    function loadDrafts() {
        return api.get("/email/drafts").then(({ data }) => setDrafts(data || [])).catch(() => setDrafts([]));
    }

    function buildDraftPayload(extra = {}) {
        // Iter 90: split the textarea on newlines/commas/semicolons. The
        // backend re-validates and dedupes, so we just hand it the raw list.
        const externals = externalEmails
            .split(/[\s,;\n]+/)
            .map((s) => s.trim())
            .filter(Boolean);
        const ccs = ccEmails
            .split(/[\s,;\n]+/)
            .map((s) => s.trim())
            .filter(Boolean);
        return {
            name: "",
            subject,
            body_html,
            background_color: background_color || "",
            segment: segment === "individual" ? "custom" : segment,
            tier_id: tier_id || undefined,
            chapter_id: chapter_id || undefined,
            custom_user_ids: segment === "individual" ? individualIds : [],
            external_emails: externals,
            cc_emails: ccs,
            attachment_ids: attachments.map((a) => a.id),
            is_autosave: true,
            ...extra,
        };
    }

    // Auto-save: debounced 2s after the last change. Also flushes immediately
    // when the tab is hidden or the window is about to unload, so accidentally
    // clicking away or closing the tab never loses work.
    async function flushAutosave() {
        if (!dirtyRef.current) return;
        if (!subject && !body_html) return;
        setDraftStatus("saving");
        try {
            await api.post("/email/drafts", buildDraftPayload());
            const stamp = new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
            setDraftStatus(`Saved at ${stamp}`);
            dirtyRef.current = false;
            loadDrafts();
        } catch {
            setDraftStatus("Save failed — your changes are still in the browser");
        }
    }

    useEffect(() => {
        dirtyRef.current = true;
        if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
        autosaveTimer.current = setTimeout(flushAutosave, 2000);
        return () => clearTimeout(autosaveTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subject, body_html, background_color, segment, tier_id, chapter_id, individualIds, externalEmails, ccEmails, attachments]);

    useEffect(() => {
        const onVis = () => { if (document.visibilityState === "hidden") flushAutosave(); };
        const onBeforeUnload = () => { flushAutosave(); };
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("beforeunload", onBeforeUnload);
        window.addEventListener("pagehide", onBeforeUnload);
        return () => {
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("beforeunload", onBeforeUnload);
            window.removeEventListener("pagehide", onBeforeUnload);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subject, body_html, background_color, segment, tier_id, chapter_id, individualIds, externalEmails, ccEmails, attachments]);

    function loadDraft(did) {
        const d = drafts.find((x) => x.id === did);
        if (!d) return;
        setSubject(d.subject || "");
        setBody(d.body_html || "");
        setBackgroundColor(d.background_color || "");
        if (d.custom_user_ids?.length || (d.external_emails || []).length) {
            setSegment("individual");
            setIndividualIds(d.custom_user_ids || []);
            setExternalEmails((d.external_emails || []).join("\n"));
        } else {
            setSegment(d.segment || "active");
            setIndividualIds([]);
            setExternalEmails("");
        }
        setCcEmails((d.cc_emails || []).join("\n"));
        setTierId(d.tier_id || "");
        setChapterId(d.chapter_id || "");
        dirtyRef.current = false;
        toast.success(d.is_autosave ? "Restored auto-saved draft" : `Loaded draft "${d.name}"`);
    }

    async function saveNamedDraft() {
        const name = window.prompt("Name this draft (e.g. 'Spring Newsletter v2')");
        if (!name || !name.trim()) return;
        try {
            await api.post("/email/drafts", buildDraftPayload({ name: name.trim(), is_autosave: false }));
            toast.success(`Draft "${name.trim()}" saved`);
            loadDrafts();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        }
    }

    async function deleteDraft(did) {
        if (!confirm("Delete this draft?")) return;
        try {
            await api.delete(`/email/drafts/${did}`);
            toast.success("Draft deleted");
            loadDrafts();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    }


    function applyTemplate(tid) {
        const t = templates.find((x) => x.id === tid);
        if (!t) return;
        setSubject(t.subject);
        setBody(t.body_html);
    }

    function insertSignature(sid) {
        const s = signatures.find((x) => x.id === sid);
        if (!s) return;
        setBody((prev) => {
            const sep = '<p>—</p>';
            return (prev || "") + sep + (s.body_html || "");
        });
        toast.success(`Inserted "${s.name}"`);
    }

    function buildPayload() {
        const externals = externalEmails
            .split(/[\s,;\n]+/)
            .map((s) => s.trim())
            .filter(Boolean);
        const ccs = ccEmails
            .split(/[\s,;\n]+/)
            .map((s) => s.trim())
            .filter(Boolean);
        const payload = {
            subject,
            body_html,
            background_color: background_color || "",
            segment,
            tier_id: tier_id || undefined,
            chapter_id: chapter_id || undefined,
            external_emails: externals,
            cc_emails: ccs,
            attachment_ids: attachments.map((a) => a.id),
        };
        if (segment === "individual") {
            payload.segment = "custom";
            payload.custom_user_ids = individualIds;
        }
        return payload;
    }

    async function uploadAttachments(fileList) {
        if (!fileList || fileList.length === 0) return;
        setAttachBusy(true);
        for (const file of Array.from(fileList)) {
            try {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/email/upload-attachment", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setAttachments((prev) => [...prev, data]);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || `Couldn't attach ${file.name}`);
            }
        }
        setAttachBusy(false);
    }

    function removeAttachment(id) {
        setAttachments((prev) => prev.filter((a) => a.id !== id));
        api.delete(`/email/attachments/${id}`).catch(() => { /* best-effort cleanup */ });
    }

    function formatBytes(n) {
        if (!n) return "0 B";
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
        return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    }

    async function runPreview() {
        try {
            const { data } = await api.post("/email/preview", buildPayload());
            setPreview(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Preview failed"); }
    }

    async function sendBlast(test_only = false) {
        if (!subject || !body_html) { toast.error("Subject and body required"); return; }
        if (!test_only && !confirm(`Send to ${preview?.recipient_count ?? "all matching"} recipients?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/email/blast", { ...buildPayload(), test_only });
            toast.success(`Sent: ${data.sent}, failed: ${data.failed}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
        }
        setBusy(false);
    }

    return (
        <div className="grid lg:grid-cols-[1fr_400px] gap-6">
            <div className="bg-card rounded-2xl border border-border p-6 space-y-4 shadow-warm">
                <div className="flex flex-wrap items-center gap-2 -mt-1 pb-3 border-b border-border" data-testid="drafts-toolbar">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mr-1">Drafts</div>
                    {drafts.length > 0 && (
                        <Select value="" onValueChange={loadDraft}>
                            <SelectTrigger className="rounded-full h-8 w-auto min-w-[180px] text-xs px-3" data-testid="drafts-picker">
                                <SelectValue placeholder={`Load draft (${drafts.length})…`} />
                            </SelectTrigger>
                            <SelectContent>
                                {drafts.map((d) => (
                                    <SelectItem key={d.id} value={d.id}>
                                        {d.is_autosave ? "↻ Auto-saved" : (d.name || "Untitled")}
                                        {d.subject ? ` — ${d.subject.slice(0, 40)}` : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <Button type="button" variant="outline" size="sm" className="rounded-full h-8 text-xs" onClick={saveNamedDraft} data-testid="save-draft-btn">
                        Save as draft
                    </Button>
                    {drafts.some((d) => !d.is_autosave) && (
                        <Select value="" onValueChange={deleteDraft}>
                            <SelectTrigger className="rounded-full h-8 w-auto min-w-[110px] text-xs px-3 border-destructive/40 text-destructive" data-testid="delete-draft-picker">
                                <SelectValue placeholder="Delete draft…" />
                            </SelectTrigger>
                            <SelectContent>
                                {drafts.filter((d) => !d.is_autosave).map((d) => (
                                    <SelectItem key={d.id} value={d.id}>{d.name || "Untitled"}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    {draftStatus && (
                        <span className="text-xs text-muted-foreground ml-auto" data-testid="draft-status">{draftStatus}</span>
                    )}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    {templates.length > 0 && (
                        <div>
                            <Label>Start from template</Label>
                            <Select value="" onValueChange={applyTemplate}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="email-template-picker"><SelectValue placeholder="Pick a template…" /></SelectTrigger>
                                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    )}
                    {signatures.length > 0 && (
                        <div>
                            <Label>Insert signature</Label>
                            <Select value="" onValueChange={insertSignature}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="email-signature-picker"><SelectValue placeholder="Append a signature…" /></SelectTrigger>
                                <SelectContent>{signatures.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} {s.kind === "org" ? "(shared)" : "(personal)"}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    )}
                </div>
                <div>
                    <Label>Subject *</Label>
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-xl mt-1.5" placeholder="Hi {{first_name}}, news from your chapter" data-testid="email-subject" />
                </div>
                <div>
                    <Label>CC <span className="text-xs text-muted-foreground font-normal">(optional — comma, space, or newline separated)</span></Label>
                    <Input
                        value={ccEmails}
                        onChange={(e) => setCcEmails(e.target.value)}
                        placeholder="advisor@example.org, president@example.org"
                        className="rounded-xl mt-1.5"
                        data-testid="email-cc"
                    />
                    <div className="text-[11px] text-muted-foreground mt-1">
                        CC recipients get one copy of the message (not one per member). Great for looping in advisors or chapter leadership.
                        {isMembershipManager && (
                            <span className="block mt-0.5 text-primary font-semibold" data-testid="email-reply-to-hint">
                                Replies will go to recruitment@alphaomegaphi.org.
                            </span>
                        )}
                    </div>
                </div>
                <div>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <Label>Body *</Label>
                        <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                            Email background
                            <input
                                type="color"
                                value={background_color || "#ffffff"}
                                onChange={(e) => setBackgroundColor(e.target.value)}
                                className="h-7 w-9 cursor-pointer rounded border border-border bg-transparent"
                                data-testid="email-bg-color"
                                title="Email background color"
                            />
                            {background_color && (
                                <button
                                    type="button"
                                    onClick={() => setBackgroundColor("")}
                                    className="text-xs text-muted-foreground hover:text-primary underline"
                                    data-testid="email-bg-color-clear"
                                >
                                    Clear
                                </button>
                            )}
                        </label>
                    </div>
                    <div className="mt-1.5" style={background_color ? { backgroundColor: background_color, padding: 6, borderRadius: 16 } : undefined}>
                        <RichEditor value={body_html} onChange={setBody} placeholder="Write your message — press Enter for new lines, use toolbar for images." minHeight={300} />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1.5">Variables: <code>{"{{name}}"}</code>, <code>{"{{first_name}}"}</code>, <code>{"{{last_name}}"}</code>, <code>{"{{line_name}}"}</code>, <code>{"{{email}}"}</code></div>
                </div>
                {/* Attachments (Iter 143) — PDFs, DOCX, images, etc. */}
                <div>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <Label>Attachments</Label>
                        <div className="flex items-center gap-2">
                            <input
                                ref={attachInputRef}
                                type="file"
                                multiple
                                className="hidden"
                                onChange={(e) => { uploadAttachments(e.target.files); e.target.value = ""; }}
                                data-testid="email-attach-input"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                className="rounded-full"
                                onClick={() => attachInputRef.current?.click()}
                                disabled={attachBusy}
                                data-testid="email-attach-btn"
                            >
                                <Upload className="h-4 w-4 mr-1.5" />
                                {attachBusy ? "Uploading…" : "Attach files"}
                            </Button>
                        </div>
                    </div>
                    {attachments.length > 0 && (
                        <div className="mt-2 rounded-2xl border border-border bg-muted/30 divide-y divide-border" data-testid="email-attachments-list">
                            {attachments.map((a) => (
                                <div key={a.id} className="flex items-center gap-3 px-3 py-2" data-testid={`email-attachment-${a.id}`}>
                                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate" title={a.filename}>{a.filename}</div>
                                        <div className="text-xs text-muted-foreground">{formatBytes(a.size)} · {a.content_type}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeAttachment(a.id)}
                                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-destructive"
                                        title="Remove attachment"
                                        data-testid={`email-attachment-remove-${a.id}`}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1.5">
                        Up to 20 MB per file. Supported: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), PowerPoint (.ppt, .pptx), text, CSV, images, ZIP.
                    </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                        <Label>Segment</Label>
                        <Select value={segment} onValueChange={setSegment}>
                            <SelectTrigger className="rounded-xl mt-1.5" data-testid="email-segment"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="active">All active members</SelectItem>
                                <SelectItem value="all">Everyone (incl. Omega)</SelectItem>
                                <SelectItem value="admins">Admins only</SelectItem>
                                <SelectItem value="tier">By tier</SelectItem>
                                <SelectItem value="chapter">By chapter</SelectItem>
                                <SelectItem value="individual">Specific members / external emails</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {segment === "tier" && (
                        <div>
                            <Label>Tier</Label>
                            <Select value={tier_id} onValueChange={setTierId}>
                                <SelectTrigger className="rounded-xl mt-1.5"><SelectValue placeholder="Pick" /></SelectTrigger>
                                <SelectContent>{tiers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    )}
                    {segment === "chapter" && (
                        <div>
                            <Label>Chapter</Label>
                            <Select value={chapter_id} onValueChange={setChapterId}>
                                <SelectTrigger className="rounded-xl mt-1.5"><SelectValue placeholder="Pick" /></SelectTrigger>
                                <SelectContent>{chapters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    )}
                    {segment === "individual" && (
                        <div className="sm:col-span-2 space-y-3">
                            <div>
                                <Label>Members ({individualIds.length} selected)</Label>
                                {individualIds.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="email-individual-chips">
                                        {individualIds.map((id) => {
                                            const m = members.find((x) => x.id === id);
                                            if (!m) return null;
                                            return (
                                                <span
                                                    key={id}
                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold border border-primary/20"
                                                    data-testid={`email-individual-chip-${id}`}
                                                >
                                                    {m.name}
                                                    <button
                                                        type="button"
                                                        onClick={() => setIndividualIds((prev) => prev.filter((x) => x !== id))}
                                                        className="hover:text-red-600 leading-none text-base"
                                                        title={`Remove ${m.name}`}
                                                        data-testid={`email-individual-remove-${id}`}
                                                    >×</button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                                <Input
                                    placeholder="Search by name or email — click to add/remove…"
                                    value={memberSearch}
                                    onChange={(e) => setMemberSearch(e.target.value)}
                                    className="rounded-xl mt-2"
                                    data-testid="email-individual-search"
                                />
                                <div className="max-h-40 overflow-y-auto mt-2 border border-border rounded-xl bg-muted/20">
                                    {members
                                        .filter((m) => {
                                            if (!memberSearch) return true;
                                            const q = memberSearch.toLowerCase();
                                            return m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q);
                                        })
                                        .slice(0, 30)
                                        .map((m) => {
                                            const picked = individualIds.includes(m.id);
                                            return (
                                                <button
                                                    key={m.id}
                                                    type="button"
                                                    onClick={() => setIndividualIds((prev) => picked ? prev.filter((x) => x !== m.id) : [...prev, m.id])}
                                                    className={`w-full text-left px-3 py-2 hover:bg-muted/60 border-b last:border-0 flex items-center justify-between gap-2 ${picked ? "bg-primary/10" : ""}`}
                                                    data-testid={`email-individual-pick-${m.id}`}
                                                >
                                                    <div>
                                                        <div className="text-sm font-medium">{m.name}</div>
                                                        <div className="text-xs text-muted-foreground">{m.email}</div>
                                                    </div>
                                                    {picked && <span className="text-primary font-bold text-lg">✓</span>}
                                                </button>
                                            );
                                        })}
                                </div>
                            </div>
                            <div>
                                <Label>External emails (outside the roster)</Label>
                                <textarea
                                    value={externalEmails}
                                    onChange={(e) => setExternalEmails(e.target.value)}
                                    placeholder={"vendor@example.com\npress@news.com, partner@org.net"}
                                    rows={3}
                                    className="w-full rounded-xl mt-1.5 px-3 py-2 border border-border bg-background text-sm font-mono resize-y"
                                    data-testid="email-external-textarea"
                                />
                                <p className="text-xs text-muted-foreground mt-1.5">
                                    One per line, or separated by commas / semicolons. Invalid addresses are silently skipped. These recipients always receive the email — opt-out preferences don't apply.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                    <Button variant="outline" onClick={runPreview} className="rounded-full" data-testid="email-preview-btn">Preview</Button>
                    <Button variant="outline" onClick={() => sendBlast(true)} disabled={busy} className="rounded-full" data-testid="email-test-btn">Send test to me</Button>
                    <Button onClick={() => sendBlast(false)} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90 ml-auto" data-testid="email-send-btn">
                        <Send className="h-4 w-4 mr-1.5" /> {busy ? "Sending…" : `Send blast${preview ? ` (${preview.recipient_count})` : ""}`}
                    </Button>
                </div>
            </div>
            <div className="bg-card rounded-2xl border border-border p-5 shadow-warm sticky top-20 h-fit">
                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">Live preview</div>
                {preview ? (
                    <div className="text-sm">
                        <div className="text-xs text-muted-foreground mb-1">
                            Recipients: <strong>{preview.recipient_count}</strong><br />
                            Sample: {preview.sample_recipient.name} &lt;{preview.sample_recipient.email}&gt;
                        </div>
                        <div className="font-bold mt-3 mb-2">{preview.subject}</div>
                        <div className="border rounded-xl p-3 bg-white max-h-96 overflow-auto prose prose-sm max-w-none" dangerouslySetInnerHTML={safeHtml(preview.html)} />
                    </div>
                ) : (
                    <div className="text-sm text-muted-foreground">Click <strong>Preview</strong> to render against a sample recipient.</div>
                )}
            </div>
        </div>
    );
}

// Official chapter filter — used in member-facing chapter pickers.
const OFFICIAL_CHAPTER_NAMES = ["Texas", "Florida", "Tri-South", "DMV"];
const officialOnly = (c) => OFFICIAL_CHAPTER_NAMES.includes(c.name);

function EmailTemplates() {
    const [items, setItems] = useState([]);
    const [testingId, setTestingId] = useState("");
    const load = () => api.get("/email/templates").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);
    async function del(id) {
        if (!confirm("Delete this template?")) return;
        await api.delete(`/email/templates/${id}`);
        load();
    }
    async function sendTestToMe(t) {
        setTestingId(t.id);
        try {
            const { data } = await api.post("/email/test-send", { template_id: t.id });
            if (data.ok) toast.success(`Test "${t.name}" sent to ${data.to || "you"} — check your inbox`);
            else toast.error(`Brevo rejected: ${formatApiError(data.detail) || "unknown error"}`, { duration: 10000 });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Test send failed");
        } finally {
            setTestingId("");
        }
    }
    return (
        <div>
            <div className="flex justify-end mb-4">
                <TemplateDialog onSaved={load} trigger={<Button className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="new-template-btn"><Plus className="h-4 w-4 mr-1" />New template</Button>} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                {items.map((t) => (
                    <div key={t.id} className="bg-card border border-border rounded-2xl p-5" data-testid={`template-${t.id}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="font-heading font-semibold text-lg flex items-center gap-2">
                                    {t.name}
                                    {t.is_builtin && <span className="text-[10px] uppercase tracking-wider font-bold bg-primary/10 text-primary rounded-full px-2 py-0.5">Built-in</span>}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">{t.subject}</div>
                            </div>
                            <div className="flex gap-1">
                                <TemplateDialog template={t} onSaved={load} trigger={<Button variant="outline" size="sm" className="rounded-full">Edit</Button>} />
                                <Button variant="ghost" size="icon" onClick={() => del(t.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                            </div>
                        </div>
                        {t.description && <p className="text-sm text-muted-foreground mt-2">{t.description}</p>}
                        <div className="mt-3 flex justify-end">
                            <Button
                                variant="outline"
                                size="sm"
                                className="rounded-full text-xs"
                                onClick={() => sendTestToMe(t)}
                                disabled={testingId === t.id}
                                data-testid={`template-test-send-${t.id}`}
                            >
                                <Send className="h-3.5 w-3.5 mr-1.5" />
                                {testingId === t.id ? "Sending…" : "Send test to me"}
                            </Button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="col-span-full text-muted-foreground text-center py-8">No templates yet. Create reusable email layouts.</div>}
            </div>
        </div>
    );
}

function TemplateDialog({ template, onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", subject: "", body_html: "", description: "" });
    useEffect(() => {
        if (open) setForm(template ? { ...template } : { name: "", subject: "", body_html: "", description: "" });
    }, [open, template]);

    async function save() {
        try {
            if (template) await api.put(`/email/templates/${template.id}`, form);
            else await api.post("/email/templates", form);
            toast.success("Saved");
            setOpen(false);
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{template ? "Edit template" : "New email template"}</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                    <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5" data-testid="template-name-input" /></div>
                    <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1.5" placeholder="What this template is for" /></div>
                    <div>
                        <Label>Body</Label>
                        <div className="mt-1.5">
                            <RichEditor value={form.body_html} onChange={(html) => setForm((f) => ({ ...f, body_html: html }))} placeholder="Write the template body — add images, lists, links from the toolbar." minHeight={260} />
                        </div>
                    </div>
                </div>
                <DialogFooter><Button onClick={save} className="rounded-full bg-primary hover:bg-primary/90" data-testid="template-save-btn">Save</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* -------- Email Signatures (personal + org) -------- */
function EmailSignatures() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/email/signatures").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);
    async function del(id) {
        if (!confirm("Delete this signature?")) return;
        await api.delete(`/email/signatures/${id}`);
        toast.success("Deleted");
        load();
    }
    const personal = items.filter((s) => s.kind === "personal");
    const org = items.filter((s) => s.kind === "org");
    return (
        <div className="space-y-8">
            <div>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h3 className="font-heading text-xl font-bold">My signatures</h3>
                        <p className="text-xs text-muted-foreground">Only you can see and edit these.</p>
                    </div>
                    <SignatureDialog kind="personal" onSaved={load} trigger={<Button className="rounded-full bg-primary hover:bg-primary/90 shadow-warm" data-testid="new-personal-sig-btn"><Plus className="h-4 w-4 mr-1" />New personal signature</Button>} />
                </div>
                <SignatureGrid items={personal} onSaved={load} onDelete={del} />
            </div>
            <div>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h3 className="font-heading text-xl font-bold">Shared signatures</h3>
                        <p className="text-xs text-muted-foreground">Any admin can use these (President's closing, Chapter footer, etc.)</p>
                    </div>
                    <SignatureDialog kind="org" onSaved={load} trigger={<Button variant="outline" className="rounded-full" data-testid="new-org-sig-btn"><Plus className="h-4 w-4 mr-1" />New shared signature</Button>} />
                </div>
                <SignatureGrid items={org} onSaved={load} onDelete={del} />
            </div>
        </div>
    );
}

function SignatureGrid({ items, onSaved, onDelete }) {
    if (items.length === 0) return <div className="text-sm text-muted-foreground py-6 text-center bg-muted/30 rounded-2xl border-2 border-dashed border-border">No signatures yet — add your name, title, and a photo so members instantly recognize who wrote.</div>;
    return (
        <div className="grid md:grid-cols-2 gap-4">
            {items.map((s) => (
                <div key={s.id} className="bg-card border border-border rounded-2xl p-5" data-testid={`signature-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="font-heading font-semibold text-lg">{s.name}</div>
                        <div className="flex gap-1 shrink-0">
                            <SignatureDialog signature={s} onSaved={onSaved} trigger={<Button variant="outline" size="sm" className="rounded-full">Edit</Button>} />
                            <Button variant="ghost" size="icon" onClick={() => onDelete(s.id)} data-testid={`delete-signature-${s.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                    </div>
                    <div className="mt-3 border rounded-xl p-3 bg-slate-50 max-h-48 overflow-auto prose prose-sm max-w-none" dangerouslySetInnerHTML={safeHtml(s.body_html)} />
                </div>
            ))}
        </div>
    );
}

function SignatureDialog({ signature, kind, onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", body_html: "", kind: kind || "personal" });
    useEffect(() => {
        if (open) {
            setForm(signature
                ? { name: signature.name, body_html: signature.body_html, kind: signature.kind }
                : { name: "", body_html: "<p><strong>Your Name</strong><br>Title · Chapter</p><p>email@aop.org · 555-0100</p>", kind: kind || "personal" }
            );
        }
    }, [open, signature, kind]);

    async function save() {
        if (!form.name.trim()) { toast.error("Name required"); return; }
        try {
            if (signature) {
                await api.put(`/email/signatures/${signature.id}`, { name: form.name, body_html: form.body_html });
            } else {
                await api.post("/email/signatures", form);
            }
            toast.success("Saved");
            setOpen(false);
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{signature ? "Edit signature" : `New ${form.kind === "org" ? "shared" : "personal"} signature`}</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                    <div>
                        <Label>Signature name</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. President closing, My default" className="rounded-xl mt-1.5" data-testid="signature-name-input" />
                    </div>
                    <div>
                        <Label>Signature content</Label>
                        <div className="mt-1.5">
                            <RichEditor value={form.body_html} onChange={(html) => setForm((f) => ({ ...f, body_html: html }))} placeholder="Type your name, title, contact info. Click the image button to add a photo or logo." minHeight={220} />
                        </div>
                        <div className="text-xs text-muted-foreground mt-1.5">Tip: drag a photo into the editor for a portrait, or paste from your clipboard.</div>
                    </div>
                </div>
                <DialogFooter><Button onClick={save} className="rounded-full bg-primary hover:bg-primary/90" data-testid="signature-save-btn">Save signature</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BlastHistory() {
    const [items, setItems] = useState([]);
    const [openId, setOpenId] = useState("");
    const [failedDetails, setFailedDetails] = useState({});
    useEffect(() => { api.get("/email/blasts").then(({ data }) => setItems(data)).catch(() => {}); }, []);

    async function toggleFailed(bid) {
        if (openId === bid) { setOpenId(""); return; }
        setOpenId(bid);
        if (!failedDetails[bid]) {
            try {
                const { data } = await api.get(`/email/blasts/${bid}/failed`);
                setFailedDetails((m) => ({ ...m, [bid]: data }));
            } catch {
                setFailedDetails((m) => ({ ...m, [bid]: { failed: [], failed_count: 0 } }));
            }
        }
    }

    function copyEmails(emails) {
        const text = emails.join(", ");
        navigator.clipboard.writeText(text).then(
            () => toast.success(`Copied ${emails.length} address${emails.length === 1 ? "" : "es"} to clipboard`),
            () => toast.error("Copy failed"),
        );
    }

    return (
        <div className="space-y-8">
            <div>
                <h3 className="font-heading text-xl font-bold mb-3">Blast history</h3>
                <div className="bg-card rounded-2xl border overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="text-left px-4 py-2.5">Sent</th>
                                <th className="text-left px-4 py-2.5">Subject</th>
                                <th className="text-left px-4 py-2.5">Segment</th>
                                <th className="text-left px-4 py-2.5">Sent</th>
                                <th className="text-left px-4 py-2.5">Failed</th>
                                <th className="text-left px-4 py-2.5">Opens</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((b) => {
                                const open = openId === b.id;
                                const det = failedDetails[b.id];
                                const failedList = det?.failed || [];
                                return (
                                    <React.Fragment key={b.id}>
                                        <tr className="border-t border-border" data-testid={`blast-${b.id}`}>
                                            <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{b.sent_at && format(parseISO(b.sent_at), "MMM d, yyyy h:mm a")}</td>
                                            <td className="px-4 py-2.5 font-medium">{b.subject}</td>
                                            <td className="px-4 py-2.5 text-xs">{b.segment}{b.test_only ? " · test" : ""}</td>
                                            <td className="px-4 py-2.5">{b.sent_count}</td>
                                            <td className="px-4 py-2.5">
                                                {(b.failed_count || 0) > 0 ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleFailed(b.id)}
                                                        className="text-destructive font-semibold underline-offset-2 hover:underline"
                                                        data-testid={`blast-failed-toggle-${b.id}`}
                                                    >
                                                        {b.failed_count} {open ? "▾" : "▸"}
                                                    </button>
                                                ) : (
                                                    <span className="text-muted-foreground">0</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2.5">{b.opens || 0}</td>
                                        </tr>
                                        {open && (
                                            <tr className="bg-red-50/50" data-testid={`blast-failed-detail-${b.id}`}>
                                                <td colSpan={6} className="px-4 py-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="text-xs font-bold uppercase tracking-wider text-destructive">Failed recipients — needs attention</div>
                                                        {failedList.length > 0 && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="rounded-full h-7 text-xs"
                                                                onClick={() => copyEmails(failedList.map((f) => f.email).filter(Boolean))}
                                                                data-testid={`blast-copy-failed-${b.id}`}
                                                            >
                                                                Copy {failedList.filter((f) => f.email).length} emails
                                                            </Button>
                                                        )}
                                                    </div>
                                                    {failedList.length === 0 && <div className="text-sm text-muted-foreground">Loading…</div>}
                                                    {failedList.length > 0 && (
                                                        <ul className="space-y-1 text-sm">
                                                            {failedList.map((f, i) => (
                                                                <li key={i} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                                                    <span className="font-mono text-xs">{f.email || <em className="text-muted-foreground">(no email)</em>}</span>
                                                                    {f.reason && <span className="text-xs text-muted-foreground">— {f.reason}</span>}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {items.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-6">No blasts yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
            <PasswordSetupFailures />
        </div>
    );
}

function PasswordSetupFailures() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    function load() {
        setLoading(true);
        api.get("/email/password-setup-failures")
            .then(({ data }) => setItems(data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }
    useEffect(() => { load(); }, []);

    function copyAll() {
        const emails = items.map((i) => i.email).filter(Boolean);
        if (!emails.length) return;
        navigator.clipboard.writeText(emails.join(", ")).then(
            () => toast.success(`Copied ${emails.length} address${emails.length === 1 ? "" : "es"}`),
            () => toast.error("Copy failed"),
        );
    }

    return (
        <div data-testid="password-setup-failures">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h3 className="font-heading text-xl font-bold">Password-setup link failures</h3>
                    <p className="text-xs text-muted-foreground">Members whose set-password / welcome email could not be delivered in the last 90 days. Re-send manually from the Members tab once you've corrected their address.</p>
                </div>
                <div className="flex gap-2">
                    {items.length > 0 && (
                        <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={copyAll} data-testid="copy-setup-failures-btn">
                            Copy {items.length} emails
                        </Button>
                    )}
                    <Button variant="ghost" size="sm" className="rounded-full text-xs" onClick={load} data-testid="refresh-setup-failures-btn">Refresh</Button>
                </div>
            </div>
            <div className="bg-card rounded-2xl border overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="text-left px-4 py-2.5">Attempted</th>
                            <th className="text-left px-4 py-2.5">Member</th>
                            <th className="text-left px-4 py-2.5">Email</th>
                            <th className="text-left px-4 py-2.5">Reason</th>
                            <th className="text-left px-4 py-2.5">By</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={5} className="text-center text-muted-foreground py-6">Loading…</td></tr>}
                        {!loading && items.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-6">No failures in the last 90 days — onboarding emails are landing cleanly.</td></tr>}
                        {!loading && items.map((it) => (
                            <tr key={it.id} className="border-t border-border" data-testid={`setup-failure-${it.id}`}>
                                <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{it.attempted_at && format(parseISO(it.attempted_at), "MMM d, yyyy h:mm a")}</td>
                                <td className="px-4 py-2.5">{it.user_name || <em className="text-muted-foreground">unknown</em>}</td>
                                <td className="px-4 py-2.5 font-mono text-xs">{it.email || <em className="text-muted-foreground">(no email)</em>}</td>
                                <td className="px-4 py-2.5 text-xs text-muted-foreground">{it.reason || "—"}</td>
                                <td className="px-4 py-2.5 text-xs text-muted-foreground">{it.admin_name || "—"}{it.mode === "bulk" ? " · bulk" : ""}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


/**
 * Bulk-import donation transactions via CSV. Mirrors the Hours CSV importer:
 * pick a file → automatic dry-run preview with row-by-row READY/ERROR pills →
 * Confirm to write. Errors quote the row number + the offending message so
 * the admin can fix and re-upload without leaving the dialog.
 */
function DonationsCsvImportDialog({ onImported }) {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    function reset() {
        setFile(null);
        setPreview(null);
    }

    async function downloadTemplate() {
        try {
            const { data } = await api.get("/donations/admin/csv/template", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "donations_template.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Template download failed");
        }
    }

    async function runDryRun(f) {
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/donations/admin/csv?dry_run=true", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "CSV preview failed");
            setPreview(null);
        } finally {
            setBusy(false);
        }
    }

    async function confirmImport() {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/donations/admin/csv?dry_run=false", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const created = data?.created || 0;
            const failed = data?.failed || 0;
            if (failed > 0) {
                toast.warning(`${created} imported, ${failed} skipped — check the preview for row errors before closing.`);
                // Keep the dialog open so the admin can see the row-level errors.
            } else {
                toast.success(`${created} donation${created === 1 ? "" : "s"} imported`);
                setOpen(false);
                reset();
            }
            onImported && onImported();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="donations-csv-btn">
                    <Upload className="h-4 w-4 mr-1" />Bulk import donations
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="donations-csv-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Bulk import donations</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    <div className="rounded-xl bg-muted/40 p-3 text-sm space-y-1">
                        <div><strong>CSV columns:</strong> <code className="bg-background px-1.5 py-0.5 rounded">member_email, full_name, first_name, last_name, amount, cause, date, note, anonymous, method</code></div>
                        <div className="text-xs text-muted-foreground">
                            <strong>Member identifier (one per row):</strong> <code>member_email</code> (preferred), <em>or</em> <code>full_name</code>, <em>or</em> both <code>first_name</code>+<code>last_name</code> — use a name when the on-file email doesn&apos;t match. Ambiguous names (≥2 matches) are flagged so you can add an email to disambiguate.
                        </div>
                        <div className="text-xs text-muted-foreground">
                            <code>cause</code> is optional — leave blank to import as unallocated, OR enter any text. If the text matches an existing cause title (case-insensitive) the donation links to that cause; if not, it&apos;s saved as unallocated and the label is preserved on the receipt. <code>date</code> accepts YYYY-MM-DD, MM/DD/YYYY, M/D/YY, or 15-Jun-2026. <code>anonymous</code> = true/false (default false).
                        </div>
                        <button type="button" onClick={downloadTemplate} className="text-primary font-semibold hover:underline text-xs" data-testid="donations-csv-template-btn">
                            ⬇ Download CSV template
                        </button>
                    </div>

                    {!preview && (
                        <label className="rounded-xl border-2 border-dashed border-slate-300 px-4 py-6 text-sm font-semibold cursor-pointer hover:bg-white hover:border-primary transition-colors flex items-center gap-2 justify-center" data-testid="donations-csv-file-btn">
                            <Upload className="h-4 w-4" />{file ? file.name : "Choose CSV file"}
                            <input
                                type="file"
                                accept=".csv,text/csv"
                                className="hidden"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) {
                                        setFile(f);
                                        runDryRun(f);
                                    }
                                }}
                            />
                        </label>
                    )}

                    {busy && !preview && <div className="text-sm text-muted-foreground">Analyzing…</div>}

                    {preview && (
                        <div className="space-y-2" data-testid="donations-csv-preview">
                            <div className="flex items-center justify-between text-sm">
                                <div>
                                    <strong>{preview.total}</strong> rows · <span className="text-emerald-700">{preview.total - preview.failed} ready</span>{preview.failed > 0 && <span className="text-destructive"> · {preview.failed} errors</span>}
                                </div>
                                <button type="button" onClick={reset} className="text-xs text-primary hover:underline font-semibold" data-testid="donations-csv-reset">
                                    Choose a different file
                                </button>
                            </div>
                            <div className="max-h-72 overflow-y-auto rounded-xl border border-border bg-card divide-y divide-border">
                                {preview.rows.map((r) => (
                                    <div key={r.row} className="px-3 py-2 text-xs flex items-start gap-2" data-testid={`donations-csv-row-${r.row}`}>
                                        <span className={`uppercase tracking-wider font-bold rounded-full px-2 py-0.5 text-[10px] shrink-0 ${r.status === "READY" ? "bg-emerald-100 text-emerald-700" : "bg-destructive/15 text-destructive"}`}>{r.status}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold">Row {r.row} · {r.member_email || r.member_identifier || "(no member)"} · ${r.amount_raw || "0"}{r.cause_title ? ` · ${r.cause_title}` : ""}</div>
                                            {r.errors.length > 0 && (
                                                <div className="text-destructive">{r.errors.join("; ")}</div>
                                            )}
                                            {(r.warnings || []).length > 0 && (
                                                <div className="text-amber-700">⚠ {r.warnings.join("; ")}</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => { setOpen(false); reset(); }} className="rounded-full">Cancel</Button>
                    <Button
                        onClick={confirmImport}
                        disabled={busy || !preview || preview.total === preview.failed}
                        className="rounded-full bg-primary hover:bg-primary/90"
                        data-testid="donations-csv-confirm"
                    >
                        {busy ? "Importing…" : preview ? `Import ${preview.total - preview.failed} donation${(preview.total - preview.failed) === 1 ? "" : "s"}` : "Import"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
